import { aircraftTypeById, requireAirport } from "./data/worldQueries";
import { SAVE_SCHEMA_VERSION } from "./data/initialSave";
import {
  resolveOccurrenceForLeg,
  resolveScheduleSelector,
  rotationLegSelectorId,
  rotationSelectorsOverlap,
  type ResolvedScheduleSelector
} from "./data/scheduleQueries";
import { defaultAircraftCapabilities, validateAircraftCapabilities } from "./rules/aircraft";
import { buildAirportDepartureOffer } from "./rules/airport";
import { buildAutoAssignment, validateOperation, type DispatchBindingRequest, type DispatchBindingSnapshot } from "./rules/dispatch";
import { applyDailyEventDisruptions, totalAirspaceDelayMinutes } from "./rules/events";
import { buildOperationalLedger } from "./rules/economics";
import { dayMinuteToLabel, normalizeTime, timeToMinutes } from "./rules/schedule";
import { hydrateGroundOpsNode } from "./groundOps";
import { groundOpsNodeProfile, requireFlightConfig, requireGroundOpsConfig } from "./flightCommandConfig";
import { addIsoDays, clamp, cloneSave, deterministicHash, formatSignedMoney, nextId, signed } from "./utils";
import type {
  Aircraft,
  AircraftReleaseCandidate,
  AircraftStateSnapshot,
  AircraftType,
  ActiveOpsDay,
  Airport,
  AirportDepartureOffer,
  AirportServiceRecord,
  CustomFlight,
  DailyOperation,
  DispatchAssignment,
  FlightDisruptionDecision,
  FlightDisruptionIntent,
  FlightDisruptionOutcome,
  FlightOccurrence,
  FlightOccurrenceEvent,
  FlightEvent,
  FlightFuelOrder,
  FlightInstance,
  FlightLeg,
  FlightLegTemplate,
  FlightMovement,
  GroundOpsFlowSnapshot,
  GroundOpsNodeKind,
  GroundOpsNodeState,
  FlightPayloadAdjustment,
  FlightPlan,
  GlobalEvent,
  GroundOpsAirportConfigBundle,
  OperationalKpis,
  OpsDayCloseResult,
  PassengerTransfer,
  PlayerRotation,
  RedispatchAction,
  RedispatchAirportConfigContext,
  FlightAirportConfigBundle,
  RecoveryFlight,
  SaveGame,
  ScheduleSource
} from "./types";


export { createDefaultFlightOccurrences, createDefaultFlightPlans, createDefaultPlayerRotations, createInitialSave, SAVE_SCHEMA_VERSION } from "./data/initialSave";

const HOURS_PER_KM = 1 / 780;
const ON_TIME_ARRIVAL_THRESHOLD_MINUTES = 15;
const MAX_CYCLES_SINCE_CHECK = 420;
const EMPTY_SCHEDULE: ScheduleSource = { flightPlans: [], occurrences: [], cancellations: [], occurrenceEvents: [] };
const DOMESTIC_COUNTRY = "中国";
const RECOVERY_PASSENGER_RESPONSE_RANGE = [25, 60] as const;
const RECOVERY_EMPTY_RESPONSE_RANGE = [20, 45] as const;
const POSITIONING_RESPONSE_RANGE = [15, 35] as const;
const FLIGHT_ACTION_WINDOW_MINUTES = 90;
const GROUND_SERVICE_START_OFFSET_MINUTES = 150;
const GROUND_SERVICE_START_OFFSET_MAX_MINUTES = 240;
const PASSENGER_BOARDING_START_OFFSET_MINUTES = 50;
const CARGO_LOADING_START_OFFSET_MINUTES = 65;
const LOADSHEET_MINUTES = 8;
const DOORS_CLOSE_MINUTES = 4;
const CHOCKS_OFF_MINUTES = 3;
const PUSHBACK_MINUTES = 6;
const READY_FOR_DEPARTURE_REASON = "地面保障完成，等待起飞";
const OPS_DAY_CLOSE_BUFFER_MINUTES = 180;

export interface FlightLedgerPreviewOverrides {
  payloadAdjustment?: FlightPayloadAdjustment;
  fuelOrder?: FlightFuelOrder;
}

export type FlightLedgerResult = ReturnType<typeof buildOperationalLedger> & { airportOffer: AirportDepartureOffer };

type DailyResourcePlan = {
  usedAircraftIds: Set<string>;
  aircraftStates: Map<string, AircraftPlanState>;
};

type AircraftPlanState = {
  aircraftId: string;
  location: string;
  readyMinute: number;
  hoursSinceMaintenance: number;
  cyclesSinceCheck: number;
};

type ResolvedDispatch = {
  assignment: DispatchAssignment | undefined;
  conflicts: string[];
};

type LegPlan = {
  template: FlightLegTemplate;
  aircraftTypeId?: string;
  scheduledFlightId?: string;
  flightPlan?: FlightPlan;
  occurrenceId?: string;
  occurrence?: FlightOccurrence;
  origin: Airport;
  destination: Airport;
  distanceKm: number;
  blockMinutes: number;
  plannedDepartureMinute: number;
  plannedArrivalMinute: number;
};

export type CustomFlightInput = Pick<CustomFlight, "kind" | "origin" | "destination" | "scheduledDay" | "departureTimeLocal" | "aircraftTypeId"> &
  Partial<Pick<CustomFlight, "title" | "recoveryForFlightInstanceId" | "passengerTransfers" | "linkedProgramId" | "linkedLegSequence">>;
export type RecoveryFlightInput = CustomFlightInput;

export interface PlayerRotationInput {
  id?: string;
  name?: string;
  occurrenceIds?: string[];
  scheduledFlightIds?: string[];
}

export function normalizeSaveGame(save: SaveGame): SaveGame {
  const next = cloneSave(save);
  next.schemaVersion = SAVE_SCHEMA_VERSION;
  delete (next as SaveGame & { crews?: unknown }).crews;
  for (const assignment of next.assignments) {
    delete (assignment as DispatchAssignment & { crewIds?: unknown }).crewIds;
  }
  for (const flight of next.activeOpsDay?.flights ?? []) {
    delete (flight as FlightInstance & { crewIds?: unknown }).crewIds;
    migrateFlightGroundOpsToCurrentMovement(flight);
    if (flight.releaseDraft) {
      delete (flight.releaseDraft as NonNullable<FlightInstance["releaseDraft"]> & { selectedCrewIds?: unknown }).selectedCrewIds;
    }
    if (flight.status === "released" && flight.releaseDraft && !flight.releaseDraft.status) {
      flight.releaseDraft.status = "confirmed";
      flight.releaseDraft.confirmedMinute = flight.releaseDraft.confirmedMinute ?? flight.releaseDraft.generatedMinute;
    }
  }
  return next;
}

export type AircraftInput = Omit<Aircraft, "id"> & { id?: string };

export function addCustomFlight(save: SaveGame, input: CustomFlightInput): SaveGame {
  const next = cloneSave(save);
  const origin = requireAirport(input.origin);
  const destination = requireAirport(input.destination);
  if (!aircraftTypeById(input.aircraftTypeId)) {
    throw new Error("aircraft type not found");
  }
  const sourceFlight = activeFlightById(next, input.recoveryForFlightInstanceId);
  const passengerTransfers = customFlightPassengerTransfers(input, next, sourceFlight);
  const recoveryTitle = input.title ?? customFlightTitle(input.kind, origin.code, destination.code);
  const recovery: RecoveryFlight = {
    id: nextId("recovery", next.recoveryFlights.length + next.day),
    kind: input.kind,
    title: recoveryTitle,
    origin: origin.code,
    destination: destination.code,
    scheduledDay: scheduledRecoveryDay(next.day, input.scheduledDay),
    departureTimeLocal: normalizeTime(input.departureTimeLocal),
    aircraftTypeId: input.aircraftTypeId,
    recoveryForFlightInstanceId: sourceFlight?.id,
    passengerTransfers: optionalPassengerTransfers(passengerTransfers),
    linkedProgramId: input.linkedProgramId || undefined,
    linkedLegSequence: input.linkedLegSequence,
    status: "open",
    createdDay: next.day,
    createdAt: new Date().toISOString()
  };
  next.recoveryFlights.push(recovery);
  if (next.activeOpsDay?.status === "open" && recovery.scheduledDay <= next.day) {
    enqueueActiveRecoveryFlight(next, recovery);
  }
  next.messages.unshift(`${customFlightKindLabel(recovery.kind)}已创建：${recovery.title}。`);
  return next;
}

function activeFlightById(save: SaveGame, flightInstanceId: string | undefined): FlightInstance | undefined {
  if (!flightInstanceId) {
    return undefined;
  }
  return save.activeOpsDay?.flights.find((flight) => flight.id === flightInstanceId);
}

function scheduledRecoveryDay(currentDay: number, requestedScheduledDay: number): number {
  const roundedScheduledDay = Math.round(requestedScheduledDay);
  return Math.max(currentDay, roundedScheduledDay);
}

function customFlightPassengerTransfers(input: CustomFlightInput, save: SaveGame, sourceFlight: FlightInstance | undefined): PassengerTransfer[] {
  if (input.kind !== "recovery") {
    return [];
  }
  return normalizePassengerTransfers(input.passengerTransfers, save, sourceFlight);
}

function optionalPassengerTransfers(passengerTransfers: PassengerTransfer[]): PassengerTransfer[] | undefined {
  if (passengerTransfers.length > 0) {
    return passengerTransfers;
  }
  return undefined;
}

function customFlightTitle(kind: CustomFlightInput["kind"], originCode: string, destinationCode: string): string {
  return `${customFlightKindLabel(kind)} ${originCode}-${destinationCode}`;
}

function customFlightKindLabel(kind: CustomFlightInput["kind"]): string {
  if (kind === "positioning") {
    return "空机调机";
  }
  return "恢复航班";
}

export function addRecoveryFlight(save: SaveGame, input: RecoveryFlightInput): SaveGame {
  return addCustomFlight(save, input);
}

export function addPlayerRotation(save: SaveGame, input: PlayerRotationInput, schedule: ScheduleSource): SaveGame {
  return upsertPlayerRotation(save, { ...input, id: undefined }, schedule);
}

export function updatePlayerRotation(save: SaveGame, rotationId: string, input: PlayerRotationInput, schedule: ScheduleSource): SaveGame {
  return upsertPlayerRotation(save, { ...input, id: rotationId }, schedule);
}

function upsertPlayerRotation(save: SaveGame, input: PlayerRotationInput, schedule: ScheduleSource): SaveGame {
  const next = cloneSave(save);
  assertRotationCanChange(next, input.id);
  const selectors = rotationInputSelectors(input, schedule);
  if (selectors.length === 0) {
    throw new Error("至少选择一条 GM 定期航班。");
  }
  if (selectors.some((selector) => !selector.flightPlan)) {
    throw new Error("GM 定期航班不存在。");
  }
  const existingIndex = playerRotationIndex(next, input.id);
  if (input.id && existingIndex < 0) {
    throw new Error("玩家航班串不存在。");
  }
  const rotationId = input.id ?? nextId("rot", next.rotations.length + next.day);
  const duplicate = findOccupiedRotationSelector(next.rotations, selectors.map((selector) => selector.selectorId), rotationId, schedule);
  if (duplicate) {
    throw new Error(`GM 定期航班已在其他航班串中：${duplicate}`);
  }
  const legs = buildPlayerRotationLegs(selectors, next, rotationId, schedule);
  const firstPlan = selectors[0].flightPlan!;
  const lastPlan = selectors.at(-1)!.flightPlan!;
  const name = input.name?.trim() || `${firstPlan.origin}-${lastPlan.destination} 航班串`;
  const rotation: PlayerRotation = {
    id: rotationId,
    name,
    legs,
    createdDay: playerRotationCreatedDay(next, existingIndex),
    updatedDay: next.day
  };
  if (existingIndex >= 0) {
    next.rotations[existingIndex] = rotation;
  } else {
    next.rotations.push(rotation);
  }
  next.messages.unshift(`${playerRotationUpsertMessage(existingIndex)}：${rotation.name}。`);
  return next;
}

function playerRotationIndex(save: SaveGame, rotationId: string | undefined): number {
  if (!rotationId) {
    return -1;
  }
  return save.rotations.findIndex((rotation) => rotation.id === rotationId);
}

function playerRotationCreatedDay(save: SaveGame, existingIndex: number): number {
  if (existingIndex >= 0) {
    return save.rotations[existingIndex].createdDay;
  }
  return save.day;
}

function playerRotationUpsertMessage(existingIndex: number): string {
  if (existingIndex >= 0) {
    return "玩家航班串已更新";
  }
  return "玩家航班串已编组";
}

export function deletePlayerRotation(save: SaveGame, rotationId: string): SaveGame {
  const next = cloneSave(save);
  assertRotationCanChange(next, rotationId);
  const rotation = next.rotations.find((item) => item.id === rotationId);
  if (!rotation) {
    throw new Error("玩家航班串不存在。");
  }
  next.rotations = next.rotations.filter((item) => item.id !== rotationId);
  next.assignments = next.assignments.filter((assignment) => assignment.targetType !== "rotation" || assignment.targetId !== rotationId);
  next.messages.unshift(`玩家航班串已删除：${rotation.name}。`);
  return next;
}

export function upsertAircraft(save: SaveGame, input: AircraftInput): SaveGame {
  const next = cloneSave(save);
  const registration = normalizeRegistration(input.registration);
  const location = requireAirport(input.location).code;
  const type = aircraftTypeById(input.typeId);
  if (!type) {
    throw new Error("机型数据缺失。");
  }
  const defaults = defaultAircraftCapabilities(type);
  if (next.fleet.some((aircraft) => aircraft.registration === registration && aircraft.id !== input.id)) {
    throw new Error("飞机注册号已存在。");
  }
  const existingIndex = fleetAircraftIndex(next, input.id);
  if (input.id && existingIndex < 0) {
    throw new Error("飞机不存在。");
  }
  const aircraft: Aircraft = {
    id: input.id ?? nextId("ac", next.fleet.length + next.day),
    registration,
    typeId: input.typeId,
    status: input.status,
    location,
    etopsMinutes: aircraftEtopsMinutes(input, defaults),
    maxTakeoffWeightKg: aircraftMaxTakeoffWeightKg(input, defaults),
    ilsCategory: input.ilsCategory ?? defaults.ilsCategory,
    fuelOnBoardKg: aircraftInitialFuelOnBoardKg(input, type),
    activeFlightInstanceId: input.activeFlightInstanceId,
    hoursSinceMaintenance: aircraftHoursSinceMaintenance(input),
    cyclesSinceCheck: aircraftCyclesSinceCheck(input),
    availableFromDay: aircraftAvailableFromDay(input, next.day)
  };
  if (existingIndex >= 0) {
    next.fleet[existingIndex] = aircraft;
  } else {
    next.fleet.push(aircraft);
  }
  next.messages.unshift(`${aircraftUpsertMessage(existingIndex)}：${aircraft.registration}。`);
  return next;
}

function aircraftEtopsMinutes(input: AircraftInput, defaults: ReturnType<typeof defaultAircraftCapabilities>): number {
  const rawEtopsMinutes = Number(input.etopsMinutes) || defaults.etopsMinutes;
  const roundedEtopsMinutes = Math.round(rawEtopsMinutes);
  return Math.max(0, roundedEtopsMinutes);
}

function aircraftMaxTakeoffWeightKg(input: AircraftInput, defaults: ReturnType<typeof defaultAircraftCapabilities>): number {
  const rawMaxTakeoffWeightKg = Number(input.maxTakeoffWeightKg) || defaults.maxTakeoffWeightKg;
  const roundedMaxTakeoffWeightKg = Math.round(rawMaxTakeoffWeightKg);
  return Math.max(1, roundedMaxTakeoffWeightKg);
}

function aircraftInitialFuelOnBoardKg(input: AircraftInput, type: AircraftType): number {
  const defaultFuelOnBoardKg = type.maxFuelKg * 0.5;
  const rawFuelOnBoardKg = Number(input.fuelOnBoardKg) || defaultFuelOnBoardKg;
  const roundedFuelOnBoardKg = Math.round(rawFuelOnBoardKg);
  return Math.max(0, roundedFuelOnBoardKg);
}

function aircraftHoursSinceMaintenance(input: AircraftInput): number {
  const rawHoursSinceMaintenance = Number(input.hoursSinceMaintenance) || 0;
  return Math.max(0, rawHoursSinceMaintenance);
}

function aircraftCyclesSinceCheck(input: AircraftInput): number {
  const rawCyclesSinceCheck = Number(input.cyclesSinceCheck) || 0;
  const roundedCyclesSinceCheck = Math.round(rawCyclesSinceCheck);
  return Math.max(0, roundedCyclesSinceCheck);
}

function aircraftAvailableFromDay(input: AircraftInput, currentDay: number): number {
  const rawAvailableFromDay = Number(input.availableFromDay) || currentDay;
  const roundedAvailableFromDay = Math.round(rawAvailableFromDay);
  return Math.max(1, roundedAvailableFromDay);
}

function fleetAircraftIndex(save: SaveGame, aircraftId: string | undefined): number {
  if (!aircraftId) {
    return -1;
  }
  return save.fleet.findIndex((aircraft) => aircraft.id === aircraftId);
}

function aircraftUpsertMessage(existingIndex: number): string {
  if (existingIndex >= 0) {
    return "飞机资料已更新";
  }
  return "飞机资料已新增";
}

export function deleteAircraft(save: SaveGame, aircraftId: string): SaveGame {
  const next = cloneSave(save);
  if (next.activeOpsDay?.flights.some((flight) => flight.aircraftId === aircraftId && !isTerminalFlightStatus(flight.status))) {
    throw new Error("运行日开放时不能删除正在执行的飞机。");
  }
  const aircraft = next.fleet.find((item) => item.id === aircraftId);
  if (!aircraft) {
    throw new Error("飞机不存在。");
  }
  next.fleet = next.fleet.filter((item) => item.id !== aircraftId);
  next.assignments = next.assignments.filter((assignment) => assignment.aircraftId !== aircraftId);
  next.messages.unshift(`飞机资料已删除：${aircraft.registration}。相关排班已释放。`);
  return next;
}

export function buildOpsDayPreview(save: SaveGame, schedule: ScheduleSource = EMPTY_SCHEDULE, activeEvents: GlobalEvent[] = []): ActiveOpsDay {
  if (save.activeOpsDay?.status === "open") {
    const active = cloneSave(save);
    syncActiveRecoveryFlights(active);
    sortOpsEvents(active.activeOpsDay!);
    return active.activeOpsDay!;
  }
  const previewSource = cloneSave(save);
  previewSource.activeOpsDay = undefined;
  const preview = startOpsDay(previewSource, schedule, activeEvents).activeOpsDay;
  if (!preview) {
    throw new Error("无法生成运营日时间轴。");
  }
  return preview;
}

export function upsertAssignment(save: SaveGame, assignment: Omit<DispatchAssignment, "id">, schedule?: ScheduleSource): SaveGame {
  const next = cloneSave(save);
  if (!assignmentTargetExists(next, assignment, schedule)) {
    throw new Error("assignment target not found");
  }
  if (!next.fleet.some((aircraft) => aircraft.id === assignment.aircraftId)) {
    throw new Error("aircraft not found");
  }
  if (schedule) {
    const blocks = validateAssignmentTarget(next, schedule, assignment);
    if (blocks.length > 0) {
      throw new Error(blocks[0]);
    }
  }

  const index = next.assignments.findIndex((item) => item.targetType === assignment.targetType && item.targetId === assignment.targetId);
  const value: DispatchAssignment = {
    ...assignment,
    assignmentMode: assignment.assignmentMode ?? "manual",
    updatedDay: next.day,
    id: assignmentIdForUpsert(next, index)
  };
  if (index >= 0) {
    next.assignments[index] = value;
  } else {
    next.assignments.push(value);
  }
  applyAssignmentToActiveFlight(next, value);
  next.messages.unshift(`运行排班已锁定：${assignmentTargetLabel(assignment.targetType)} ${assignment.targetId}。`);
  return next;
}

function assignmentIdForUpsert(save: SaveGame, existingIndex: number): string {
  if (existingIndex >= 0) {
    return save.assignments[existingIndex].id;
  }
  return nextId("assign", save.assignments.length + save.day);
}

function assignmentTargetLabel(targetType: DispatchAssignment["targetType"]): string {
  if (targetType === "rotation") {
    return "玩家航班串";
  }
  return "恢复/调机";
}

function recoveryFlightNo(recovery: RecoveryFlight): string {
  const suffix = recovery.id.slice(-4);
  if (recovery.kind === "positioning") {
    return `POS${suffix}`;
  }
  return `RCV${suffix}`;
}

function aircraftAssignmentSource(assignmentMode: DispatchAssignment["assignmentMode"]): FlightInstance["aircraftAssignmentSource"] {
  if (assignmentMode === "auto") {
    return "auto";
  }
  return "manual";
}

function assignmentModeForFlight(assignment: DispatchAssignment | undefined): FlightInstance["aircraftAssignmentSource"] {
  if (assignment) {
    return assignment.assignmentMode;
  }
  return "none";
}

function recoveredFlightStatus(currentMinute: number, estimatedDepartureMinute: number): FlightInstance["status"] {
  if (currentMinute > estimatedDepartureMinute) {
    return "delayed";
  }
  return "awaiting_release";
}

function recoveredFlightReason(status: FlightInstance["status"]): string | undefined {
  if (status === "delayed") {
    return "恢复排班已补齐，等待重新放行";
  }
  return undefined;
}

export function validateAssignmentTarget(save: SaveGame, schedule: ScheduleSource, assignment: Omit<DispatchAssignment, "id">): string[] {
  const aircraft = save.fleet.find((item) => item.id === assignment.aircraftId);
  if (!aircraft) {
    return ["指派飞机不存在。"];
  }
  if (assignment.targetType === "rotation") {
    const rotation = save.rotations.find((item) => item.id === assignment.targetId);
    if (!rotation) {
      return ["玩家航班串不存在。"];
    }
    const { plans, missingPlanIds } = resolveRotationLegPlans(rotation, schedule, aircraft.typeId, save.day);
    if (missingPlanIds.length > 0) {
      return [`航班串引用的 GM 航班计划不存在：${missingPlanIds.join(" / ")}。`];
    }
    return plans.flatMap((plan) =>
      validateOperation({
        day: save.day,
        origin: plan.origin,
        destination: plan.destination,
        distanceKm: plan.distanceKm,
        assignment: assignment as DispatchAssignment,
        licenseRequired: undefined,
        grantedLicenseCodes: save.licenses.filter((license) => license.granted).map((license) => license.code),
        aircraft,
        events: []
      })
    );
  }
  if (assignment.targetType === "recovery") {
    const recovery = save.recoveryFlights.find((item) => item.id === assignment.targetId);
    if (!recovery) {
      return ["恢复/调机任务不存在。"];
    }
    const turn = turnProfileFor(recovery.aircraftTypeId, recovery.origin, recovery.destination);
    const plan = buildLegPlan(
      {
        id: recovery.id,
        sequence: 1,
        flightNo: recoveryFlightNo(recovery),
        origin: recovery.origin,
        destination: recovery.destination,
        departureDayOffset: Math.max(0, recovery.scheduledDay - save.day),
        departureTimeLocal: recovery.departureTimeLocal,
        minimumTurnMinutes: turn.minimumTurnMinutes
      },
      recovery.aircraftTypeId
    );
    return validateOperation({
      day: save.day,
      plannedTypeId: recovery.aircraftTypeId,
      origin: plan.origin,
      destination: plan.destination,
      distanceKm: plan.distanceKm,
      assignment: assignment as DispatchAssignment,
      licenseRequired: undefined,
      grantedLicenseCodes: save.licenses.filter((license) => license.granted).map((license) => license.code),
      aircraft,
      events: []
    });
  }
  return [];
}

function applyAssignmentToActiveFlight(save: SaveGame, assignment: DispatchAssignment) {
  const ops = save.activeOpsDay;
  if (!ops || ops.status !== "open") {
    return;
  }
  let flight = ops.flights.find((item) => item.sourceType === assignment.targetType && item.sourceId === assignment.targetId);
  if (!flight && assignment.targetType === "recovery") {
    const recovery = save.recoveryFlights.find((item) => item.id === assignment.targetId && (item.status === "open" || item.status === "assigned") && item.scheduledDay <= save.day);
    if (recovery) {
      enqueueActiveRecoveryFlight(save, recovery);
      flight = ops.flights.find((item) => item.sourceType === assignment.targetType && item.sourceId === assignment.targetId);
    }
  }
  if (!flight || !["scheduled", "awaiting_release", "needs_recovery", "delayed"].includes(flight.status)) {
    return;
  }
  const aircraft = save.fleet.find((item) => item.id === assignment.aircraftId);
  flight.aircraftId = assignment.aircraftId;
  flight.aircraftTypeId = aircraft?.typeId ?? flight.aircraftTypeId;
  flight.fuelPolicy = assignment.fuelPolicy;
  flight.aircraftAssignmentSource = aircraftAssignmentSource(assignment.assignmentMode);
  if (flight.status === "needs_recovery") {
    flight.status = recoveredFlightStatus(ops.currentMinute, flight.estimatedDepartureMinute);
    flight.reason = recoveredFlightReason(flight.status);
  }
  if (assignment.targetType === "recovery") {
    const recovery = save.recoveryFlights.find((item) => item.id === assignment.targetId);
    if (recovery) {
      applyRecoveryReadiness(save, ops, recovery, flight);
    }
  }
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 已锁定 ${aircraft?.registration ?? assignment.aircraftId} 执行。`, assignment);
}

export function startOpsDay(save: SaveGame, schedule: ScheduleSource = EMPTY_SCHEDULE, activeEvents: GlobalEvent[] = []): SaveGame {
  const next = cloneSave(save);
  if (next.activeOpsDay?.status === "open" && next.activeOpsDay.day === next.day) {
    return next;
  }

  const alerts: string[] = [];
  restoreAircraftAvailability(next, alerts);
  const disruptionState = {
    day: next.day,
    reputation: next.reputation,
    licenses: next.licenses,
    fleet: next.fleet
  };
  applyDailyEventDisruptions(disruptionState, activeEvents, alerts);
  next.reputation = disruptionState.reputation;

  const dailyPlan: DailyResourcePlan = { usedAircraftIds: new Set(), aircraftStates: new Map() };
  const ops: ActiveOpsDay = {
    id: `ops-${next.day}`,
    day: next.day,
    dateIso: next.dateIso,
    currentMinute: 0,
    clockMode: "paused",
    autoStepMinutes: 30,
    autoStepSeconds: 30,
    status: "open",
    flights: [],
    events: [],
    nextEventSeq: 1
  };

  for (const rotation of next.rotations.filter((item) => rotationOperatesToday(item, next, schedule)).sort((a, b) => rotationPriority(schedule, b) - rotationPriority(schedule, a))) {
    const { plans, missingPlanIds } = resolveRotationLegPlans(rotation, schedule, undefined, next.day);
    for (const missingId of missingPlanIds) {
      appendOpsEvent(ops, `missing-${rotation.id}-${missingId}`, "blocked", 0, "system", `GM 航班计划缺失：${missingId}`);
    }
    const rotationAssignments = resolveRotationFlightAssignments(next, dailyPlan, rotation, plans, activeEvents);
    for (const plan of plans) {
      const resolved = rotationAssignments.get(plan.template.sequence);
      const flight = createFlightInstance({
        save: next,
        sourceType: "rotation",
        sourceId: rotation.id,
        sourceTitle: rotation.name,
        plan,
        assignment: resolved?.assignment,
        aircraftAssignmentSource: resolved?.assignmentSource ?? "none",
        sequence: plan.template.sequence
      });
      if (!resolved?.assignment) {
        flight.status = "needs_recovery";
        flight.reason = resolved?.reason ?? "始发站无可用飞机，需要 AOC 手动处置";
      }
      ops.flights.push(flight);
      appendOpsEvent(ops, flight.id, "created", 0, "system", `${flight.flightNo} 已进入运行日队列。`);
      if (!resolved?.assignment) {
        appendOpsEvent(ops, flight.id, "blocked", 0, "system", `${flight.flightNo} 待恢复：${flight.reason}`);
      }
    }
  }

  for (const recovery of next.recoveryFlights.filter((item) => (item.status === "open" || item.status === "assigned") && item.scheduledDay <= next.day)) {
    const resolved = resolveDailyAssignment(next, dailyPlan, {
      targetType: "recovery",
      targetId: recovery.id,
      preferredTypeId: recovery.aircraftTypeId
    });
    commitOpsAssignment(next, dailyPlan, resolved.assignment);
    const flight = createFlightInstance({
      save: next,
      sourceType: "recovery",
      sourceId: recovery.id,
      sourceTitle: recovery.title,
      plan: buildLegPlan(recoveryFlightTemplate(next, recovery), recovery.aircraftTypeId),
      assignment: resolved.assignment,
      aircraftAssignmentSource: assignmentModeForFlight(resolved.assignment),
      sequence: 1,
      recovery
    });
    ops.flights.push(flight);
    recovery.status = "assigned";
    appendOpsEvent(ops, flight.id, "created", 0, "system", `${flight.flightNo} 已进入恢复运行队列。`);
    applyRecoveryReadiness(next, ops, recovery, flight);
  }

  next.activeOpsDay = ops;
  next.messages = [`运行日 D${next.day} 已启动：${ops.flights.length} 班进入实时追踪。`, ...alerts, ...next.messages].slice(0, 100);
  return next;
}

function enqueueActiveRecoveryFlight(save: SaveGame, recovery: RecoveryFlight) {
  const ops = save.activeOpsDay;
  if (!ops || ops.status !== "open" || ops.flights.some((flight) => flight.sourceType === "recovery" && flight.sourceId === recovery.id)) {
    return;
  }
  const assignment = save.assignments.find((item) => item.targetType === "recovery" && item.targetId === recovery.id);
  const flight = createFlightInstance({
    save,
    sourceType: "recovery",
    sourceId: recovery.id,
    sourceTitle: recovery.title,
    plan: buildLegPlan(recoveryFlightTemplate(save, recovery), recovery.aircraftTypeId),
    assignment,
    aircraftAssignmentSource: assignmentModeForFlight(assignment),
    sequence: 1,
    recovery
  });
  if (!assignment) {
    flight.status = "needs_recovery";
    flight.reason = "未完成飞机排班。";
  } else {
    recovery.status = "assigned";
  }
  ops.flights.push(flight);
  appendOpsEvent(ops, flight.id, "created", ops.currentMinute, "system", `${flight.flightNo} 已进入恢复运行队列。`);
  applyRecoveryReadiness(save, ops, recovery, flight);
  if (!assignment) {
    appendOpsEvent(ops, flight.id, "blocked", ops.currentMinute, "system", `${flight.flightNo} 待恢复：${flight.reason}`);
  }
}

function syncActiveRecoveryFlights(save: SaveGame) {
  if (!save.activeOpsDay || save.activeOpsDay.status !== "open") {
    return;
  }
  for (const recovery of save.recoveryFlights.filter((item) => (item.status === "open" || item.status === "assigned") && item.scheduledDay <= save.day)) {
    enqueueActiveRecoveryFlight(save, recovery);
  }
}

function normalizePassengerTransfers(transfers: PassengerTransfer[] | undefined, save: SaveGame, sourceFlight: FlightInstance | undefined): PassengerTransfer[] {
  if (transfers && transfers.length > 0) {
    return transfers
      .map((transfer) => ({
        fromFlightInstanceId: transfer.fromFlightInstanceId,
        passengers: normalizedTransferPassengerCount(transfer.passengers),
        reason: transfer.reason
      }))
      .filter((transfer) => transfer.fromFlightInstanceId && transfer.passengers > 0);
  }
  if (!sourceFlight) {
    return [];
  }
  const passengers = estimatedPassengerCountForFlight(sourceFlight);
  if (passengers <= 0) {
    return [];
  }
  return [
    {
      fromFlightInstanceId: sourceFlight.id,
      passengers,
      reason: `${sourceFlight.flightNo} 未送达旅客转运`
    }
  ];
}

function normalizedTransferPassengerCount(passengers: number): number {
  const roundedPassengers = Math.round(passengers);
  return Math.max(0, roundedPassengers);
}

function recoveryDepartureDayOffset(scheduledDay: number, currentDay: number): number {
  const dayOffset = scheduledDay - currentDay;
  return Math.max(0, dayOffset);
}

function recoveryReadinessDepartureDelay(readyAtMinute: number, estimatedDepartureMinute: number): number {
  const delayMinutes = readyAtMinute - estimatedDepartureMinute;
  return Math.max(0, delayMinutes);
}

function recoveryFlightTemplate(save: SaveGame, recovery: RecoveryFlight): FlightLegTemplate {
  const turn = turnProfileFor(recovery.aircraftTypeId, recovery.origin, recovery.destination);
  return {
    id: recovery.id,
    sequence: 1,
    flightNo: recoveryFlightNo(recovery),
    origin: recovery.origin,
    destination: recovery.destination,
    departureDayOffset: recoveryDepartureDayOffset(recovery.scheduledDay, save.day),
    departureTimeLocal: recovery.departureTimeLocal,
    minimumTurnMinutes: turn.minimumTurnMinutes
  };
}

function applyRecoveryReadiness(save: SaveGame, ops: ActiveOpsDay, recovery: RecoveryFlight, flight: FlightInstance) {
  const readiness = buildRecoveryReadiness(save, ops, recovery, flight);
  const previousReadyAt = flight.readiness?.readyAtMinute;
  flight.readiness = readiness;
  flight.minimumTurnMinutes = readiness.minimumTurnMinutes;
  const departureDelay = recoveryReadinessDepartureDelay(readiness.readyAtMinute, flight.estimatedDepartureMinute);
  if (departureDelay > 0) {
    flight.estimatedDepartureMinute = readiness.readyAtMinute;
    flight.estimatedArrivalMinute += departureDelay;
    flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
    if (flight.status === "scheduled" || flight.status === "awaiting_release" || flight.status === "delayed") {
      flight.status = "delayed";
      flight.reason = `恢复准备窗口延后 ${departureDelay} 分钟`;
    } else if (!flight.reason) {
      flight.reason = `恢复准备窗口延后 ${departureDelay} 分钟`;
    }
  }
  if (previousReadyAt === readiness.readyAtMinute) {
    return;
  }
  appendOpsEvent(
    ops,
    flight.id,
    "ready",
    ops.currentMinute,
    "system",
    `${flight.flightNo} 恢复准备评估：最早起飞 ${dayMinuteToLabel(readiness.readyAtMinute)}，${readiness.reasons.join(" / ")}。`,
    readiness
  );
  if (departureDelay > 0) {
    appendOpsEvent(ops, flight.id, "delayed", ops.currentMinute, "system", `${flight.flightNo} 因恢复准备窗口延后至 ${dayMinuteToLabel(readiness.readyAtMinute)}。`, readiness);
  }
  appendOpsEvent(ops, flight.id, "ready", readiness.readyAtMinute, "system", `${flight.flightNo} 恢复准备完成，可执行。`, readiness);
}

function buildRecoveryReadiness(save: SaveGame, ops: ActiveOpsDay, recovery: RecoveryFlight, flight: FlightInstance): NonNullable<FlightInstance["readiness"]> {
  const turn = turnProfileFor(flight.aircraftTypeId, flight.origin, flight.destination, recovery.id);
  const sourceFlight = opsFlightById(ops, recovery.recoveryForFlightInstanceId);
  const priorAircraftFlight = latestPriorAircraftFlight(ops, flight);
  const sourceArrivalMinute = arrivalMinuteForReadiness(sourceFlight);
  const aircraftArrivalMinute = arrivalMinuteForReadiness(priorAircraftFlight);
  const passengerCount = recovery.passengerTransfers?.reduce((sum, transfer) => sum + transfer.passengers, 0) ?? 0;
  const responseMinutes = randomRange(`${recovery.id}:response`, POSITIONING_RESPONSE_RANGE[0], POSITIONING_RESPONSE_RANGE[1]);
  const passengerTransferMinutes = recoveryPassengerTransferMinutes(recovery, passengerCount);
  const generatedMinute = ops.currentMinute;
  const readyCandidates = [
    flight.plannedDepartureMinute,
    generatedMinute + responseMinutes
  ];
  if (sourceArrivalMinute !== undefined) {
    readyCandidates.push(sourceArrivalMinute + turn.actualTurnMinutes);
    if (passengerTransferMinutes > 0) {
      readyCandidates.push(sourceArrivalMinute + passengerTransferMinutes);
    }
  }
  if (aircraftArrivalMinute !== undefined && priorAircraftFlight?.destination === flight.origin) {
    readyCandidates.push(aircraftArrivalMinute + turn.actualTurnMinutes);
  }
  const readyAtMinute = Math.max(...readyCandidates);
  const reasons = [
    `过站 ${turn.actualTurnMinutes}m`,
    `签派响应 ${responseMinutes}m`
  ];
  if (passengerTransferMinutes > 0) {
    reasons.push(`旅客转运 ${passengerTransferMinutes}m`);
  }
  if (passengerCount > 0) {
    reasons.push(`承接 ${passengerCount} pax`);
  }
  return {
    generatedMinute,
    requestedDepartureMinute: flight.plannedDepartureMinute,
    minimumTurnMinutes: turn.minimumTurnMinutes,
    standardTurnMinutes: turn.standardTurnMinutes,
    actualTurnMinutes: turn.actualTurnMinutes,
    responseMinutes,
    passengerTransferMinutes,
    readyAtMinute,
    readyWindowStartMinute: recoveryReadyWindowStartMinute(flight.plannedDepartureMinute, readyAtMinute, sourceArrivalMinute, turn.minimumTurnMinutes),
    readyWindowEndMinute: readyAtMinute + 20,
    sourceArrivalMinute,
    reasons
  };
}

function opsFlightById(ops: ActiveOpsDay, flightInstanceId: string | undefined): FlightInstance | undefined {
  if (!flightInstanceId) {
    return undefined;
  }
  return ops.flights.find((item) => item.id === flightInstanceId);
}

function arrivalMinuteForReadiness(flight: FlightInstance | undefined): number | undefined {
  if (!flight) {
    return undefined;
  }
  return flight.actualArrivalMinute ?? flight.estimatedArrivalMinute;
}

function recoveryReadyWindowStartMinute(plannedDepartureMinute: number, readyAtMinute: number, sourceArrivalMinute: number | undefined, minimumTurnMinutes: number): number {
  const transferReadyMinute = (sourceArrivalMinute ?? readyAtMinute) + minimumTurnMinutes;
  const readinessLimitedMinute = Math.min(readyAtMinute, transferReadyMinute);
  return Math.max(plannedDepartureMinute, readinessLimitedMinute);
}

function recoveryPassengerTransferMinutes(recovery: RecoveryFlight, passengerCount: number): number {
  if (recovery.kind === "positioning") {
    return 0;
  }
  if (passengerCount > 0) {
    return randomRange(`${recovery.id}:passengers`, RECOVERY_PASSENGER_RESPONSE_RANGE[0], RECOVERY_PASSENGER_RESPONSE_RANGE[1]);
  }
  return randomRange(`${recovery.id}:empty-recovery`, RECOVERY_EMPTY_RESPONSE_RANGE[0], RECOVERY_EMPTY_RESPONSE_RANGE[1]);
}

function latestPriorAircraftFlight(ops: ActiveOpsDay, flight: FlightInstance): FlightInstance | undefined {
  if (!flight.aircraftId) {
    return undefined;
  }
  return ops.flights
    .filter((item) => item.id !== flight.id && item.aircraftId === flight.aircraftId && item.estimatedArrivalMinute <= flight.estimatedDepartureMinute)
    .sort((a, b) => b.estimatedArrivalMinute - a.estimatedArrivalMinute)[0];
}

function turnProfileFor(aircraftTypeId: string, originCode: string, destinationCode: string, seed = `${aircraftTypeId}:${originCode}:${destinationCode}`) {
  const type = aircraftTypeById(aircraftTypeId);
  const origin = requireAirport(originCode);
  const destination = requireAirport(destinationCode);
  const widebody = (type?.seatCapacity ?? 0) >= 220;
  const international = origin.countryZh !== DOMESTIC_COUNTRY || destination.countryZh !== DOMESTIC_COUNTRY;
  const base = baseTurnMinutes(widebody);
  const internationalAddon = internationalTurnAddon(international);
  const minimumTurnMinutes = base - 10 + internationalAddon;
  const standardTurnMinutes = base + internationalAddon;
  const randomDelayCeiling = turnRandomDelayCeiling(international);
  const randomTurnVariation = randomRange(`${seed}:turn`, -10, randomDelayCeiling);
  const actualTurnMinutes = Math.max(minimumTurnMinutes, standardTurnMinutes + randomTurnVariation);
  return { minimumTurnMinutes, standardTurnMinutes, actualTurnMinutes };
}

function baseTurnMinutes(widebody: boolean): number {
  if (widebody) {
    return 55;
  }
  return 45;
}

function internationalTurnAddon(international: boolean): number {
  if (international) {
    return 20;
  }
  return 0;
}

function turnRandomDelayCeiling(international: boolean): number {
  if (international) {
    return 20;
  }
  return 15;
}

function randomRange(seed: string, min: number, max: number): number {
  const low = Math.min(min, max);
  const high = Math.max(min, max);
  return low + (stableHash(seed) % (high - low + 1));
}

function stableHash(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function opsTickTargetMinute(currentMinute: number, minutes: number): number {
  const roundedMinutes = Math.round(minutes);
  const protectedMinutes = Math.max(0, roundedMinutes);
  return Math.max(currentMinute, currentMinute + protectedMinutes);
}

export function tickOps(save: SaveGame, minutes: number, schedule: ScheduleSource = EMPTY_SCHEDULE): SaveGame {
  const next = cloneSave(save);
  const ops = next.activeOpsDay;
  if (!ops || ops.status !== "open") {
    return next;
  }
  syncActiveRecoveryFlights(next);
  const targetMinute = opsTickTargetMinute(ops.currentMinute, minutes);

  for (const flight of [...ops.flights].sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute || a.sequence - b.sequence)) {
    if (isTerminalFlightStatus(flight.status)) {
      continue;
    }
    advanceGroundOpsNodes(next, ops, flight, targetMinute);
    if (
      targetMinute >= flight.estimatedDepartureMinute &&
      (flight.status === "awaiting_release" || flight.status === "scheduled" || flight.status === "needs_recovery" || flight.status === "delayed" || flight.status === "released")
    ) {
      holdUnreleasedFlight(ops, flight, targetMinute);
    }
    dispatchDueOccurrenceEventsForFlight(ops, schedule, flight, targetMinute);
    if (targetMinute >= flight.estimatedDepartureMinute && flight.status === "ready_for_departure") {
      departFlight(next, ops, flight);
    }
    dispatchDueOccurrenceEventsForFlight(ops, schedule, flight, targetMinute);
    if (targetMinute >= flight.estimatedArrivalMinute && (flight.status === "airborne" || flight.status === "diverted")) {
      landFlight(next, ops, flight);
    }
    propagateAircraftDelays(ops, flight.aircraftId);
  }

  ops.currentMinute = targetMinute;
  return next;
}

export function isOpsDayReadyToClose(save: SaveGame): boolean {
  const ops = save.activeOpsDay;
  return Boolean(ops && ops.status === "open" && isReadyToCloseOpsDay(ops));
}

export function applyRedispatch(
  save: SaveGame,
  action: RedispatchAction,
  activeEvents: GlobalEvent[] = [],
  schedule: ScheduleSource = EMPTY_SCHEDULE,
  configContext?: RedispatchAirportConfigContext
): SaveGame {
  const next = cloneSave(save);
  const ops = requireOpenOpsDay(next);
  syncActiveRecoveryFlights(next);
  advanceAllGroundOpsNodes(next, ops, ops.currentMinute);
  const flight = requireRedispatchFlight(ops, action.flightInstanceId);
  dispatchDueOccurrenceEventsForFlight(ops, schedule, flight, ops.currentMinute);
  applyRedispatchAction({ save: next, ops, flight, activeEvents, schedule, configContext }, action);
  dispatchDueOccurrenceEventsForFlight(ops, schedule, flight, ops.currentMinute);

  propagateAircraftDelays(ops, flight.aircraftId);
  return next;
}

type RedispatchContext = {
  save: SaveGame;
  ops: ActiveOpsDay;
  flight: FlightInstance;
  activeEvents: GlobalEvent[];
  schedule: ScheduleSource;
  configContext?: RedispatchAirportConfigContext;
};

function requireOpenOpsDay(save: SaveGame): ActiveOpsDay {
  if (!save.activeOpsDay || save.activeOpsDay.status !== "open") {
    throw new Error("当前没有开放的运行日。");
  }
  return save.activeOpsDay;
}

function requireRedispatchFlight(ops: ActiveOpsDay, flightInstanceId: string): FlightInstance {
  const flight = ops.flights.find((item) => item.id === flightInstanceId);
  if (!flight) {
    throw new Error("运行航班不存在。");
  }
  if (flight.status === "landed" || flight.status === "cancelled" || flight.status === "failed") {
    throw new Error("已结束航班不能重签派。");
  }
  return flight;
}

function applyRedispatchAction(context: RedispatchContext, action: RedispatchAction) {
  switch (action.type) {
    case "delay":
      delayFlight(context, action);
      return;
    case "cancel":
      cancelFlight(context, action);
      return;
    case "confirm_release_plan":
      confirmReleasePlan(context.save, context.ops, context.flight, context.activeEvents, context.schedule, requireFlightConfig(context.configContext, "确认放行计划需要航班机场配置，请重试操作。"), { actor: "player", minute: context.ops.currentMinute, preserveOnBlock: true });
      return;
    case "start_ground_service":
      startGroundService(context.save, context.ops, context.flight, requireGroundOpsConfig(context.configContext, "开始地面保障需要机场地服配置，请重试操作。"), action.reason || "玩家开始地面保障");
      return;
    case "swap_aircraft":
      swapAircraft(context, action);
      return;
    case "reroute":
      rerouteRedispatchedFlight(context, action);
      return;
    case "technical_stop":
      addTechnicalStop(context, action);
      return;
    case "adjust_payload":
      adjustPayload(context, action);
      return;
    case "set_fuel":
      setFuelOrder(context, action);
      return;
    case "divert":
      divertRedispatchedFlight(context, action);
      return;
    case "resolve_disruption":
      resolveFlightDisruption(context.save, context.ops, context.flight, action, context.schedule);
      return;
    case "continue_to_destination":
      continueRedispatchedFlight(context, action);
      return;
    case "complete_at_current_airport":
      completeRedispatchedFlight(context, action);
      return;
    case "unbind_rotation":
      unbindRotation(context, action);
      return;
  }
}

function delayFlight({ ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "delay" }>) {
  assertFlightActionWindowOpen(flight, ops.currentMinute, "延误");
  const delay = redispatchDelayMinutes(action.delayMinutes);
  flight.estimatedDepartureMinute += delay;
  flight.estimatedArrivalMinute += delay;
  flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
  flight.status = "delayed";
  flight.reason = action.reason || `玩家延误 ${delay} 分钟`;
  addDelayEvent(flight, "player_action", delay, true, flight.reason);
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "delayed", ops.currentMinute, "player", `${flight.flightNo} 延误 ${delay} 分钟。`, action);
}

function redispatchDelayMinutes(delayMinutes: number): number {
  const roundedDelayMinutes = Math.round(delayMinutes);
  return Math.max(1, roundedDelayMinutes);
}

function cancelFlight({ save, ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "cancel" }>) {
  flight.status = "cancelled";
  flight.completionMode = "cancelled";
  flight.finalAirport = flight.currentAirport ?? flight.origin;
  flight.reason = action.reason || "玩家取消航班";
  flight.costUsd = Math.max(flight.costUsd, 12_000);
  cancelPendingGroundOpsNodes(flight, ops.currentMinute, allGroundOpsNodeKinds(), "all");
  releaseAircraftLock(save, flight);
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "cancelled", ops.currentMinute, "player", `${flight.flightNo} 已取消：${flight.reason}`, action);
}

function swapAircraft(context: RedispatchContext, action: Extract<RedispatchAction, { type: "swap_aircraft" }>) {
  const { save, ops, flight } = context;
  const aircraft = save.fleet.find((item) => item.id === action.aircraftId);
  if (!aircraft) {
    throw new Error("替换飞机不存在。");
  }
  const candidate = buildAircraftReleaseCandidates(save, ops, flight).find((item) => item.aircraftId === aircraft.id);
  if (!candidate || candidate.availability === "unavailable") {
    throw new Error(candidate?.reason ?? "所选飞机不能执行本班。");
  }
  releaseAircraftLock(save, flight);
  flight.aircraftId = aircraft.id;
  flight.aircraftTypeId = aircraft.typeId;
  flight.aircraftAssignmentSource = "manual";
  cancelPendingGroundOpsNodes(flight, ops.currentMinute, ["fuel_uplift", "fuel_defuel"]);
  flight.fuelOrder = undefined;
  clearFlightLedger(flight);
  markFlightNeedsRelease(flight);
  flight.reason = action.reason || "玩家更换飞机";
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 改由 ${aircraft.registration} 执行。`, action);
  cascadeManualAircraftSwap(save, ops, flight, aircraft, action.reason || "同一航班串前序换机");
}

function rerouteRedispatchedFlight({ save, ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "reroute" }>) {
  const destination = requireAirport(action.destination);
  rerouteFlight(save, flight, destination.code, action.reason || "玩家改航");
  resetReleaseSensitiveFlightState(ops, flight);
  flight.reason = action.reason || "玩家改航";
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 改航至 ${destination.code}。`, action);
}

function addTechnicalStop({ ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "technical_stop" }>) {
  const stop = requireAirport(action.airportCode);
  const origin = requireAirport(flight.origin);
  const destination = requireAirport(flight.destination);
  const technicalDistance = calculateDistanceKm(origin.code, stop.code) + calculateDistanceKm(stop.code, destination.code);
  const extraMinutes = technicalStopExtraMinutes(technicalDistance, flight.distanceKm);
  flight.technicalStop = stop.code;
  flight.distanceKm = technicalDistance;
  flight.estimatedArrivalMinute += extraMinutes;
  flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
  resetReleaseSensitiveFlightState(ops, flight);
  flight.reason = action.reason || `技术经停 ${stop.code}`;
  addDelayEvent(flight, "player_action", extraMinutes, true, flight.reason);
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 增加 ${stop.code} 技术经停。`, action);
}

function technicalStopExtraMinutes(technicalDistanceKm: number, originalDistanceKm: number): number {
  const addedDistanceKm = technicalDistanceKm - originalDistanceKm;
  const addedBlockMinutes = Math.round(addedDistanceKm * HOURS_PER_KM * 60);
  const extraMinutes = addedBlockMinutes + 75;
  return Math.max(75, extraMinutes);
}

function adjustPayload({ save, ops, flight, configContext }: RedispatchContext, action: Extract<RedispatchAction, { type: "adjust_payload" }>) {
  assertFlightStillOnGround(flight, "减载");
  const current = flight.payloadAdjustment ?? { passengerReduction: 0, cargoReductionKg: 0 };
  const passengerReduction = action.passengerReduction ?? current.passengerReduction;
  const cargoReductionKg = action.cargoReductionKg ?? current.cargoReductionKg;
  const groundOps = currentGroundOpsForFlight(flight);
  if (groundOps?.status === "in_progress" || groundOps?.status === "completed") {
    enqueuePayloadOffloadNode(save, ops, flight, passengerReduction, cargoReductionKg, requireGroundOpsConfig(configContext, "机场地服配置缺失，无法新增减载节点。"), action.reason || "玩家减载");
  } else {
    updatePlannedPayloadAdjustment(ops, flight, passengerReduction, cargoReductionKg, action.reason || "玩家减载");
  }
}

function setFuelOrder({ save, ops, flight, configContext }: RedispatchContext, action: Extract<RedispatchAction, { type: "set_fuel" }>) {
  assertFlightStillOnGround(flight, "调整加油量");
  const groundOps = currentGroundOpsForFlight(flight);
  if (groundOps?.status === "in_progress" || groundOps?.status === "completed") {
    enqueueFuelServiceNode(save, ops, flight, action.upliftKg, action.reason || "玩家加油指令", requireGroundOpsConfig(configContext, "机场燃油配置缺失，无法新增加油节点。"));
  } else {
    updatePlannedFuelOrder(save, ops, flight, action.upliftKg, action.reason || "玩家加油计划");
  }
}

function divertRedispatchedFlight({ ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "divert" }>) {
  const alternate = requireAirport(action.airportCode);
  divertFlightToAirport(flight, alternate.code, ops.currentMinute, action.reason || `备降 ${alternate.code}`, undefined, undefined);
  appendOpsEvent(ops, flight.id, "diverted", ops.currentMinute, "player", `${flight.flightNo} 备降至 ${alternate.code}。`, action);
}

function continueRedispatchedFlight({ ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "continue_to_destination" }>) {
  continueFlightToPlannedDestination(ops, flight, action.reason || "从当前机场继续飞往原计划目的地");
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 从 ${flight.origin} 继续飞往 ${flight.destination}。`, action);
}

function completeRedispatchedFlight({ ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "complete_at_current_airport" }>) {
  completeFlightAtCurrentAirport(ops, flight, action.reason || "玩家手动结束本次商业航班");
  appendOpsEvent(ops, flight.id, "landed", ops.currentMinute, "player", `${flight.flightNo} 在 ${flight.finalAirport ?? flight.destination} 手动结束。`, action);
}

function unbindRotation({ ops, flight }: RedispatchContext, action: Extract<RedispatchAction, { type: "unbind_rotation" }>) {
  if (!flight.rotationId) {
    throw new Error("恢复/调机航班不属于固定航班串。");
  }
  flight.rotationBinding = "manually_unbound";
  flight.reason = action.reason || "玩家手动解绑本班与固定航班串关系";
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "unbound", ops.currentMinute, "player", `${flight.flightNo} 已从本次航班串自动规则中解绑。`, action);
}

function resetReleaseSensitiveFlightState(ops: ActiveOpsDay, flight: FlightInstance) {
  cancelPendingGroundOpsNodes(flight, ops.currentMinute, ["fuel_uplift", "fuel_defuel"]);
  flight.fuelOrder = undefined;
  clearFlightLedger(flight);
  markFlightNeedsRelease(flight);
}

function assertFlightStillOnGround(flight: FlightInstance, label: string) {
  if (flight.status === "airborne" || flight.status === "diverted") {
    throw new Error(`已起飞航班不能${label}。`);
  }
}

export function closeOpsDay(save: SaveGame, activeEvents: GlobalEvent[] = []): OpsDayCloseResult {
  const next = cloneSave(save);
  if (!next.activeOpsDay || next.activeOpsDay.status !== "open") {
    return { save: next, operations: [], alerts: [], activeEvents };
  }
  syncActiveRecoveryFlights(next);
  const ops = next.activeOpsDay;
  if (!isReadyToCloseOpsDay(ops)) {
    throw new Error("运行日仍有未结束航班，不能提前关闭。");
  }
  for (const flight of ops.flights) {
    if (!isTerminalFlightStatus(flight.status)) {
      flight.status = "failed";
      flight.reason = flight.reason || "运行日结束时航班尚未完成";
      flight.costUsd = Math.max(flight.costUsd, 12_000);
      releaseAircraftLock(next, flight);
      appendOpsEvent(ops, flight.id, "failed", ops.currentMinute, "system", `${flight.flightNo} 未完成，运行日结束。`);
    }
  }

  const operations = operationsFromFlightInstances(next, ops);
  const totalProfit = operations.reduce((sum, operation) => sum + operation.profitUsd, 0);
  const dailyKpis = calculateOperationalKpis(operations.flatMap((operation) => operation.legs));
  const reliabilityDelta = closeOpsReliabilityDelta(operations, dailyKpis);
  const reputationDelta = operations.reduce((sum, operation) => sum + operation.reputationDelta, 0);
  const alerts = closeOpsAlerts(operations);

  next.cashUsd = Math.round(next.cashUsd + totalProfit);
  next.reliability = clamp(Math.round(next.reliability + reliabilityDelta), 0, 100);
  next.reputation = clamp(Math.round(next.reputation + reputationDelta), 0, 100);
  next.operationsLog = [...operations, ...next.operationsLog].slice(0, 120);
  next.messages = [
    `运行日 D${next.day} 结束：到达正点率 ${dailyKpis.arrivalOnTimeRate}%，取消率 ${dailyKpis.cancellationRate}%，${formatSignedMoney(totalProfit)}。`,
    `实时运行影响：平均到达延误 ${dailyKpis.averageArrivalDelayMinutes} 分钟，受影响衔接 ${dailyKpis.propagatedConnections} 段，可靠性 ${signed(reliabilityDelta)}。`,
    ...alerts.slice(0, 6),
    ...next.messages
  ].slice(0, 100);
  next.activeOpsDay = undefined;
  next.day += 1;
  next.dateIso = addIsoDays(next.dateIso, 1);

  return { save: next, operations, alerts, activeEvents };
}

function isCompleteOpsDay(ops: ActiveOpsDay): boolean {
  return ops.flights.every((flight) => isTerminalFlightStatus(flight.status));
}

function isReadyToCloseOpsDay(ops: ActiveOpsDay): boolean {
  return isCompleteOpsDay(ops) || ops.currentMinute >= opsDayCloseMinute(ops);
}

function opsDayCloseMinute(ops: ActiveOpsDay): number {
  const plannedCloseMinutes = ops.flights.map((flight) => flight.plannedArrivalMinute + OPS_DAY_CLOSE_BUFFER_MINUTES);
  return Math.max(1440, ...plannedCloseMinutes);
}

function closeOpsReliabilityDelta(operations: DailyOperation[], dailyKpis: OperationalKpis): number {
  if (operations.length === 0) {
    return 0;
  }
  const arrivalReliabilityFactor = (dailyKpis.arrivalOnTimeRate - 85) / 12;
  const cancellationPenalty = dailyKpis.cancelledLegs * 0.8;
  return Number((arrivalReliabilityFactor - cancellationPenalty).toFixed(1));
}

function closeOpsAlerts(operations: DailyOperation[]): string[] {
  return operations.flatMap((operation) => {
    if (operation.status === "operated") {
      return [];
    }
    return operation.notes.map((note) => `${operation.title}: ${note}`);
  });
}

export function calculateOperationalKpis(legs: FlightLeg[]): OperationalKpis {
  const scheduledLegs = legs.length;
  const operatedLegs = legs.filter((leg) => leg.status === "operated" || leg.status === "delayed").length;
  const cancelledLegs = legs.filter((leg) => leg.status === "cancelled").length;
  const delayedLegs = legs.filter((leg) => leg.status === "delayed").length;
  const arrivalDelays = legs
    .filter((leg) => leg.status === "operated" || leg.status === "delayed")
    .map((leg) => normalizedLegArrivalDelayMinutes(leg));
  const onTimeArrivals = legs.filter((leg) => (leg.status === "operated" || leg.status === "delayed") && (leg.arrivalDelayMinutes ?? 0) <= ON_TIME_ARRIVAL_THRESHOLD_MINUTES).length;
  const propagatedConnections = legs.filter((leg) => (leg.propagatedDelayMinutes ?? 0) > 0).length;
  return {
    scheduledLegs,
    operatedLegs,
    onTimeArrivals,
    cancelledLegs,
    delayedLegs,
    arrivalOnTimeRate: arrivalOnTimeRate(scheduledLegs, onTimeArrivals),
    cancellationRate: cancellationRate(scheduledLegs, cancelledLegs),
    averageArrivalDelayMinutes: averageArrivalDelayMinutes(arrivalDelays),
    propagatedConnections
  };
}

function arrivalOnTimeRate(scheduledLegs: number, onTimeArrivals: number): number {
  if (scheduledLegs > 0) {
    return Math.round((onTimeArrivals / scheduledLegs) * 100);
  }
  return 100;
}

function cancellationRate(scheduledLegs: number, cancelledLegs: number): number {
  if (scheduledLegs > 0) {
    return Math.round((cancelledLegs / scheduledLegs) * 100);
  }
  return 0;
}

function averageArrivalDelayMinutes(arrivalDelays: number[]): number {
  if (arrivalDelays.length > 0) {
    return Math.round(arrivalDelays.reduce((sum, delay) => sum + delay, 0) / arrivalDelays.length);
  }
  return 0;
}

function normalizedLegArrivalDelayMinutes(leg: FlightLeg): number {
  const roundedArrivalDelayMinutes = Math.round(leg.arrivalDelayMinutes ?? 0);
  return Math.max(0, roundedArrivalDelayMinutes);
}

export function calculateDistanceKm(originCode: string, destinationCode: string): number {
  const origin = requireAirport(originCode);
  const destination = requireAirport(destinationCode);
  const toRad = (value: number) => (value * Math.PI) / 180;
  const earthKm = 6371;
  const dLat = toRad(destination.lat - origin.lat);
  const dLon = toRad(destination.lon - origin.lon);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(origin.lat)) * Math.cos(toRad(destination.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const centralAngleRadians = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceKm = earthKm * centralAngleRadians;
  return Math.round(distanceKm);
}

function flightBlockMinutes(distanceKm: number, type: AircraftType | undefined): number {
  const cruiseHours = distanceKm * HOURS_PER_KM;
  const turnBufferHours = (type?.turnMinutes ?? 60) / 180;
  const rawBlockHours = cruiseHours + turnBufferHours;
  const protectedBlockHours = Math.max(1, rawBlockHours);
  return Math.round(protectedBlockHours * 60);
}

export function activeEventTitles(events: GlobalEvent[]): string[] {
  return events.filter((event) => event.status === "active").map((event) => event.title);
}

function assignedAircraftForInput(save: SaveGame, assignment: DispatchAssignment | undefined): Aircraft | undefined {
  if (!assignment?.aircraftId) {
    return undefined;
  }
  return save.fleet.find((aircraft) => aircraft.id === assignment.aircraftId);
}

function initialMovementKind(sourceType: FlightInstance["sourceType"]): FlightMovement["kind"] {
  if (sourceType === "recovery") {
    return "positioning";
  }
  return "planned";
}

function flightRotationId(sourceType: FlightInstance["sourceType"], sourceId: string): string | undefined {
  if (sourceType === "rotation") {
    return sourceId;
  }
  return undefined;
}

function flightRotationBinding(sourceType: FlightInstance["sourceType"]): FlightInstance["rotationBinding"] {
  if (sourceType === "rotation") {
    return "auto_bound";
  }
  return "manually_unbound";
}

function createFlightInstance(input: {
  save: SaveGame;
  sourceType: FlightInstance["sourceType"];
  sourceId: string;
  sourceTitle: string;
  plan: LegPlan;
  assignment: DispatchAssignment | undefined;
  aircraftAssignmentSource?: FlightInstance["aircraftAssignmentSource"];
  sequence: number;
  recovery?: RecoveryFlight;
}): FlightInstance {
  const flightId = `flt-${input.sourceType}-${input.sourceId}-${input.sequence}`;
  const assignedAircraft = assignedAircraftForInput(input.save, input.assignment);
  const initialMovement: FlightMovement = {
    id: `${flightId}-mv-001`,
    sequence: 1,
    kind: initialMovementKind(input.sourceType),
    origin: input.plan.origin.code,
    destination: input.plan.destination.code,
    plannedDepartureMinute: input.plan.plannedDepartureMinute,
    plannedArrivalMinute: input.plan.plannedArrivalMinute,
    estimatedDepartureMinute: input.plan.plannedDepartureMinute,
    estimatedArrivalMinute: input.plan.plannedArrivalMinute,
    status: "planned",
    groundOps: groundOpsPlanForMovement(
      {
        aircraftTypeId: assignedAircraft?.typeId ?? input.plan.aircraftTypeId ?? "a320",
        plannedDepartureMinute: input.plan.plannedDepartureMinute,
        estimatedDepartureMinute: input.plan.plannedDepartureMinute
      },
      {
        plannedDepartureMinute: input.plan.plannedDepartureMinute,
        estimatedDepartureMinute: input.plan.plannedDepartureMinute
      }
    )
  };
  return {
    id: flightId,
    sourceType: input.sourceType,
    sourceId: input.sourceId,
	    sourceTitle: input.sourceTitle,
	    occurrenceId: input.plan.occurrenceId,
	    scheduledFlightId: input.plan.scheduledFlightId,
	    recoveryId: input.recovery?.id,
    recoveryForFlightInstanceId: input.recovery?.recoveryForFlightInstanceId,
    passengerTransfers: input.recovery?.passengerTransfers?.map((transfer) => ({ ...transfer })),
    rotationId: flightRotationId(input.sourceType, input.sourceId),
    rotationBinding: flightRotationBinding(input.sourceType),
    aircraftAssignmentSource: input.aircraftAssignmentSource ?? assignmentModeForFlight(input.assignment),
    sequence: input.sequence,
    flightNo: input.plan.template.flightNo,
    aircraftTypeId: assignedAircraft?.typeId ?? input.plan.aircraftTypeId ?? "a320",
    aircraftId: input.assignment?.aircraftId,
    fuelPolicy: input.assignment?.fuelPolicy ?? "minimum_cost",
    payloadAdjustment: undefined,
    fuelOrder: undefined,
    releaseDraft: undefined,
    appliedOccurrenceEventIds: [],
    movements: [initialMovement],
    currentMovementId: initialMovement.id,
    delayEvents: [],
    delaySummary: emptyDelaySummary(),
    plannedOrigin: input.plan.origin.code,
    plannedDestination: input.plan.destination.code,
    origin: input.plan.origin.code,
    destination: input.plan.destination.code,
    plannedDepartureMinute: input.plan.plannedDepartureMinute,
    plannedArrivalMinute: input.plan.plannedArrivalMinute,
    estimatedDepartureMinute: input.plan.plannedDepartureMinute,
    estimatedArrivalMinute: input.plan.plannedArrivalMinute,
    distanceKm: input.plan.distanceKm,
    plannedBlockMinutes: input.plan.blockMinutes,
    minimumTurnMinutes: input.plan.template.minimumTurnMinutes,
    status: "awaiting_release",
    delayMinutes: 0,
    currentAirport: input.plan.origin.code,
    disruptionDecisions: [],
    passengers: 0,
    revenueUsd: 0,
    costUsd: 0
  };
}

function emptyDelaySummary() {
  return {
    departureDelayMinutes: 0,
    arrivalDelayMinutes: 0,
    propagatedDelayMinutes: 0,
    controllableDelayMinutes: 0,
    a15Exceeded: false
  };
}

function snapshotAircraftState(aircraft: Aircraft): AircraftStateSnapshot {
  return {
    aircraftId: aircraft.id,
    registration: aircraft.registration,
    typeId: aircraft.typeId,
    location: aircraft.location,
    fuelOnBoardKg: aircraftFuelOnBoardKg(aircraft),
    status: aircraft.status,
    hoursSinceMaintenance: aircraft.hoursSinceMaintenance,
    cyclesSinceCheck: aircraft.cyclesSinceCheck
  };
}

function aircraftFuelOnBoardKg(aircraft: Aircraft): number {
  if (typeof aircraft.fuelOnBoardKg === "number" && Number.isFinite(aircraft.fuelOnBoardKg)) {
    return normalizedAircraftFuelOnBoardKg(aircraft.fuelOnBoardKg);
  }
  const type = aircraftTypeById(aircraft.typeId);
  return Math.round((type?.maxFuelKg ?? 0) * 0.5);
}

function normalizedAircraftFuelOnBoardKg(fuelOnBoardKg: number): number {
  const roundedFuelOnBoardKg = Math.round(fuelOnBoardKg);
  return Math.max(0, roundedFuelOnBoardKg);
}

export function buildFlightInstanceLedgerPreview(
  save: SaveGame,
  flight: FlightInstance,
  events: GlobalEvent[] = [],
  overrides: FlightLedgerPreviewOverrides = {},
  flightConfig: FlightAirportConfigBundle
) {
  const previewFlight: FlightInstance = {
    ...flight,
    payloadAdjustment: overrides.payloadAdjustment ?? flight.payloadAdjustment,
    fuelOrder: overrides.fuelOrder ?? flight.fuelOrder
  };
  return buildFlightLedger(save, previewFlight, events, flightConfig);
}

export function buildFlightReleaseDraft(
  save: SaveGame,
  flight: FlightInstance,
  events: GlobalEvent[] = [],
  schedule: ScheduleSource = EMPTY_SCHEDULE,
  flightConfig: FlightAirportConfigBundle
) {
  const ops = save.activeOpsDay;
  const currentMinute = ops?.currentMinute ?? 0;
  const previousRevision = flight.releaseDraft?.revision ?? 0;
  return {
    id: `release-draft-${flight.id}`,
    generatedMinute: currentMinute,
    revision: previousRevision + 1,
    selectedAircraftId: flight.aircraftId,
    aircraftCandidates: releaseAircraftCandidatesForOps(save, ops, flight),
    blockers: releaseBlocks(save, flight, events, schedule, currentMinute, flightConfig)
  };
}

type ActionWindowFlight = Pick<FlightInstance, "estimatedDepartureMinute"> & Partial<Pick<FlightInstance, "id" | "flightNo" | "sourceId">>;

export function flightActionWindowOpenMinute(flight: Pick<FlightInstance, "estimatedDepartureMinute">): number {
  const openMinute = flight.estimatedDepartureMinute - FLIGHT_ACTION_WINDOW_MINUTES;
  return Math.max(0, openMinute);
}

export function groundServiceWindowOpenMinute(flight: ActionWindowFlight): number {
  const openMinute = flight.estimatedDepartureMinute - groundServiceStartOffsetMinutes(flight);
  return Math.max(0, openMinute);
}

function groundServiceStartOffsetMinutes(flight: ActionWindowFlight): number {
  const range = GROUND_SERVICE_START_OFFSET_MAX_MINUTES - GROUND_SERVICE_START_OFFSET_MINUTES;
  const seed = flight.id ?? flight.flightNo ?? flight.sourceId ?? String(flight.estimatedDepartureMinute);
  return GROUND_SERVICE_START_OFFSET_MINUTES + (deterministicHash(seed) % (range + 1));
}

function flightActionWindowBlock(flight: Pick<FlightInstance, "estimatedDepartureMinute">, minute: number, label: string): string | undefined {
  const openMinute = flightActionWindowOpenMinute(flight);
  if (minute < openMinute) {
    return `${label}窗口未开启：预计 ${dayMinuteToLabel(openMinute)} 后可${label}。`;
  }
  return undefined;
}

function groundServiceWindowBlock(flight: ActionWindowFlight, minute: number): string | undefined {
  const openMinute = groundServiceWindowOpenMinute(flight);
  if (minute < openMinute) {
    return `地面保障窗口未开启：预计 ${dayMinuteToLabel(openMinute)} 后可地面保障。`;
  }
  return undefined;
}

function assertFlightActionWindowOpen(flight: FlightInstance, minute: number, label: string) {
  const block = flightActionWindowBlock(flight, minute, label);
  if (block) {
    throw new Error(block);
  }
}

function assertGroundServiceWindowOpen(flight: FlightInstance, minute: number) {
  const block = groundServiceWindowBlock(flight, minute);
  if (block) {
    throw new Error(block);
  }
}

function releaseAircraftCandidatesForOps(save: SaveGame, ops: ActiveOpsDay | undefined, flight: FlightInstance): AircraftReleaseCandidate[] {
  if (!ops) {
    return [];
  }
  return buildAircraftReleaseCandidates(save, ops, flight);
}

function buildAirportOfferForFlight(
  save: SaveGame,
  flight: FlightInstance,
  events: GlobalEvent[],
  flightConfig: FlightAirportConfigBundle,
  minute = save.activeOpsDay?.currentMinute ?? 0
): AirportDepartureOffer | undefined {
  const type = aircraftTypeForAirportOffer(save, flight);
  if (!type) {
    return undefined;
  }
  return buildAirportDepartureOffer({
    origin: requireAirport(flight.origin),
    destination: requireAirport(flight.destination),
    aircraftType: type,
    distanceKm: flight.distanceKm,
    day: flightOperationalDay(save, flight),
    minute,
    events,
    originOps: flightConfig.origin.ops,
    destinationOps: flightConfig.destination.ops,
    originPayload: flightConfig.origin.payload,
    originFuel: flightConfig.origin.fuel,
    destinationFuel: flightConfig.destination.fuel,
    routeMarket: flightConfig.routeMarket
  });
}

function aircraftTypeForAirportOffer(save: SaveGame, flight: FlightInstance): AircraftType | undefined {
  if (flight.aircraftId) {
    const aircraft = save.fleet.find((item) => item.id === flight.aircraftId);
    return aircraftTypeById(aircraft?.typeId ?? flight.aircraftTypeId);
  }
  return aircraftTypeById(flight.aircraftTypeId);
}

function flightAircraft(save: SaveGame, flight: FlightInstance): Aircraft | undefined {
  if (!flight.aircraftId) {
    return undefined;
  }
  return save.fleet.find((item) => item.id === flight.aircraftId);
}

function ledgerAircraftType(flight: FlightInstance, aircraft: Aircraft | undefined): AircraftType | undefined {
  if (aircraft) {
    return aircraftTypeById(aircraft.typeId);
  }
  return aircraftTypeById(flight.aircraftTypeId);
}

function recoveryTransferredPassengerCount(flight: FlightInstance): number {
  if (flight.sourceType !== "recovery" || flight.flightNo.startsWith("POS")) {
    return 0;
  }
  return flight.passengerTransfers?.reduce((sum, transfer) => sum + transfer.passengers, 0) ?? 0;
}

function airportOfferWithRecoveryTransfers(airportOffer: AirportDepartureOffer, transferredPassengers: number): AirportDepartureOffer {
  if (transferredPassengers <= 0) {
    return airportOffer;
  }
  const passengerBaggageKg = Math.round(transferredPassengers * 18);
  return {
    ...airportOffer,
    payloadOffer: {
      ...airportOffer.payloadOffer,
      passengers: transferredPassengers,
      baggageKg: passengerBaggageKg,
      cargoKg: 0,
      reasons: [...airportOffer.payloadOffer.reasons, `恢复转运 ${transferredPassengers} pax`]
    }
  };
}

function ledgerFuelOnBoardBeforeReleaseKg(
  save: SaveGame,
  flight: FlightInstance,
  aircraft: Aircraft,
  plannedFuelOrder: FlightFuelOrder | undefined
): number {
  const projectedFuelOnBoardKg = projectedAircraftFuelOnBoardKg(save, flight, aircraft);
  const rawFuelOnBoardKg = plannedFuelOrder?.baseFuelOnBoardKg ?? projectedFuelOnBoardKg;
  const roundedFuelOnBoardKg = Math.round(rawFuelOnBoardKg);
  return Math.max(0, roundedFuelOnBoardKg);
}

function buildFlightLedger(save: SaveGame, flight: FlightInstance, events: GlobalEvent[], flightConfig: FlightAirportConfigBundle, airportOfferOverride?: AirportDepartureOffer): FlightLedgerResult | undefined {
  const aircraft = flightAircraft(save, flight);
  const type = ledgerAircraftType(flight, aircraft);
  if (!aircraft || !type) {
    return undefined;
  }
  const origin = requireAirport(flight.origin);
  const destination = requireAirport(flight.destination);
  const airportOffer = airportOfferOverride ?? buildAirportOfferForFlight(save, flight, events, flightConfig);
  if (!airportOffer) {
    return undefined;
  }
  const transferredPassengers = recoveryTransferredPassengerCount(flight);
  const effectiveAirportOffer = airportOfferWithRecoveryTransfers(airportOffer, transferredPassengers);
  const plannedPayloadAdjustment = plannedPayloadAdjustmentForRelease(flight);
  const plannedFuelOrder = plannedFuelOrderForRelease(flight);
  const fuelOnBoardKg = ledgerFuelOnBoardBeforeReleaseKg(save, flight, aircraft, plannedFuelOrder);
  return {
    ...buildOperationalLedger({
      origin,
      destination,
      type,
      distanceKm: flight.distanceKm,
      fuelOnBoardKg,
      aircraftMaxTakeoffWeightKg: aircraft.maxTakeoffWeightKg,
      fuelPolicy: flight.fuelPolicy,
      airportOffer: effectiveAirportOffer,
      passengerReduction: plannedPayloadAdjustment?.passengerReduction,
      cargoReductionKg: plannedPayloadAdjustment?.cargoReductionKg,
      requestedUpliftKg: plannedFuelOrder?.upliftKg,
      expectedHoldingMinutes: totalAirspaceDelayMinutes(origin, destination, events)
    }),
    airportOffer: effectiveAirportOffer
  };
}

function buildAircraftReleaseCandidates(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance): AircraftReleaseCandidate[] {
  return save.fleet
    .map((aircraft) => aircraftReleaseCandidate(save, ops, flight, aircraft))
    .sort((a, b) => aircraftReleaseCandidateRank(b) - aircraftReleaseCandidateRank(a) || a.readyAtMinute - b.readyAtMinute || a.registration.localeCompare(b.registration));
}

function aircraftCapabilityBlocks(aircraft: Aircraft, type: AircraftType | undefined, flight: FlightInstance): string[] {
  if (!type) {
    return ["机型数据缺失。"];
  }
  return validateAircraftCapabilities({
    aircraft,
    type,
    origin: requireAirport(flight.origin),
    destination: requireAirport(flight.destination),
    distanceKm: flight.distanceKm
  });
}

function priorAircraftReadyMinute(prior: FlightInstance | undefined): number | undefined {
  if (!prior) {
    return undefined;
  }
  return prior.estimatedArrivalMinute + minimumTurnForFlight(prior);
}

function aircraftReleaseCandidate(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, aircraft: Aircraft): AircraftReleaseCandidate {
  const operationDay = flightOperationalDay(save, flight);
  const type = aircraftTypeById(aircraft.typeId);
  const capabilityBlocks = aircraftCapabilityBlocks(aircraft, type, flight);
  if (aircraft.status === "maintenance" || aircraft.status === "grounded" || aircraft.availableFromDay > operationDay) {
    capabilityBlocks.push(`${aircraft.registration} 当前不可用。`);
  }
  const overlapping = overlappingAircraftFlight(ops, flight, aircraft.id);
  const prior = latestPriorCandidateFlight(ops, flight, aircraft.id);
  const priorReadyAt = priorAircraftReadyMinute(prior);
  const projectedFuel = projectedAircraftFuelOnBoardKg(save, flight, aircraft);

  if (capabilityBlocks.length > 0) {
    return candidate(aircraft, "unavailable", Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, projectedFuel, capabilityBlocks[0], prior?.id, overlapping?.id);
  }
  if (overlapping) {
    return candidate(
      aircraft,
      "unavailable",
      overlapping.estimatedArrivalMinute,
      overlapping.estimatedArrivalMinute + minimumTurnForFlight(overlapping),
      projectedFuel,
      `${aircraft.registration} 已被 ${overlapping.flightNo} 占用至 ${dayMinuteToLabel(overlapping.estimatedArrivalMinute + minimumTurnForFlight(overlapping))}。`,
      prior?.id,
      overlapping.id
    );
  }
  if (aircraft.location === flight.origin) {
    return candidate(aircraft, "available_now", ops.currentMinute, ops.currentMinute, projectedFuel, `${aircraft.registration} 已在 ${flight.origin}。`);
  }
  if (prior && prior.destination === flight.origin && priorReadyAt !== undefined && priorReadyAt <= flight.estimatedDepartureMinute) {
    return candidate(
      aircraft,
      "arrives_before_departure",
      prior.estimatedArrivalMinute,
      priorReadyAt,
      projectedFuel,
      `${aircraft.registration} 将执行 ${prior.flightNo} 到达 ${flight.origin}，预计 ${dayMinuteToLabel(priorReadyAt)} 可用。`,
      prior.id
    );
  }
  if (prior && prior.destination === flight.origin && priorReadyAt !== undefined) {
    return candidate(
      aircraft,
      "unavailable",
      prior.estimatedArrivalMinute,
      priorReadyAt,
      projectedFuel,
      `${aircraft.registration} 预计 ${dayMinuteToLabel(priorReadyAt)} 才可用，晚于本班计划起飞。`,
      prior.id
    );
  }
  return candidate(aircraft, "unavailable", Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, projectedFuel, `飞机位置不符：${aircraft.registration} 当前在 ${aircraft.location}，不会在起飞前到达 ${flight.origin}。`);
}

function candidate(
  aircraft: Aircraft,
  availability: AircraftReleaseCandidate["availability"],
  availableAtMinute: number,
  readyAtMinute: number,
  projectedFuelOnBoardKg: number,
  reason: string,
  sourceFlightInstanceId?: string,
  conflictFlightInstanceId?: string
): AircraftReleaseCandidate {
  return {
    aircraftId: aircraft.id,
    registration: aircraft.registration,
    typeId: aircraft.typeId,
    location: aircraft.location,
    availability,
    availableAtMinute,
    readyAtMinute,
    projectedFuelOnBoardKg,
    sourceFlightInstanceId,
    conflictFlightInstanceId,
    reason
  };
}

function aircraftReleaseCandidateRank(candidate: AircraftReleaseCandidate): number {
  if (candidate.availability === "available_now") return 300_000 - candidate.readyAtMinute;
  if (candidate.availability === "arrives_before_departure") return 200_000 - candidate.readyAtMinute;
  return -candidate.readyAtMinute;
}

function latestPriorCandidateFlight(ops: ActiveOpsDay, flight: FlightInstance, aircraftId: string): FlightInstance | undefined {
  return ops.flights
    .filter(
      (item) =>
        item.id !== flight.id &&
        item.aircraftId === aircraftId &&
        !isTerminalFlightStatus(item.status) &&
        item.status !== "cancelled" &&
        item.status !== "needs_recovery" &&
        item.estimatedArrivalMinute <= flight.estimatedDepartureMinute
    )
    .sort((a, b) => b.estimatedArrivalMinute - a.estimatedArrivalMinute)[0];
}

function overlappingAircraftFlight(ops: ActiveOpsDay, flight: FlightInstance, aircraftId: string): FlightInstance | undefined {
  const start = flight.estimatedDepartureMinute;
  const end = flight.estimatedArrivalMinute + minimumTurnForFlight(flight);
  return ops.flights.find((item) => {
    if (item.id === flight.id || item.aircraftId !== aircraftId || isTerminalFlightStatus(item.status) || item.status === "cancelled" || item.status === "needs_recovery") {
      return false;
    }
    const otherStart = item.estimatedDepartureMinute;
    const otherEnd = item.estimatedArrivalMinute + minimumTurnForFlight(item);
    if (otherEnd <= start) {
      return false;
    }
    if (otherStart >= end) {
      return false;
    }
    return true;
  });
}

function selectedAircraftCandidate(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance): AircraftReleaseCandidate | undefined {
  if (!flight.aircraftId) {
    return undefined;
  }
  return buildAircraftReleaseCandidates(save, ops, flight).find((item) => item.aircraftId === flight.aircraftId);
}

function priorCandidateForActiveOps(ops: ActiveOpsDay | undefined, flight: FlightInstance, aircraftId: string): FlightInstance | undefined {
  if (!ops) {
    return undefined;
  }
  return latestPriorCandidateFlight(ops, flight, aircraftId);
}

function projectedAircraftFuelOnBoardKg(save: SaveGame, flight: FlightInstance, aircraft: Aircraft): number {
  const ops = save.activeOpsDay;
  const prior = priorCandidateForActiveOps(ops, flight, aircraft.id);
  if (prior && prior.destination === flight.origin) {
    if (prior.fuelPlan) {
      return prior.fuelPlan.plannedLandingFuelKg;
    }
  }
  return aircraftFuelOnBoardKg(aircraft);
}

function projectedFuelForAircraft(save: SaveGame, flight: FlightInstance, aircraft: Aircraft | undefined): number {
  if (!aircraft) {
    return 0;
  }
  return projectedAircraftFuelOnBoardKg(save, flight, aircraft);
}

function releaseAircraftLock(save: SaveGame, flight: FlightInstance) {
  const aircraft = flightAircraft(save, flight);
  if (!aircraft || aircraft.activeFlightInstanceId !== flight.id) {
    return;
  }
  aircraft.activeFlightInstanceId = undefined;
  if (aircraft.status === "in_flight" && flight.status !== "airborne" && flight.status !== "diverted") {
    aircraft.status = "available";
  }
}

function clearFlightLedger(flight: FlightInstance) {
  flight.aircraftSnapshotBefore = undefined;
  flight.aircraftSnapshotAfter = undefined;
  flight.airportServiceRecordId = undefined;
  flight.airportOfferSnapshot = undefined;
  flight.payloadPlan = undefined;
  flight.fuelPlan = undefined;
  flight.weightPlan = undefined;
  flight.costBreakdown = undefined;
  flight.revenueBreakdown = undefined;
  flight.passengers = 0;
  flight.revenueUsd = 0;
  flight.costUsd = 0;
}

function groundOpsFlowForCurrentMovement(flight: FlightInstance): GroundOpsFlowSnapshot {
  const movement = currentMovement(flight);
  movement.groundOps = movement.groundOps ?? groundOpsPlanForMovement(flight, movement);
  return movement.groundOps;
}

function activeGroundOpsNodes(flight: FlightInstance): GroundOpsNodeState[] {
  return currentGroundOpsNodesForFlight(flight).filter((node) => node.status === "planned" || node.status === "running" || node.status === "blocked");
}

function activeGroundOpsNodesInFlow(flow: GroundOpsFlowSnapshot): GroundOpsNodeState[] {
  return flow.nodes.filter((node) => node.status === "planned" || node.status === "running" || node.status === "blocked");
}

function allGroundOpsNodeKinds(): GroundOpsNodeKind[] {
  return [
    "fuel_uplift",
    "fuel_defuel",
    "payload_offload",
    "cabin_cleaning",
    "water_service",
    "catering",
    "passenger_boarding",
    "baggage_loading",
    "cargo_loading",
    "loadsheet",
    "doors_closed",
    "chocks_off",
    "pushback",
    "taxi_out"
  ];
}

function plannedPayloadAdjustmentForRelease(flight: FlightInstance): FlightPayloadAdjustment | undefined {
  const node = [...activeGroundOpsNodes(flight)].reverse().find((item) => item.kind === "payload_offload");
  if (!node) {
    return flight.payloadAdjustment;
  }
  return {
    passengerReduction: plannedPassengerReductionFromNode(node, flight.payloadAdjustment),
    cargoReductionKg: plannedCargoReductionFromNode(node, flight.payloadAdjustment),
    reason: node.reason || flight.payloadAdjustment?.reason
  };
}

function plannedFuelOrderForRelease(flight: FlightInstance): FlightFuelOrder | undefined {
  const node = [...activeGroundOpsNodes(flight)].reverse().find((item) => item.kind === "fuel_uplift" || item.kind === "fuel_defuel");
  if (!node) {
    return flight.fuelOrder;
  }
  return {
    upliftKg: plannedFuelUpliftFromNode(node, flight.fuelOrder),
    baseFuelOnBoardKg: plannedBaseFuelFromNode(node, flight.fuelOrder),
    reason: node.reason || flight.fuelOrder?.reason
  };
}

function plannedPassengerReductionFromNode(node: GroundOpsNodeState, fallback: FlightPayloadAdjustment | undefined): number {
  const rawPassengerReduction = node.requestedPassengerReduction ?? fallback?.passengerReduction ?? 0;
  return normalizedPassengerReductionCount(rawPassengerReduction);
}

function plannedCargoReductionFromNode(node: GroundOpsNodeState, fallback: FlightPayloadAdjustment | undefined): number {
  const rawCargoReductionKg = node.requestedCargoReductionKg ?? fallback?.cargoReductionKg ?? 0;
  return normalizedCargoReductionWeightKg(rawCargoReductionKg);
}

function plannedFuelUpliftFromNode(node: GroundOpsNodeState, fallback: FlightFuelOrder | undefined): number {
  const rawUpliftKg = node.targetFuelUpliftKg ?? fallback?.upliftKg ?? 0;
  return normalizedFuelUpliftWeightKg(rawUpliftKg);
}

function plannedBaseFuelFromNode(node: GroundOpsNodeState, fallback: FlightFuelOrder | undefined): number {
  const rawBaseFuelOnBoardKg = node.baseFuelOnBoardKg ?? fallback?.baseFuelOnBoardKg ?? 0;
  return normalizedFuelBaseWeightKg(rawBaseFuelOnBoardKg);
}

function normalizedPassengerReductionCount(passengerReduction: number): number {
  const roundedPassengerReduction = Math.round(passengerReduction);
  return Math.max(0, roundedPassengerReduction);
}

function normalizedCargoReductionWeightKg(cargoReductionKg: number): number {
  const roundedCargoReductionKg = Math.round(cargoReductionKg);
  return Math.max(0, roundedCargoReductionKg);
}

function normalizedFuelUpliftWeightKg(upliftKg: number): number {
  const roundedUpliftKg = Math.round(upliftKg);
  return Math.max(0, roundedUpliftKg);
}

function normalizedFuelBaseWeightKg(baseFuelOnBoardKg: number): number {
  const roundedBaseFuelOnBoardKg = Math.round(baseFuelOnBoardKg);
  return Math.max(0, roundedBaseFuelOnBoardKg);
}

function baseFuelOnBoardForFuelOrder(save: SaveGame, flight: FlightInstance, aircraft: Aircraft | undefined): number {
  const projectedFuelOnBoardKg = projectedFuelForAircraft(save, flight, aircraft);
  const rawBaseFuelOnBoardKg = flight.fuelOrder?.baseFuelOnBoardKg ?? flight.fuelPlan?.fuelOnBoardBeforeKg ?? projectedFuelOnBoardKg;
  return normalizedFuelBaseWeightKg(rawBaseFuelOnBoardKg);
}

function payloadOffloadNodeMatchesTarget(existingNode: GroundOpsNodeState | undefined, targetPassengerReduction: number, targetCargoReductionKg: number): boolean {
  if (!existingNode) {
    return false;
  }
  const existingPassengerReduction = normalizedPassengerReductionCount(existingNode.requestedPassengerReduction ?? 0);
  const existingCargoReductionKg = normalizedCargoReductionWeightKg(existingNode.requestedCargoReductionKg ?? 0);
  return existingPassengerReduction === targetPassengerReduction && existingCargoReductionKg === targetCargoReductionKg;
}

function payloadReductionDelta(targetReduction: number, currentReduction: number): number {
  const remainingReduction = targetReduction - currentReduction;
  return Math.max(0, remainingReduction);
}

function currentFuelUpliftForService(existingNode: GroundOpsNodeState | undefined, flight: FlightInstance): number {
  const rawCurrentUpliftKg = existingNode?.targetFuelUpliftKg ?? flight.fuelOrder?.upliftKg ?? flight.fuelPlan?.upliftKg ?? 0;
  return normalizedFuelUpliftWeightKg(rawCurrentUpliftKg);
}

function updatePlannedPayloadAdjustment(ops: ActiveOpsDay, flight: FlightInstance, passengerReduction: number, cargoReductionKg: number, reason?: string) {
  const normalizedPassengerReduction = normalizedPassengerReductionCount(passengerReduction);
  const normalizedCargoReductionKg = normalizedCargoReductionWeightKg(cargoReductionKg);
  flight.payloadAdjustment = {
    passengerReduction: normalizedPassengerReduction,
    cargoReductionKg: normalizedCargoReductionKg,
    reason
  };
  clearFlightLedger(flight);
  markFlightNeedsRelease(flight);
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 业载计划已更新：减载 ${flight.payloadAdjustment.passengerReduction} pax / ${flight.payloadAdjustment.cargoReductionKg}kg。`, flight.payloadAdjustment);
}

function updatePlannedFuelOrder(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, targetUpliftKg: number, reason: string | undefined) {
  const aircraft = flightAircraft(save, flight);
  const baseFuelOnBoardKg = baseFuelOnBoardForFuelOrder(save, flight, aircraft);
  flight.fuelOrder = {
    upliftKg: normalizedFuelUpliftWeightKg(targetUpliftKg),
    baseFuelOnBoardKg,
    reason
  };
  clearFlightLedger(flight);
  markFlightNeedsRelease(flight);
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 加油计划已更新：本站加油 ${flight.fuelOrder.upliftKg}kg。`, flight.fuelOrder);
}

function cancelPendingGroundOpsNodes(flight: FlightInstance, minute: number, kinds: GroundOpsNodeKind[], scope: "current" | "all" = "current") {
  const kindSet = new Set(kinds);
  const movements = groundOpsCancellationMovements(flight, scope);
  for (const movement of movements) {
    for (const node of movement.groundOps?.nodes ?? []) {
      if ((node.status === "planned" || node.status === "running" || node.status === "blocked") && kindSet.has(node.kind)) {
        node.status = "cancelled";
        node.completedMinute = minute;
      }
    }
  }
}

function groundOpsCancellationMovements(flight: FlightInstance, scope: "current" | "all"): FlightMovement[] {
  if (scope === "all") {
    return flight.movements ?? [];
  }
  return [currentMovement(flight)];
}

function enqueuePayloadOffloadNode(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, passengerReduction: number, cargoReductionKg: number, groundOpsConfig: GroundOpsAirportConfigBundle, reason?: string) {
  const current: FlightPayloadAdjustment = flight.payloadAdjustment ?? { passengerReduction: 0, cargoReductionKg: 0 };
  const targetPassengerReduction = normalizedPassengerReductionCount(passengerReduction);
  const targetCargoReductionKg = normalizedCargoReductionWeightKg(cargoReductionKg);
  if (targetPassengerReduction < current.passengerReduction || targetCargoReductionKg < current.cargoReductionKg) {
    throw new Error("已完成减载不能在本版直接恢复。");
  }
  const existingNode = [...activeGroundOpsNodes(flight)].reverse().find((node) => node.kind === "payload_offload");
  if (payloadOffloadNodeMatchesTarget(existingNode, targetPassengerReduction, targetCargoReductionKg)) {
    return;
  }

  cancelPendingGroundOpsNodes(flight, ops.currentMinute, ["payload_offload"]);
  const profile = groundOpsNodeProfile(groundOpsConfig);
  const passengerDelta = payloadReductionDelta(targetPassengerReduction, current.passengerReduction);
  const cargoDeltaKg = payloadReductionDelta(targetCargoReductionKg, current.cargoReductionKg);
  if (passengerDelta === 0 && cargoDeltaKg === 0) {
    flight.payloadAdjustment = {
      passengerReduction: targetPassengerReduction,
      cargoReductionKg: targetCargoReductionKg,
      reason: reason || current.reason || "玩家减载"
    };
    clearFlightLedger(flight);
    markFlightNeedsRelease(flight);
    return;
  }

  const node: GroundOpsNodeState = {
    id: nextGroundOpsNodeId(flight),
    movementId: currentMovement(flight).id,
    kind: "payload_offload",
    status: "planned",
    createdMinute: ops.currentMinute,
    plannedStartMinute: ops.currentMinute,
    seed: groundOpsNodeSeed(flight, "payload_offload"),
    requestedPassengerReduction: targetPassengerReduction,
    appliedPassengerReduction: current.passengerReduction,
    requestedCargoReductionKg: targetCargoReductionKg,
    appliedCargoReductionKg: current.cargoReductionKg,
    params: {
      groundHandlingLevel: profile.groundHandlingLevel,
      paxProcessingCapacity: profile.paxProcessingCapacity,
      cargoProcessingCapacityKg: profile.cargoProcessingCapacityKg,
      passengerRatePerWave: profile.passengerRatePerWave,
      cargoRateKgPerWave: profile.cargoRateKgPerWave,
      baggageRateKgPerWave: profile.baggageRateKgPerWave
    },
    delayMinutes: 0,
    reason: reason || "玩家减载"
  };
  addGroundOpsNode(save, ops, flight, node);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 新增运行节点：${groundOpsNodeSummary(node)}，预计 ${dayMinuteToLabel(node.readyAtMinute ?? ops.currentMinute)} 完成。`, node);
}

function enqueueFuelServiceNode(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, targetUpliftKg: number, reason: string | undefined, groundOpsConfig: GroundOpsAirportConfigBundle) {
  const aircraft = flightAircraft(save, flight);
  const baseFuelOnBoardKg = baseFuelOnBoardForFuelOrder(save, flight, aircraft);
  const existingNode = [...activeGroundOpsNodes(flight)].reverse().find((node) => node.kind === "fuel_uplift" || node.kind === "fuel_defuel");
  const currentUpliftKg = currentFuelUpliftForService(existingNode, flight);
  const normalizedTargetUpliftKg = normalizedFuelUpliftWeightKg(targetUpliftKg);
  const targetReleaseFuelKg = baseFuelOnBoardKg + normalizedTargetUpliftKg;
  if (normalizedTargetUpliftKg === currentUpliftKg) {
    if (existingNode) {
      return;
    }
    flight.fuelOrder = {
      upliftKg: normalizedTargetUpliftKg,
      baseFuelOnBoardKg,
      reason: reason || flight.fuelOrder?.reason || "玩家加油指令"
    };
    clearFlightLedger(flight);
    markFlightNeedsRelease(flight);
    return;
  }

  cancelPendingGroundOpsNodes(flight, ops.currentMinute, ["fuel_uplift", "fuel_defuel"]);
  const currentFuelOnBoardKg = currentFuelOnBoardForService(aircraft, baseFuelOnBoardKg, currentUpliftKg);
  const fuelDeltaKg = targetReleaseFuelKg - currentFuelOnBoardKg;
  if (fuelDeltaKg === 0) {
    flight.fuelOrder = {
      upliftKg: normalizedTargetUpliftKg,
      baseFuelOnBoardKg,
      reason: reason || flight.fuelOrder?.reason || "玩家加油指令"
    };
    clearFlightLedger(flight);
    markFlightNeedsRelease(flight);
    return;
  }
  const kind = fuelServiceNodeKind(fuelDeltaKg);
  const profile = groundOpsNodeProfile(groundOpsConfig);
  const node: GroundOpsNodeState = {
    id: nextGroundOpsNodeId(flight),
    movementId: currentMovement(flight).id,
    kind,
    status: "planned",
    createdMinute: ops.currentMinute,
    plannedStartMinute: ops.currentMinute,
    seed: groundOpsNodeSeed(flight, kind),
    requestedFuelDeltaKg: fuelDeltaKg,
    appliedFuelDeltaKg: 0,
    targetFuelUpliftKg: normalizedTargetUpliftKg,
    baseFuelOnBoardKg,
    targetReleaseFuelKg,
    params: {
      groundHandlingLevel: profile.groundHandlingLevel,
      refuelRateKgPerMin: profile.refuelRateKgPerMin
    },
    delayMinutes: 0,
    reason: reason || fuelServiceNodeReason(kind)
  };
  addGroundOpsNode(save, ops, flight, node);
  appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "player", `${flight.flightNo} 新增运行节点：${groundOpsNodeSummary(node)}，预计 ${dayMinuteToLabel(node.readyAtMinute ?? ops.currentMinute)} 完成。`, node);
}

function currentFuelOnBoardForService(aircraft: Aircraft | undefined, baseFuelOnBoardKg: number, currentUpliftKg: number): number {
  if (aircraft) {
    return aircraftFuelOnBoardKg(aircraft);
  }
  return baseFuelOnBoardKg + currentUpliftKg;
}

function groundServiceTaxiOutMinutes(taxiOutMinutes: number | undefined): number {
  const rawTaxiOutMinutes = taxiOutMinutes ?? 18;
  const roundedTaxiOutMinutes = Math.round(rawTaxiOutMinutes);
  return Math.max(8, roundedTaxiOutMinutes);
}

function fuelServiceNodeKind(fuelDeltaKg: number): GroundOpsNodeKind {
  if (fuelDeltaKg >= 0) {
    return "fuel_uplift";
  }
  return "fuel_defuel";
}

function fuelServiceNodeReason(kind: GroundOpsNodeKind): string {
  if (kind === "fuel_defuel") {
    return "玩家抽油指令";
  }
  return "玩家加油指令";
}

function aircraftPositionBlock(aircraft: Aircraft | undefined, origin: string): string {
  if (aircraft) {
    return `飞机位置不符：${aircraft.registration} 当前在 ${aircraft.location}，计划需要 ${origin}。`;
  }
  return "指派飞机不存在。";
}

function startGroundService(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, groundOpsConfig: GroundOpsAirportConfigBundle, reason: string) {
  assertGroundServiceWindowOpen(flight, ops.currentMinute);
  const hasConfirmedReleasePlan = flight.status === "released" || (flight.status === "delayed" && flight.releaseDraft?.status === "confirmed");
  if (!hasConfirmedReleasePlan) {
    throw new Error("请先确认放行计划，再开始地面保障。");
  }
  const movement = currentMovement(flight);
  const flow = groundOpsFlowForCurrentMovement(flight);
  if (flow.status === "in_progress" || flow.status === "completed") {
    throw new Error("本班地面保障已经开始。");
  }
  const aircraft = flightAircraft(save, flight);
  if (!aircraft || aircraft.location !== movement.origin) {
    throw new Error(aircraftPositionBlock(aircraft, movement.origin));
  }
  if (aircraft.activeFlightInstanceId && aircraft.activeFlightInstanceId !== flight.id) {
    throw new Error(`${aircraft.registration} 当前由 ${aircraft.activeFlightInstanceId} 锁定运行。`);
  }
  const ledger = groundServiceLedger(flight);
  if (!ledger) {
    throw new Error("请先确认放行计划，再开始地面保障。");
  }
  appendAirportServiceRecord(save, flight, ledger.airportOffer, "used", ops.currentMinute, []);
  const serviceNodes = buildGroundServiceNodes(ops, flight, movement, groundOpsConfig);
  if (serviceNodes.length === 0) {
    throw new Error("无法生成地面保障节点。");
  }
  flow.nodes = serviceNodes;
  flow.status = "in_progress";
  flow.startWindowMinute = groundServiceWindowOpenMinute(flight);
  flow.startedMinute = ops.currentMinute;
  flow.plannedTakeoffMinute = movement.plannedDepartureMinute;
  flow.taxiOutMinutes = aircraftTypeById(flight.aircraftTypeId)?.fuelProfile.taxiOutMinutes ?? flow.taxiOutMinutes;
  flow.requiredNodeCount = serviceNodes.length;
  flow.reason = reason;
  advanceGroundOpsNodes(save, ops, flight, ops.currentMinute);
  const readyAtMinute = projectGroundOpsReadyAt(save, ops, flight);
  const departureDelay = Math.max(0, readyAtMinute - flight.estimatedDepartureMinute);
  flow.readyAtMinute = readyAtMinute;
  flow.totalServiceMinutes = readyAtMinute - ops.currentMinute;
  flow.delayMinutes = departureDelay;
  if (departureDelay > 0) {
    flight.estimatedDepartureMinute += departureDelay;
    flight.estimatedArrivalMinute += departureDelay;
    flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
    flight.reason = `地面保障顺延 ${departureDelay} 分钟`;
    addDelayEvent(flight, "turnaround", departureDelay, true, flight.reason);
    appendOpsEvent(ops, flight.id, "delayed", ops.currentMinute, "system", `${flight.flightNo} 地面保障预计完成 ${dayMinuteToLabel(readyAtMinute)}，起飞顺延 ${departureDelay} 分钟。`);
  } else {
    flight.reason = reason;
  }
  aircraft.activeFlightInstanceId = flight.id;
  flight.status = "ground_service";
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "released", ops.currentMinute, "player", `${flight.flightNo} 开始地面保障：${serviceNodes.length} 个节点，目标起飞 ${dayMinuteToLabel(flight.estimatedDepartureMinute)}。`, flow);
}

function groundServiceLedger(flight: FlightInstance): Pick<FlightLedgerResult, "payloadPlan" | "fuelPlan" | "weightPlan" | "airportOffer"> | undefined {
  if (flight.payloadPlan && flight.fuelPlan && flight.weightPlan && flight.airportOfferSnapshot) {
    return {
      payloadPlan: flight.payloadPlan,
      fuelPlan: flight.fuelPlan,
      weightPlan: flight.weightPlan,
      airportOffer: flight.airportOfferSnapshot
    };
  }
  return undefined;
}

function groundServiceElapsedMinutes(flow: GroundOpsFlowSnapshot): number | undefined {
  if (flow.startedMinute === undefined || flow.readyAtMinute === undefined) {
    return undefined;
  }
  return flow.readyAtMinute - flow.startedMinute;
}

function buildGroundServiceNodes(ops: ActiveOpsDay, flight: FlightInstance, movement: FlightMovement, groundOpsConfig: GroundOpsAirportConfigBundle): GroundOpsNodeState[] {
  const type = aircraftTypeById(flight.aircraftTypeId);
  const payload = flight.payloadPlan;
  const fuel = flight.fuelPlan;
  const startMinute = ops.currentMinute;
  const profile = groundOpsNodeProfile(groundOpsConfig);
  const taxiOutMinutes = groundServiceTaxiOutMinutes(type?.fuelProfile.taxiOutMinutes);
  const passengerRate = profile.passengerRatePerWave;
  const cargoRate = profile.cargoRateKgPerWave;
  const baggageRate = profile.baggageRateKgPerWave;
  const passengerBoardingStartMinute = Math.max(startMinute, flight.estimatedDepartureMinute - PASSENGER_BOARDING_START_OFFSET_MINUTES);
  const cargoLoadingStartMinute = Math.max(startMinute, flight.estimatedDepartureMinute - CARGO_LOADING_START_OFFSET_MINUTES);
  const nodes: GroundOpsNodeState[] = [];
  const makeNode = (
    kind: GroundOpsNodeKind,
    plannedStartMinute: number | undefined,
    baseMinutes: number,
    extra: Partial<GroundOpsNodeState> = {},
    dependsOnNodeIds: string[] = []
  ): GroundOpsNodeState => {
    const node: GroundOpsNodeState = {
      id: `${movement.id}-node-${String(nodes.length + 1).padStart(3, "0")}`,
      movementId: movement.id,
      kind,
      status: "planned",
      sequence: nodes.length + 1,
      dependsOnNodeIds,
      createdMinute: startMinute,
      plannedStartMinute,
      seed: `${flight.id}:${movement.id}:${kind}:${nodes.length + 1}`,
      params: { baseMinutes, groundHandlingLevel: profile.groundHandlingLevel },
      delayMinutes: 0,
      reason: "地面保障",
      ...extra
    };
    nodes.push(node);
    return node;
  };

  const parallelNodes: GroundOpsNodeState[] = [];
  const widebodyServices = groundServiceWidebody(type);
  parallelNodes.push(makeNode("cabin_cleaning", startMinute, cabinCleaningMinutes(widebodyServices)));
  parallelNodes.push(makeNode("water_service", startMinute, waterServiceMinutes(widebodyServices)));
  parallelNodes.push(makeNode("catering", startMinute, cateringMinutes(widebodyServices)));
  parallelNodes.push(makeNode("passenger_boarding", passengerBoardingStartMinute, passengerBoardingMinutes(payload?.passengers ?? 0, passengerRate)));
  parallelNodes.push(makeNode("baggage_loading", cargoLoadingStartMinute, baggageLoadingMinutes(payload?.baggageKg ?? 0, baggageRate)));
  parallelNodes.push(makeNode("cargo_loading", cargoLoadingStartMinute, cargoLoadingMinutes(payload?.cargoKg ?? 0, cargoRate)));
  if ((fuel?.upliftKg ?? 0) > 0) {
    parallelNodes.push(makeNode("fuel_uplift", startMinute, 0, {
      requestedFuelDeltaKg: fuel!.upliftKg,
      appliedFuelDeltaKg: 0,
      targetFuelUpliftKg: fuel!.upliftKg,
      baseFuelOnBoardKg: fuel!.fuelOnBoardBeforeKg,
      targetReleaseFuelKg: fuel!.plannedTakeoffFuelKg,
      params: {
        groundHandlingLevel: profile.groundHandlingLevel,
        refuelRateKgPerMin: profile.refuelRateKgPerMin
      },
      reason: flight.fuelOrder?.reason || "地面保障加油"
    }));
  }

  const loadsheet = makeNode("loadsheet", undefined, LOADSHEET_MINUTES, {}, parallelNodes.map((node) => node.id));
  const departurePrepStart = Math.max(startMinute, flight.estimatedDepartureMinute - departurePrepMinutes(profile.groundHandlingLevel, taxiOutMinutes));
  const doors = makeNode("doors_closed", departurePrepStart, DOORS_CLOSE_MINUTES, {}, [loadsheet.id]);
  const chocks = makeNode("chocks_off", undefined, CHOCKS_OFF_MINUTES, {}, [doors.id]);
  const pushback = makeNode("pushback", undefined, PUSHBACK_MINUTES, {}, [chocks.id]);
  makeNode("taxi_out", undefined, taxiOutMinutes, {}, [pushback.id]);
  return nodes;
}

function groundServiceWidebody(type: AircraftType | undefined): boolean {
  return (type?.seatCapacity ?? 180) >= 220;
}

function cabinCleaningMinutes(widebody: boolean): number {
  if (widebody) {
    return 18;
  }
  return 12;
}

function waterServiceMinutes(widebody: boolean): number {
  if (widebody) {
    return 12;
  }
  return 8;
}

function cateringMinutes(widebody: boolean): number {
  if (widebody) {
    return 22;
  }
  return 15;
}

function passengerBoardingMinutes(passengers: number, passengerRate: number): number {
  return 5 + Math.ceil(passengers / passengerRate) * 3;
}

function baggageLoadingMinutes(baggageKg: number, baggageRate: number): number {
  return 5 + Math.ceil(baggageKg / baggageRate) * 3;
}

function cargoLoadingMinutes(cargoKg: number, cargoRate: number): number {
  return 5 + Math.ceil(cargoKg / cargoRate) * 3;
}

function departurePrepMinutes(groundHandlingLevel: number, taxiOutMinutes: number): number {
  return (
    serviceAdjustedMinutes(DOORS_CLOSE_MINUTES, groundHandlingLevel) +
    serviceAdjustedMinutes(CHOCKS_OFF_MINUTES, groundHandlingLevel) +
    serviceAdjustedMinutes(PUSHBACK_MINUTES, groundHandlingLevel) +
    serviceAdjustedMinutes(taxiOutMinutes, groundHandlingLevel)
  );
}

function serviceAdjustedMinutes(baseMinutes: number, groundHandlingLevel: number): number {
  const protectedGroundHandlingLevel = Math.max(0.25, groundHandlingLevel);
  return Math.max(1, Math.ceil(baseMinutes / protectedGroundHandlingLevel));
}

function groundOpsPlanForMovement(
  flight: Pick<FlightInstance, "aircraftTypeId" | "plannedDepartureMinute" | "estimatedDepartureMinute">,
  movement: Pick<FlightMovement, "plannedDepartureMinute" | "estimatedDepartureMinute">
): GroundOpsFlowSnapshot {
  return {
    status: "not_started",
    startWindowMinute: groundServiceWindowOpenMinute({ estimatedDepartureMinute: movement.estimatedDepartureMinute }),
    plannedTakeoffMinute: movement.plannedDepartureMinute,
    taxiOutMinutes: aircraftTypeById(flight.aircraftTypeId)?.fuelProfile.taxiOutMinutes ?? 18,
    delayMinutes: 0,
    requiredNodeCount: 0,
    nodes: []
  };
}

function addGroundOpsNode(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, node: GroundOpsNodeState) {
  const flow = groundOpsFlowForCurrentMovement(flight);
  flow.nodes = [...flow.nodes, node];
  clearFlightLedger(flight);
  if (flow.status === "completed") {
    flow.status = "in_progress";
    flow.completedMinute = undefined;
    flight.status = "ground_service";
  }
  if (flow.status !== "in_progress") {
    markFlightNeedsRelease(flight);
    return;
  }
  advanceGroundOpsNodes(save, ops, flight, ops.currentMinute);
  const readyAtMinute = projectGroundOpsReadyAt(save, ops, flight);
  flow.readyAtMinute = Math.max(flow.readyAtMinute ?? ops.currentMinute, readyAtMinute);
  flow.totalServiceMinutes = groundServiceElapsedMinutes(flow);
  const departureDelay = Math.max(0, readyAtMinute - flight.estimatedDepartureMinute);
  node.delayMinutes = departureDelay;
  if (departureDelay > 0) {
    flight.estimatedDepartureMinute += departureDelay;
    flight.estimatedArrivalMinute += departureDelay;
    flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
    flight.status = "ground_service";
    flight.reason = `${groundOpsNodeLabel(node.kind)}顺延 ${departureDelay} 分钟`;
    addDelayEvent(flight, "player_action", departureDelay, true, flight.reason);
    syncCurrentMovementFromFlight(flight);
    appendOpsEvent(ops, flight.id, "delayed", ops.currentMinute, "system", `${flight.flightNo} ${groundOpsNodeLabel(node.kind)}未完成，起飞顺延 ${departureDelay} 分钟。`, node);
  }
}

function advanceGroundOpsNodes(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, targetMinute: number) {
  const movement = currentMovement(flight);
  const flow = movement.groundOps;
  if (!flow || flow.status === "not_started") {
    return;
  }
  for (let guard = 0; guard < 200; guard += 1) {
    let changed = false;
    const nodes = [...flow.nodes].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.createdMinute - b.createdMinute || a.id.localeCompare(b.id));
    for (const nodeState of nodes) {
      if (nodeState.status !== "planned") continue;
      const startMinute = eligibleGroundOpsStartMinute(flow, nodeState, ops.currentMinute);
      if (startMinute > targetMinute) continue;
      const node = hydrateGroundOpsNode(nodeState);
      const result = node.start({ save, ops, flight, movement, flow, currentMinute: startMinute });
      if (result.ok) {
        changed = true;
      }
    }
    for (const nodeState of nodes) {
      if (nodeState.status !== "running" || nodeState.readyAtMinute === undefined || nodeState.readyAtMinute > targetMinute) continue;
      const node = hydrateGroundOpsNode(nodeState);
      if (node.end({ save, ops, flight, movement, flow, currentMinute: nodeState.readyAtMinute }, (completed) => {
        handleGroundOpsNodeCompleted(save, ops, flight, movement, flow, completed);
      })) {
        changed = true;
      }
    }
    if (!changed) break;
  }
  for (const nodeState of flow.nodes) {
    if (nodeState.status === "running") {
      hydrateGroundOpsNode(nodeState).advance({ save, ops, flight, movement, flow, currentMinute: targetMinute });
    }
  }
}

function advanceAllGroundOpsNodes(save: SaveGame, ops: ActiveOpsDay, targetMinute: number) {
  for (const flight of ops.flights) {
    if (!isTerminalFlightStatus(flight.status)) {
      advanceGroundOpsNodes(save, ops, flight, targetMinute);
    }
  }
}

function handleGroundOpsNodeCompleted(_save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, _movement: FlightMovement, flow: GroundOpsFlowSnapshot, node: GroundOpsNodeState) {
  const completedMinute = node.completedMinute ?? node.readyAtMinute ?? ops.currentMinute;
  const remainingNodes = activeGroundOpsNodesInFlow(flow);
  if (remainingNodes.length === 0 && flow.status === "in_progress") {
    flow.status = "completed";
    flow.completedMinute = completedMinute;
    flow.readyAtMinute = completedMinute;
    flow.delayMinutes = Math.max(0, flight.estimatedDepartureMinute - flight.plannedDepartureMinute);
    flight.status = "ready_for_departure";
    flight.reason = READY_FOR_DEPARTURE_REASON;
    syncCurrentMovementFromFlight(flight);
  } else if (flight.status === "released" || flight.status === "ground_service" || flight.status === "ready_for_departure") {
    flight.reason = `${groundOpsNodeLabel(node.kind)}完成，放行计划保持有效`;
  } else {
    clearFlightLedger(flight);
    markFlightNeedsRelease(flight);
    flight.reason = `${groundOpsNodeLabel(node.kind)}完成，等待重新放行`;
  }
  appendOpsEvent(ops, flight.id, "ready", completedMinute, "system", `${flight.flightNo} 运行节点完成：${groundOpsNodeSummary(node)}。`, node);
}

function nextGroundOpsNodeId(flight: FlightInstance): string {
  const movement = currentMovement(flight);
  const flow = groundOpsFlowForCurrentMovement(flight);
  return `${movement.id}-node-${String(flow.nodes.length + 1).padStart(3, "0")}`;
}

function groundOpsNodeSeed(flight: FlightInstance, kind: GroundOpsNodeKind): string {
  const movement = currentMovement(flight);
  const flow = groundOpsFlowForCurrentMovement(flight);
  return `${flight.id}:${movement.id}:${kind}:${flow.nodes.length + 1}`;
}

function eligibleGroundOpsStartMinute(flow: GroundOpsFlowSnapshot, node: GroundOpsNodeState, fallbackMinute: number): number {
  const dependencyReady = dependencyReadyMinute(flow, node, fallbackMinute);
  return Math.max(fallbackMinute, flow.startedMinute ?? fallbackMinute, node.plannedStartMinute ?? fallbackMinute, dependencyReady);
}

function dependencyReadyMinute(flow: GroundOpsFlowSnapshot, node: GroundOpsNodeState, fallbackMinute: number): number {
  const dependencyReadyMinutes = (node.dependsOnNodeIds ?? []).map((id) => flow.nodes.find((item) => item.id === id)?.completedMinute ?? Number.MAX_SAFE_INTEGER);
  return Math.max(fallbackMinute, ...dependencyReadyMinutes);
}

function projectGroundOpsReadyAt(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance): number {
  const projected = cloneSave(save);
  const projectedOps = projected.activeOpsDay;
  const projectedFlight = projectedOps?.flights.find((item) => item.id === flight.id);
  if (!projectedOps || !projectedFlight) {
    return ops.currentMinute;
  }
  const projectionLimitMinute = groundOpsProjectionLimitMinute(projectedFlight, ops.currentMinute);
  advanceGroundOpsNodes(projected, projectedOps, projectedFlight, projectionLimitMinute);
  const flow = currentGroundOpsForFlight(projectedFlight);
  return projectedGroundOpsReadyMinute(flow, ops.currentMinute);
}

function groundOpsProjectionLimitMinute(flight: FlightInstance, currentMinute: number): number {
  const departureProjectionLimitMinute = flight.estimatedDepartureMinute + 720;
  const currentProjectionLimitMinute = currentMinute + 720;
  return Math.max(departureProjectionLimitMinute, currentProjectionLimitMinute);
}

function projectedGroundOpsReadyMinute(flow: GroundOpsFlowSnapshot | undefined, currentMinute: number): number {
  const projectedNodeMinutes = (flow?.nodes ?? []).map((node) => node.completedMinute ?? node.readyAtMinute ?? node.startedMinute ?? node.plannedStartMinute ?? currentMinute);
  return Math.max(currentMinute, ...projectedNodeMinutes);
}

function groundOpsNodeSummary(node: GroundOpsNodeState): string {
  if (node.kind === "payload_offload") {
    return `${groundOpsNodeLabel(node.kind)} ${node.requestedPassengerReduction ?? 0} pax / ${Math.round(node.requestedCargoReductionKg ?? 0)}kg`;
  }
  if (node.kind === "fuel_uplift" || node.kind === "fuel_defuel") {
    const delta = groundOpsFuelDeltaSummaryKg(node);
    return `${groundOpsNodeLabel(node.kind)} ${delta}kg`;
  }
  return groundOpsNodeLabel(node.kind);
}

function groundOpsFuelDeltaSummaryKg(node: GroundOpsNodeState): number {
  const requestedDeltaKg = node.requestedFuelDeltaKg ?? 0;
  const absoluteDeltaKg = Math.abs(requestedDeltaKg);
  return Math.round(absoluteDeltaKg);
}

function groundOpsNodeLabel(kind: GroundOpsNodeKind): string {
  if (kind === "payload_offload") return "减载";
  if (kind === "fuel_defuel") return "抽油";
  if (kind === "fuel_uplift") return "加油";
  if (kind === "cabin_cleaning") return "客舱清洁";
  if (kind === "water_service") return "上水";
  if (kind === "catering") return "餐食";
  if (kind === "passenger_boarding") return "旅客登机";
  if (kind === "baggage_loading") return "行李装载";
  if (kind === "cargo_loading") return "货邮装载";
  if (kind === "loadsheet") return "舱单配平";
  if (kind === "doors_closed") return "关门";
  if (kind === "chocks_off") return "撤轮挡";
  if (kind === "pushback") return "推出";
  return "地面滑行";
}

function addDelayEvent(
  flight: FlightInstance,
  code: FlightInstance["delayEvents"][number]["code"],
  minutes: number,
  controllable: boolean,
  message: string,
  sourceFlightInstanceId?: string
) {
  const roundedMinutes = delayEventMinutes(minutes);
  const duplicate = flight.delayEvents.some((event) => event.code === code && event.message === message && event.sourceFlightInstanceId === sourceFlightInstanceId);
  if (!duplicate) {
    flight.delayEvents.push({
      id: `${flight.id}-delay-${flight.delayEvents.length + 1}`,
      code,
      minutes: roundedMinutes,
      controllable,
      message,
      sourceFlightInstanceId
    });
  }
  refreshDelaySummary(flight);
}

function delayEventMinutes(minutes: number): number {
  const roundedMinutes = Math.round(minutes);
  return Math.max(0, roundedMinutes);
}

function refreshDelaySummary(flight: FlightInstance) {
  const departureDelayMinutes = Math.max(0, (flight.actualDepartureMinute ?? flight.estimatedDepartureMinute) - flight.plannedDepartureMinute);
  const arrivalDelayMinutes = Math.max(0, (flight.actualArrivalMinute ?? flight.estimatedArrivalMinute) - flight.plannedArrivalMinute);
  const propagatedDelayMinutes = flight.delayEvents.filter((event) => event.code === "late_aircraft").reduce((sum, event) => sum + event.minutes, 0);
  const controllableDelayMinutes = flight.delayEvents.filter((event) => event.controllable).reduce((sum, event) => sum + event.minutes, 0);
  const primary = [...flight.delayEvents].sort((a, b) => b.minutes - a.minutes)[0];
  flight.delaySummary = {
    departureDelayMinutes,
    arrivalDelayMinutes,
    propagatedDelayMinutes,
    controllableDelayMinutes,
    primaryCode: primary?.code,
    a15Exceeded: arrivalDelayMinutes > ON_TIME_ARRIVAL_THRESHOLD_MINUTES
  };
}

function appendOpsEvent(
  ops: ActiveOpsDay,
  flightInstanceId: string,
  type: FlightEvent["type"],
  minute: number,
  actor: FlightEvent["actor"],
  message: string,
  payload?: unknown
): FlightEvent {
  const event: FlightEvent = {
    id: `${ops.id}-evt-${String(ops.nextEventSeq).padStart(4, "0")}`,
    flightInstanceId,
    day: ops.day,
    minute,
    type,
    actor,
    message,
    payload
  };
  ops.nextEventSeq += 1;
  ops.events.push(event);
  sortOpsEvents(ops);
  return event;
}

function appendAirportServiceRecord(
  save: SaveGame,
  flight: FlightInstance,
  offer: AirportDepartureOffer,
  decision: AirportServiceRecord["decision"],
  minute: number,
  events: GlobalEvent[]
): AirportServiceRecord {
  const existingRecords = save.airportServiceRecords ?? [];
  const record: AirportServiceRecord = {
    id: `airport-data-${save.day}-${flight.id}-${decision}-${minute}-${String(existingRecords.length + 1).padStart(4, "0")}`,
    flightInstanceId: flight.id,
    flightNo: flight.flightNo,
    day: flightOperationalDay(save, flight),
    dateIso: save.activeOpsDay?.dateIso ?? save.dateIso,
    minute,
    origin: flight.origin,
    destination: flight.destination,
    aircraftTypeId: flight.aircraftTypeId,
    aircraftId: flight.aircraftId,
    decision,
    offer,
    eventSnapshots: offer.appliedEventIds
      .map((eventId) => events.find((event) => event.id === eventId))
      .filter((event): event is GlobalEvent => Boolean(event))
      .map((event) => ({
        id: event.id,
        title: event.title,
        severity: event.severity,
        scopeType: event.scopeType,
        scopeValue: event.scopeValue,
        reason: event.reason,
        effectTypes: event.effects.map((effect) => effect.type)
      })),
    summary: {
      status: offer.handling.status,
      passengers: offer.payloadOffer.passengers,
      cargoKg: offer.payloadOffer.cargoKg,
      originFuelPriceUsdPerTon: offer.fuelQuote.priceUsdPerTon,
      destinationFuelPriceUsdPerTon: offer.destinationFuelQuote.priceUsdPerTon,
      expectedGroundDelayMinutes: offer.handling.expectedGroundDelayMinutes,
      expectedFuelingDelayMinutes: offer.fuelQuote.expectedFuelingDelayMinutes,
      blockers: [...offer.handling.blockers]
    }
  };
  save.airportServiceRecords = [record, ...existingRecords].slice(0, 240);
  flight.airportServiceRecordId = record.id;
  flight.airportOfferSnapshot = record.offer;
  return record;
}

export function sortFlightEvents(events: FlightEvent[]): FlightEvent[] {
  return [...events].sort(compareFlightEvents);
}

function sortOpsEvents(ops: ActiveOpsDay) {
  ops.events.sort(compareFlightEvents);
}

function compareFlightEvents(a: FlightEvent, b: FlightEvent): number {
  return a.day - b.day || a.minute - b.minute || flightEventSeq(a) - flightEventSeq(b) || a.id.localeCompare(b.id);
}

function flightEventSeq(event: FlightEvent): number {
  const match = event.id.match(/evt-(\d+)$/);
  if (match) {
    return Number(match[1]);
  }
  return Number.MAX_SAFE_INTEGER;
}

type ReleaseFlightOptions = {
  actor?: FlightEvent["actor"];
  minute?: number;
  preserveOnBlock?: boolean;
};

function confirmReleasePlan(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, events: GlobalEvent[], schedule: ScheduleSource, flightConfig: FlightAirportConfigBundle, options: ReleaseFlightOptions = {}) {
  const actor = options.actor ?? "system";
  const eventMinute = options.minute ?? Math.max(0, flight.estimatedDepartureMinute - 60);
  const releaseLabel = formatReleaseOperationLabel(flight);
  if (!flight.aircraftId) {
    blockOrCancelRelease(ops, flight, "未完成飞机排班。", options);
    return;
  }
  const airportOffer = buildAirportOfferForFlight(save, flight, events, flightConfig, eventMinute);
  const blocks = releaseBlocks(save, flight, events, schedule, eventMinute, flightConfig, airportOffer);
  if (blocks.length > 0) {
    if (airportOffer && airportOffer.handling.blockers.some((blocker) => blocks.includes(blocker))) {
      appendAirportServiceRecord(save, flight, airportOffer, "blocked", eventMinute, events);
    }
    blockOrCancelRelease(ops, flight, blocks[0], options);
    return;
  }

  const aircraft = save.fleet.find((item) => item.id === flight.aircraftId);
  const origin = requireAirport(flight.origin);
  const destination = requireAirport(flight.destination);
  if (!aircraft) {
    blockOrCancelRelease(ops, flight, "指派飞机不存在。", options);
    return;
  }
  const aircraftCandidate = selectedAircraftCandidate(save, ops, flight);
  if (aircraft.activeFlightInstanceId && aircraft.activeFlightInstanceId !== flight.id && aircraftCandidate?.availability !== "arrives_before_departure") {
    blockOrCancelRelease(ops, flight, `${aircraft.registration} 当前由 ${aircraft.activeFlightInstanceId} 锁定运行。`, options);
    return;
  }
  const ledger = buildFlightLedger(save, flight, events, flightConfig, airportOffer);
  if (!ledger) {
    blockOrCancelRelease(ops, flight, "无法生成业载/燃油/重量放行账本。", options);
    return;
  }
  flight.aircraftSnapshotBefore = snapshotAircraftState(aircraft);
  flight.payloadPlan = ledger.payloadPlan;
  flight.fuelPlan = ledger.fuelPlan;
  flight.weightPlan = ledger.weightPlan;
  flight.revenueBreakdown = ledger.revenueBreakdown;
  flight.costBreakdown = ledger.costBreakdown;
  flight.airportOfferSnapshot = ledger.airportOffer;
  flight.passengers = ledger.payloadPlan.passengers;
  flight.revenueUsd = ledger.revenueBreakdown.totalRevenueUsd;
  flight.costUsd = ledger.costBreakdown.totalCostUsd;
  if (ledger.weightPlan.fuelLimited) {
    appendAirportServiceRecord(save, flight, ledger.airportOffer, "checked", eventMinute, events);
    blockOrCancelRelease(
      ops,
      flight,
      fuelLimitBlockMessage(ledger.fuelPlan),
      options
    );
    return;
  }

  appendAirportServiceRecord(save, flight, ledger.airportOffer, "checked", eventMinute, events);
  const disruptionDelay = totalAirspaceDelayMinutes(origin, destination, events);
  if (disruptionDelay > 0) {
    flight.estimatedArrivalMinute += disruptionDelay;
    flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
    flight.reason = `航路限制增加 ${disruptionDelay} 分钟`;
    addDelayEvent(flight, "airspace", disruptionDelay, false, flight.reason);
    appendOpsEvent(ops, flight.id, "delayed", eventMinute, actor, `${flight.flightNo} 放行预警：${flight.reason}。`);
  }
  const movement = currentMovement(flight);
  const flow = groundOpsFlowForCurrentMovement(flight);
  movement.groundOps = {
    ...flow,
    status: releaseGroundOpsStatus(flow.status),
    startWindowMinute: groundServiceWindowOpenMinute(flight),
    plannedTakeoffMinute: movement.plannedDepartureMinute,
    taxiOutMinutes: aircraftTypeById(flight.aircraftTypeId)?.fuelProfile.taxiOutMinutes ?? flow.taxiOutMinutes,
    delayMinutes: flow.delayMinutes ?? 0,
    requiredNodeCount: flow.requiredNodeCount ?? 0,
    nodes: releaseGroundOpsNodes(flow)
  };
  flight.status = "released";
  flight.releaseDraft = {
    ...(flight.releaseDraft ?? buildFlightReleaseDraft(save, flight, events, schedule, flightConfig)),
    confirmedMinute: eventMinute,
    status: "confirmed"
  };
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "released", eventMinute, actor, releaseConfirmedMessage(flight.flightNo, releaseLabel, actor));
}

function releaseGroundOpsStatus(status: GroundOpsFlowSnapshot["status"]): GroundOpsFlowSnapshot["status"] {
  if (status === "completed") {
    return "completed";
  }
  return "not_started";
}

function releaseGroundOpsNodes(flow: GroundOpsFlowSnapshot): GroundOpsNodeState[] {
  if (flow.status === "completed") {
    return flow.nodes;
  }
  return flow.nodes.filter((node) => node.status === "completed" || node.status === "cancelled");
}

function releaseConfirmedMessage(flightNo: string, releaseLabel: string, actor: FlightEvent["actor"]): string {
  if (actor === "player") {
    return `${flightNo} ${releaseLabel}计划已确认。`;
  }
  return `${flightNo} 放行计划已确认。`;
}

function releaseBlocks(save: SaveGame, flight: FlightInstance, events: GlobalEvent[], schedule: ScheduleSource, eventMinute: number, flightConfig: FlightAirportConfigBundle, airportOfferOverride?: AirportDepartureOffer): string[] {
  const aircraft = flightAircraft(save, flight);
  const origin = requireAirport(flight.origin);
  const destination = requireAirport(flight.destination);
  const assignment = assignmentForFlight(flight);
  const operationDay = flightOperationalDay(save, flight);
  const validationBlocks = validateOperation({
    day: operationDay,
    plannedTypeId: plannedTypeIdForValidation(flight),
    origin,
    destination,
    distanceKm: flight.distanceKm,
    assignment,
    licenseRequired: undefined,
    grantedLicenseCodes: save.licenses.filter((license) => license.granted).map((license) => license.code),
    aircraft,
    events
  });
  const occurrence = scheduleOccurrenceForFlight(schedule, flight);
  const cancellation = scheduleCancellationForFlight(schedule, flight);
  const candidate = releaseCandidateForAssignedAircraft(save, aircraft, flight);
  const positionBlocks = releasePositionBlocks(aircraft, flight, candidate);
  const candidateBlocks = releaseCandidateBlocks(candidate);
  const dispatchReadyMinute = dispatchReadyMinuteForFlight(flight.readiness);
  const readinessBlocks = releaseReadinessBlocks(flight.readiness, dispatchReadyMinute, eventMinute);
  const type = ledgerAircraftType(flight, aircraft);
  const airportOffer = airportOfferOverride ?? airportOfferForReleaseBlocks(type, origin, destination, flight, operationDay, eventMinute, events, flightConfig);
  const airportBlocks = airportOffer?.handling.blockers ?? [];
  if (occurrence?.status === "cancelled") {
    return [`GM 已取消：${occurrence.cancellationReason ?? "具体日期航班取消"}`];
  }
  if (cancellation) {
    return [`GM 已取消：${cancellation.reason}`];
  }
  return [...readinessBlocks, ...airportBlocks, ...candidateBlocks, ...validationBlocks, ...positionBlocks].filter((block): block is string => Boolean(block));
}

function releaseCandidateForAssignedAircraft(
  save: SaveGame,
  aircraft: Aircraft | undefined,
  flight: FlightInstance
): AircraftReleaseCandidate | undefined {
  if (!aircraft || !save.activeOpsDay) {
    return undefined;
  }
  return selectedAircraftCandidate(save, save.activeOpsDay, flight);
}

function releasePositionBlocks(aircraft: Aircraft | undefined, flight: FlightInstance, candidate: AircraftReleaseCandidate | undefined): string[] {
  if (!aircraft || aircraft.location === flight.origin || candidate?.availability === "arrives_before_departure") {
    return [];
  }
  return [`飞机位置不符：${aircraft.registration} 当前在 ${aircraft.location}，计划需要 ${flight.origin}。`];
}

function releaseCandidateBlocks(candidate: AircraftReleaseCandidate | undefined): string[] {
  if (candidate?.availability === "unavailable") {
    return [candidate.reason];
  }
  return [];
}

function plannedTypeIdForValidation(flight: FlightInstance): string | undefined {
  if (flight.sourceType === "recovery") {
    return flight.aircraftTypeId;
  }
  return undefined;
}

function scheduleOccurrenceForFlight(schedule: ScheduleSource, flight: FlightInstance): FlightOccurrence | undefined {
  if (!flight.occurrenceId) {
    return undefined;
  }
  return schedule.occurrences.find((item) => item.id === flight.occurrenceId);
}

function scheduleCancellationForFlight(schedule: ScheduleSource, flight: FlightInstance): ScheduleSource["cancellations"][number] | undefined {
  if (!flight.occurrenceId) {
    return undefined;
  }
  return schedule.cancellations.find((item) => item.occurrenceId === flight.occurrenceId);
}

function dispatchReadyMinuteForFlight(readiness: FlightInstance["readiness"]): number {
  if (readiness) {
    return Math.max(0, readiness.readyAtMinute - 60);
  }
  return 0;
}

function airportOfferForReleaseBlocks(
  type: AircraftType | undefined,
  origin: Airport,
  destination: Airport,
  flight: FlightInstance,
  operationDay: number,
  eventMinute: number,
  events: GlobalEvent[],
  flightConfig: FlightAirportConfigBundle
): AirportDepartureOffer | undefined {
  if (!type) {
    return undefined;
  }
  return buildAirportDepartureOffer({
    origin,
    destination,
    aircraftType: type,
    distanceKm: flight.distanceKm,
    day: operationDay,
    minute: eventMinute,
    events,
    originOps: flightConfig.origin.ops,
    destinationOps: flightConfig.destination.ops,
    originPayload: flightConfig.origin.payload,
    originFuel: flightConfig.origin.fuel,
    destinationFuel: flightConfig.destination.fuel,
    routeMarket: flightConfig.routeMarket
  });
}

function releaseReadinessBlocks(readiness: FlightInstance["readiness"], dispatchReadyMinute: number, eventMinute: number): string[] {
  if (!readiness || eventMinute >= dispatchReadyMinute) {
    return [];
  }
  return [`恢复准备未完成：预计 ${dayMinuteToLabel(dispatchReadyMinute)} 后可放行，最早起飞 ${dayMinuteToLabel(readiness.readyAtMinute)}。`];
}

function flightOperationalDay(save: SaveGame, flight: Pick<FlightInstance, "plannedDepartureMinute">): number {
  const departureDayOffset = Math.floor(flight.plannedDepartureMinute / 1440);
  const protectedDepartureDayOffset = Math.max(0, departureDayOffset);
  return save.day + protectedDepartureDayOffset;
}

function activeOccurrenceEventsForFlight(schedule: ScheduleSource, flight: FlightInstance): FlightOccurrenceEvent[] {
  if (!flight.occurrenceId) {
    return [];
  }
  return schedule.occurrenceEvents.filter((event) => event.occurrenceId === flight.occurrenceId && event.status === "active");
}

function dispatchedOccurrenceEventsForFlight(schedule: ScheduleSource, flight: FlightInstance): FlightOccurrenceEvent[] {
  const dispatchedIds = new Set(flight.appliedOccurrenceEventIds);
  return activeOccurrenceEventsForFlight(schedule, flight).filter((event) => dispatchedIds.has(event.id));
}

function dispatchDueOccurrenceEventsForFlight(ops: ActiveOpsDay, schedule: ScheduleSource, flight: FlightInstance, targetMinute: number) {
  if (!flight.occurrenceId || isTerminalFlightStatus(flight.status)) {
    return;
  }
  for (const event of activeOccurrenceEventsForFlight(schedule, flight)) {
    if (flight.appliedOccurrenceEventIds.includes(event.id)) {
      continue;
    }
    const triggerMinute = occurrenceEventTriggerMinute(event, flight);
    if (triggerMinute === undefined || triggerMinute > targetMinute || occurrenceEventTriggersAfterArrival(event, flight, triggerMinute)) {
      continue;
    }
    flight.appliedOccurrenceEventIds = [...new Set([...flight.appliedOccurrenceEventIds, event.id])];
    addDelayEvent(flight, "gm_directive", 0, false, `GM 特情：${event.title}`);
    appendOpsEvent(ops, flight.id, "decision", triggerMinute, "gm", `GM 下发特情：${event.title}，${formatOccurrenceEventTrigger(event)}。`, event);
  }
}

function occurrenceEventTriggerMinute(event: FlightOccurrenceEvent, flight: FlightInstance): number | undefined {
  const trigger = normalizedOccurrenceEventTrigger(event);
  if (trigger.reference === "ground_service_start") {
    const startedMinute = currentGroundOpsForFlight(flight)?.startedMinute;
    return startedMinute === undefined ? undefined : startedMinute + trigger.offsetMinutes;
  }
  const departureMinute = flight.actualDepartureMinute;
  return departureMinute === undefined ? undefined : departureMinute + trigger.offsetMinutes;
}

function occurrenceEventTriggersAfterArrival(event: FlightOccurrenceEvent, flight: FlightInstance, triggerMinute: number): boolean {
  if (normalizedOccurrenceEventTrigger(event).reference !== "takeoff") {
    return false;
  }
  const arrivalMinute = flight.actualArrivalMinute ?? flight.estimatedArrivalMinute;
  return triggerMinute >= arrivalMinute;
}

function normalizedOccurrenceEventTrigger(event: FlightOccurrenceEvent): NonNullable<FlightOccurrenceEvent["trigger"]> {
  if (event.trigger) {
    return { reference: event.trigger.reference, offsetMinutes: Math.max(0, Math.round(event.trigger.offsetMinutes)) };
  }
  return defaultOccurrenceEventTrigger(event.phase);
}

function defaultOccurrenceEventTrigger(phase: FlightOccurrenceEvent["phase"]): NonNullable<FlightOccurrenceEvent["trigger"]> {
  if (phase === "enroute" || phase === "arrival") {
    return { reference: "takeoff", offsetMinutes: 0 };
  }
  return { reference: "ground_service_start", offsetMinutes: 0 };
}

function formatOccurrenceEventPhase(phase: FlightOccurrenceEvent["phase"]): string {
  if (phase === "pre_departure") return "起飞前";
  if (phase === "enroute") return "空中";
  if (phase === "arrival") return "进近/到达";
  return "地面";
}

function formatOccurrenceEventTrigger(event: FlightOccurrenceEvent): string {
  const trigger = normalizedOccurrenceEventTrigger(event);
  const reference = trigger.reference === "takeoff" ? "离地" : "开保障";
  return `${reference}后 ${trigger.offsetMinutes} 分钟`;
}

export function currentMovementForFlight(flight: FlightInstance): FlightMovement | undefined {
  return flight.movements?.find((movement) => movement.id === flight.currentMovementId) ?? flight.movements?.[flight.movements.length - 1];
}

export function currentGroundOpsForFlight(flight: FlightInstance): GroundOpsFlowSnapshot | undefined {
  return currentMovementForFlight(flight)?.groundOps;
}

export function currentGroundOpsNodesForFlight(flight: FlightInstance): GroundOpsNodeState[] {
  return currentGroundOpsForFlight(flight)?.nodes ?? [];
}

function currentMovement(flight: FlightInstance): FlightMovement {
  ensureMovementState(flight);
  return flight.movements.find((movement) => movement.id === flight.currentMovementId) ?? flight.movements[flight.movements.length - 1];
}

function ensureMovementState(flight: FlightInstance) {
  if (flight.movements?.length > 0) {
    if (!flight.currentMovementId) {
      flight.currentMovementId = flight.movements[flight.movements.length - 1].id;
    }
    for (const movement of flight.movements) {
      movement.groundOps = movement.groundOps ?? groundOpsPlanForMovement(flight, movement);
    }
    return;
  }
  const movement: FlightMovement = {
    id: `${flight.id}-mv-001`,
    sequence: 1,
    kind: "planned",
    origin: flight.origin,
    destination: flight.destination,
    plannedDepartureMinute: flight.plannedDepartureMinute,
    plannedArrivalMinute: flight.plannedArrivalMinute,
    estimatedDepartureMinute: flight.estimatedDepartureMinute,
    estimatedArrivalMinute: flight.estimatedArrivalMinute,
    actualDepartureMinute: flight.actualDepartureMinute,
    actualArrivalMinute: flight.actualArrivalMinute,
    status: movementStatusForFlight(flight),
    groundOps: groundOpsPlanForMovement(flight, {
      plannedDepartureMinute: flight.plannedDepartureMinute,
      estimatedDepartureMinute: flight.estimatedDepartureMinute
    }),
    reason: flight.reason
  };
  flight.movements = [movement];
  flight.currentMovementId = movement.id;
}

function migrateFlightGroundOpsToCurrentMovement(flight: FlightInstance) {
  ensureMovementState(flight);
  const movement = currentMovement(flight);
  const legacyFlight = flight as FlightInstance & {
    opsNodes?: GroundOpsNodeState[];
    groundService?: Omit<GroundOpsFlowSnapshot, "nodes">;
  };
  if (legacyFlight.opsNodes?.length && movement.groundOps && movement.groundOps.nodes.length === 0) {
    movement.groundOps = {
      ...movement.groundOps,
      ...(legacyFlight.groundService ?? {}),
      nodes: legacyFlight.opsNodes.map((node, index) => ({
        ...node,
        movementId: node.movementId ?? movement.id,
        status: migratedGroundOpsNodeStatus(node.status),
        seed: node.seed ?? `${flight.id}:${movement.id}:${node.kind}:${index + 1}`
      }))
    };
  }
  delete legacyFlight.opsNodes;
  delete legacyFlight.groundService;
}

function migratedGroundOpsNodeStatus(status: GroundOpsNodeState["status"] | string): GroundOpsNodeState["status"] {
  if (status === "pending") {
    return "running";
  }
  return status as GroundOpsNodeState["status"];
}

function syncCurrentMovementFromFlight(flight: FlightInstance) {
  const movement = currentMovement(flight);
  movement.origin = flight.origin;
  movement.destination = flight.destination;
  movement.estimatedDepartureMinute = flight.estimatedDepartureMinute;
  movement.estimatedArrivalMinute = flight.estimatedArrivalMinute;
  movement.actualDepartureMinute = flight.actualDepartureMinute;
  movement.actualArrivalMinute = flight.actualArrivalMinute;
  movement.status = movementStatusForFlight(flight);
  movement.reason = flight.reason;
}

function movementStatusForFlight(flight: FlightInstance): FlightMovement["status"] {
  if (flight.status === "released" || flight.status === "ground_service" || flight.status === "ready_for_departure") return "released";
  if (flight.status === "airborne" || flight.status === "diverted") return "airborne";
  if (flight.status === "landed" || flight.status === "interrupted") return "landed";
  if (flight.status === "cancelled") return "cancelled";
  if (flight.status === "failed") return "failed";
  return "planned";
}

function addFlightMovement(
  flight: FlightInstance,
  kind: FlightMovement["kind"],
  origin: string,
  destination: string,
  departureMinute: number,
  blockMinutes: number,
  reason: string,
  eventId?: string,
  decisionId?: string
): FlightMovement {
  ensureMovementState(flight);
  const movement: FlightMovement = {
    id: `${flight.id}-mv-${String(flight.movements.length + 1).padStart(3, "0")}`,
    sequence: flight.movements.length + 1,
    kind,
    origin,
    destination,
    plannedDepartureMinute: departureMinute,
    plannedArrivalMinute: departureMinute + blockMinutes,
    estimatedDepartureMinute: departureMinute,
    estimatedArrivalMinute: departureMinute + blockMinutes,
    status: "planned",
    triggeredByEventId: eventId,
    decisionId,
    groundOps: groundOpsPlanForMovement(flight, {
      plannedDepartureMinute: departureMinute,
      estimatedDepartureMinute: departureMinute
    }),
    reason
  };
  flight.movements = [...flight.movements, movement];
  flight.currentMovementId = movement.id;
  return movement;
}

function resolveFlightDisruption(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance, action: Extract<RedispatchAction, { type: "resolve_disruption" }>, schedule: ScheduleSource) {
  const event = dispatchedOccurrenceEventsForFlight(schedule, flight).find((item) => item.id === action.eventId);
  if (!event) {
    throw new Error("待处置特情不存在或已失效。");
  }
  if ((flight.disruptionDecisions ?? []).some((decision) => decision.eventId === event.id)) {
    throw new Error("该特情已经处置过。");
  }
  const intent = action.intent;
  const targetAirport = redispatchTargetAirport(action.targetAirport);
  if (intent === "divert" && !targetAirport) {
    throw new Error("备降处置必须选择备降机场。");
  }
  const waitMinutes = disruptionWaitMinutes(action.waitMinutes);
  const outcome = disruptionOutcomeForDecision(flight, event, intent, ops.currentMinute, targetAirport, waitMinutes);
  const decision: FlightDisruptionDecision = {
    id: `${flight.id}-decision-${String((flight.disruptionDecisions?.length ?? 0) + 1).padStart(3, "0")}`,
    eventId: event.id,
    minute: ops.currentMinute,
    intent,
    targetAirport,
    waitMinutes: disruptionDecisionWaitMinutes(intent, waitMinutes),
    note: action.reason,
    outcome
  };
  flight.disruptionDecisions = [...(flight.disruptionDecisions ?? []), decision];
  flight.appliedOccurrenceEventIds = [...new Set([...flight.appliedOccurrenceEventIds, event.id])];
  save.cashUsd = Math.round(save.cashUsd + outcome.cashDeltaUsd);
  save.reputation = clamp(Math.round(save.reputation + outcome.reputationDelta), 0, 100);

  if (intent === "cancel") {
    flight.status = "cancelled";
    flight.completionMode = "cancelled";
    flight.finalAirport = flight.currentAirport ?? flight.origin;
    flight.costUsd = Math.max(flight.costUsd, 15_000);
    flight.reason = action.reason || outcome.summary;
    cancelPendingGroundOpsNodes(flight, ops.currentMinute, ["payload_offload", "fuel_uplift", "fuel_defuel"]);
    releaseAircraftLock(save, flight);
    syncCurrentMovementFromFlight(flight);
    appendOpsEvent(ops, flight.id, "cancelled", ops.currentMinute, "player", `${flight.flightNo} 特情处置：提前取消。${outcome.summary}`, decision);
    return;
  }

  if (intent === "divert" && targetAirport) {
    divertFlightToAirport(flight, targetAirport, ops.currentMinute, action.reason || outcome.summary, event.id, decision.id);
    appendOpsEvent(ops, flight.id, "diverted", ops.currentMinute, "player", `${flight.flightNo} 特情处置：备降 ${targetAirport}。${outcome.summary}`, decision);
    return;
  }

  if (intent === "wait") {
    const delayMinutes = waitDecisionDelayMinutes(waitMinutes, outcome.delayMinutes);
    applyDisruptionDelay(flight, delayMinutes, action.reason || outcome.summary);
    appendOpsEvent(ops, flight.id, "decision", ops.currentMinute, "player", `${flight.flightNo} 特情处置：等待 ${delayMinutes} 分钟。${outcome.summary}`, decision);
    return;
  }

  if (outcome.delayMinutes > 0) {
    applyDisruptionDelay(flight, outcome.delayMinutes, action.reason || outcome.summary);
  }
  appendOpsEvent(ops, flight.id, "decision", ops.currentMinute, "player", `${flight.flightNo} 特情处置：继续运行。${outcome.summary}`, decision);
}

function redispatchTargetAirport(airportCode: string | undefined): string | undefined {
  if (!airportCode) {
    return undefined;
  }
  return requireAirport(airportCode).code;
}

function disruptionDecisionWaitMinutes(intent: FlightDisruptionIntent, waitMinutes: number): number | undefined {
  if (intent === "wait") {
    return waitMinutes;
  }
  return undefined;
}

function disruptionWaitMinutes(waitMinutes: number | undefined): number {
  const roundedWaitMinutes = Math.round(waitMinutes ?? 0);
  return Math.max(0, roundedWaitMinutes);
}

function waitDecisionDelayMinutes(waitMinutes: number, outcomeDelayMinutes: number): number {
  return Math.max(waitMinutes, outcomeDelayMinutes);
}

function disruptionOutcomeForDecision(
  flight: FlightInstance,
  event: FlightOccurrenceEvent,
  intent: FlightDisruptionIntent,
  minute: number,
  targetAirport: string | undefined,
  waitMinutes: number
): FlightDisruptionOutcome {
  const severityFactor = disruptionSeverityFactor(event.severity);
  const roll = deterministicHash(`${flight.id}:${event.id}:${intent}:${minute}:${targetAirport ?? ""}:${waitMinutes}`) % 100;
  const minDelay = disruptionMinimumDelayMinutes(event, severityFactor);
  const maxDelay = disruptionMaximumDelayMinutes(event, severityFactor, minDelay);
  const randomDelay = minDelay + (roll % Math.max(1, maxDelay - minDelay + 1));
  if (intent === "divert") {
    const newsRisk = clamp(event.riskProfile.newsRisk * severityFactor + 0.18, 0, 0.9);
    const news = roll < Math.round(newsRisk * 100);
    return diversionDisruptionOutcome(news, randomDelay);
  }
  if (intent === "cancel") {
    const pressure = clamp(event.riskProfile.cancellationPressure * severityFactor, 0, 1);
    const accepted = roll < Math.round(pressure * 100);
    return cancellationDisruptionOutcome(accepted, event.severity);
  }
  if (intent === "wait") {
    const extended = roll < Math.round(clamp(event.riskProfile.diversionRisk * severityFactor, 0, 0.85) * 100);
    return waitDisruptionOutcome(extended, waitMinutes, randomDelay);
  }
  const negative = roll < Math.round(clamp((event.riskProfile.newsRisk + event.riskProfile.diversionRisk + event.riskProfile.cancellationPressure * 0.35) * severityFactor, 0, 0.95) * 100);
  return continueDisruptionOutcome(negative, randomDelay);
}

function disruptionMinimumDelayMinutes(event: FlightOccurrenceEvent, severityFactor: number): number {
  const rawMinimumDelayMinutes = event.riskProfile.delayMinutesMin * severityFactor;
  const roundedMinimumDelayMinutes = Math.round(rawMinimumDelayMinutes);
  return Math.max(0, roundedMinimumDelayMinutes);
}

function disruptionMaximumDelayMinutes(event: FlightOccurrenceEvent, severityFactor: number, minimumDelayMinutes: number): number {
  const rawMaximumDelayMinutes = event.riskProfile.delayMinutesMax * severityFactor;
  const roundedMaximumDelayMinutes = Math.round(rawMaximumDelayMinutes);
  return Math.max(minimumDelayMinutes, roundedMaximumDelayMinutes);
}

function diversionDisruptionOutcome(newsRiskTriggered: boolean, randomDelay: number): FlightDisruptionOutcome {
  let summary = "备降控制了安全风险，但飞机位置需要恢复。";
  let reputationDelta = -1;
  let generatedEvent: FlightDisruptionOutcome["generatedEvent"] = "diversion_recovery";
  if (newsRiskTriggered) {
    summary = "备降引发旅客舆情，后续恢复压力上升。";
    reputationDelta = -3;
    generatedEvent = "negative_news";
  }
  return {
    summary,
    delayMinutes: randomDelay,
    reputationDelta,
    cashDeltaUsd: -18_000,
    generatedEvent
  };
}

function cancellationDisruptionOutcome(acceptedByMarket: boolean, severity: FlightOccurrenceEvent["severity"]): FlightDisruptionOutcome {
  let summary = "提前取消切断运行风险，但旅客和媒体反应偏负面。";
  let cashDeltaUsd = -24_000;
  let generatedEvent: FlightDisruptionOutcome["generatedEvent"] = "cancellation_backlash";
  if (acceptedByMarket) {
    summary = "提前取消被市场理解为必要安全决策，但仍产生补偿成本。";
    cashDeltaUsd = -18_000;
    generatedEvent = "none";
  }
  return {
    summary,
    delayMinutes: 0,
    reputationDelta: cancellationReputationDelta(acceptedByMarket, severity),
    cashDeltaUsd,
    generatedEvent
  };
}

function waitDisruptionOutcome(extendedDelayTriggered: boolean, waitMinutes: number, randomDelay: number): FlightDisruptionOutcome {
  const reducedRandomDelay = Math.round(randomDelay * 0.35);
  let additionalDelayMinutes = reducedRandomDelay;
  let summary = "等待后运行风险下降。";
  let reputationDelta = 0;
  let cashDeltaUsd = -2_500;
  let generatedEvent: FlightDisruptionOutcome["generatedEvent"] = "none";
  if (extendedDelayTriggered) {
    additionalDelayMinutes = randomDelay;
    summary = "等待窗口继续恶化，整体延误被拉长。";
    reputationDelta = -1;
    cashDeltaUsd = -6_000;
    generatedEvent = "extended_delay";
  }
  return {
    summary,
    delayMinutes: waitMinutes + additionalDelayMinutes,
    reputationDelta,
    cashDeltaUsd,
    generatedEvent
  };
}

function continueDisruptionOutcome(negativeOutcomeTriggered: boolean, randomDelay: number): FlightDisruptionOutcome {
  let summary = "继续运行暂未触发额外后果。";
  let delayMinutes = 0;
  let reputationDelta = 0;
  let cashDeltaUsd = 0;
  let generatedEvent: FlightDisruptionOutcome["generatedEvent"] = "none";
  if (negativeOutcomeTriggered) {
    summary = "继续运行后风险兑现，追加延误和旅客投诉。";
    delayMinutes = randomDelay;
    reputationDelta = -2;
    cashDeltaUsd = -8_000;
    generatedEvent = "extended_delay";
  }
  return { summary, delayMinutes, reputationDelta, cashDeltaUsd, generatedEvent };
}

function disruptionSeverityFactor(severity: FlightOccurrenceEvent["severity"]): number {
  if (severity === "critical") {
    return 1.8;
  }
  if (severity === "warning") {
    return 1.35;
  }
  if (severity === "watch") {
    return 1.1;
  }
  return 0.8;
}

function cancellationReputationDelta(accepted: boolean, severity: FlightOccurrenceEvent["severity"]): number {
  if (!accepted) {
    return -3;
  }
  if (severity === "critical") {
    return 0;
  }
  return -1;
}

function applyDisruptionDelay(flight: FlightInstance, delayMinutes: number, reason: string) {
  const delay = positiveDisruptionDelayMinutes(delayMinutes);
  if (flight.status === "airborne" || flight.status === "diverted") {
    flight.estimatedArrivalMinute += delay;
  } else {
    flight.estimatedDepartureMinute += delay;
    flight.estimatedArrivalMinute += delay;
    if (flight.status !== "needs_recovery" && flight.status !== "interrupted") {
      flight.status = "delayed";
    }
  }
  flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
  flight.reason = reason;
  addDelayEvent(flight, "player_action", delay, true, reason);
  syncCurrentMovementFromFlight(flight);
}

function positiveDisruptionDelayMinutes(delayMinutes: number): number {
  const roundedDelayMinutes = Math.round(delayMinutes);
  return Math.max(1, roundedDelayMinutes);
}

function statusAfterDiversion(status: FlightInstance["status"]): FlightInstance["status"] {
  if (status === "airborne") {
    return "diverted";
  }
  return "delayed";
}

function divertedEstimatedArrivalMinute(currentEstimatedArrivalMinute: number, decisionMinute: number): number {
  const earliestArrivalMinute = decisionMinute + 35;
  const latestArrivalMinute = decisionMinute + 120;
  const cappedCurrentArrivalMinute = Math.min(currentEstimatedArrivalMinute, latestArrivalMinute);
  return Math.max(earliestArrivalMinute, cappedCurrentArrivalMinute);
}

function movementKindAfterDiversion(kind: FlightMovement["kind"]): FlightMovement["kind"] {
  if (kind === "planned") {
    return "diversion";
  }
  return kind;
}

function divertFlightToAirport(flight: FlightInstance, airportCode: string, minute: number, reason: string, eventId?: string, decisionId?: string) {
  const alternate = requireAirport(airportCode);
  flight.destination = alternate.code;
  flight.status = statusAfterDiversion(flight.status);
  flight.reason = reason;
  flight.estimatedArrivalMinute = divertedEstimatedArrivalMinute(flight.estimatedArrivalMinute, minute);
  flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
  addDelayEvent(flight, "player_action", flight.delayMinutes, true, reason);
  const movement = currentMovement(flight);
  movement.kind = movementKindAfterDiversion(movement.kind);
  movement.triggeredByEventId = eventId ?? movement.triggeredByEventId;
  movement.decisionId = decisionId ?? movement.decisionId;
  syncCurrentMovementFromFlight(flight);
}

function continueFlightToPlannedDestination(ops: ActiveOpsDay, flight: FlightInstance, reason: string) {
  if (flight.status !== "interrupted") {
    throw new Error("只有已落地但未抵达原计划目的地的航班才能继续补飞。");
  }
  const origin = flight.currentAirport ?? flight.destination;
  if (!origin || origin === flight.plannedDestination) {
    completeFlightAtCurrentAirport(ops, flight, "已抵达原计划目的地");
    return;
  }
  const destination = requireAirport(flight.plannedDestination);
  const type = aircraftTypeById(flight.aircraftTypeId);
  const distanceKm = calculateDistanceKm(origin, destination.code);
  const blockMinutes = flightBlockMinutes(distanceKm, type);
  const departureMinute = continuationDepartureMinute(ops.currentMinute, flight);
  const movement = addFlightMovement(flight, "continuation", origin, destination.code, departureMinute, blockMinutes, reason);
  flight.origin = origin;
  flight.destination = destination.code;
  flight.distanceKm = distanceKm;
  flight.plannedBlockMinutes = blockMinutes;
  flight.estimatedDepartureMinute = movement.estimatedDepartureMinute;
  flight.estimatedArrivalMinute = movement.estimatedArrivalMinute;
  flight.actualDepartureMinute = undefined;
  flight.actualArrivalMinute = undefined;
  flight.status = "awaiting_release";
  flight.reason = reason;
  clearFlightLedger(flight);
  syncCurrentMovementFromFlight(flight);
}

function continuationDepartureMinute(currentMinute: number, flight: FlightInstance): number {
  const earliestOpsDepartureMinute = currentMinute + 45;
  const latestArrivalMinute = flight.actualArrivalMinute ?? flight.estimatedArrivalMinute;
  const protectedMinimumTurnMinutes = Math.max(45, flight.minimumTurnMinutes);
  const aircraftReadyDepartureMinute = latestArrivalMinute + protectedMinimumTurnMinutes;
  return Math.max(earliestOpsDepartureMinute, aircraftReadyDepartureMinute);
}

function completionModeForFinalAirport(finalAirport: string, plannedDestination: string): FlightInstance["completionMode"] {
  if (finalAirport === plannedDestination) {
    return "arrived_planned_destination";
  }
  return "manual_closed";
}

function completeFlightAtCurrentAirport(ops: ActiveOpsDay, flight: FlightInstance, reason: string) {
  if (flight.status !== "interrupted") {
    throw new Error("只有已中断在外站的航班才能手动结束。");
  }
  const finalAirport = flight.currentAirport ?? flight.destination;
  flight.status = "landed";
  flight.completionMode = completionModeForFinalAirport(finalAirport, flight.plannedDestination);
  flight.finalAirport = finalAirport;
  flight.destination = finalAirport;
  flight.actualArrivalMinute = flight.actualArrivalMinute ?? ops.currentMinute;
  flight.estimatedArrivalMinute = Math.max(flight.estimatedArrivalMinute, flight.actualArrivalMinute);
  flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
  flight.reason = reason;
  syncCurrentMovementFromFlight(flight);
}

function fuelLimitBlockMessage(fuelPlan: FlightLedgerResult["fuelPlan"]): string {
  const minimumReleaseFuelTons = Math.round(fuelPlan.minimumReleaseFuelKg / 1000);
  const plannedTakeoffFuelTons = Math.round(fuelPlan.plannedTakeoffFuelKg / 1000);
  const maximumReleaseFuelTons = Math.round(fuelPlan.maximumReleaseFuelKg / 1000);
  return `燃油/重量限制：最低放行油 ${minimumReleaseFuelTons}t，当前计划 ${plannedTakeoffFuelTons}t，上限 ${maximumReleaseFuelTons}t。`;
}

function blockOrCancelRelease(ops: ActiveOpsDay, flight: FlightInstance, reason: string, options: ReleaseFlightOptions) {
  if (!options.preserveOnBlock) {
    flight.status = "needs_recovery";
    flight.reason = reason;
    appendOpsEvent(ops, flight.id, "blocked", options.minute ?? Math.max(0, flight.estimatedDepartureMinute - 60), options.actor ?? "system", `${flight.flightNo} 待恢复：${reason}`);
    return;
  }
  const releaseLabel = formatReleaseOperationLabel(flight);
  flight.status = "needs_recovery";
  flight.reason = `${releaseLabel}未通过：${reason}`;
  appendOpsEvent(ops, flight.id, "blocked", options.minute ?? ops.currentMinute, options.actor ?? "system", `${flight.flightNo} ${releaseLabel}未通过：${reason}`);
}

function formatReleaseOperationLabel(flight: FlightInstance): string {
  if (flight.status === "released") return "二次放行";
  if (flight.releaseDraft?.status === "confirmed") return "更新放行";
  if (flight.status === "needs_recovery" || flight.reason?.includes("放行未通过") || flight.reason?.includes("重新放行")) return "重新放行";
  return "放行";
}

function holdUnreleasedFlight(ops: ActiveOpsDay, flight: FlightInstance, targetMinute: number) {
  const delta = Math.max(1, targetMinute - flight.estimatedDepartureMinute);
  flight.estimatedDepartureMinute += delta;
  flight.estimatedArrivalMinute += delta;
  flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
  const waitReason = unreleasedFlightWaitReason(flight.status);
  if (flight.status !== "needs_recovery") {
    flight.status = "delayed";
    if (!flight.reason?.includes(waitReason)) {
      flight.reason = waitReason;
    }
  } else if (!flight.reason) {
    flight.reason = waitReason;
  }
  addDelayEvent(flight, "player_action", delta, true, flight.reason);
  syncCurrentMovementFromFlight(flight);
  const duplicate = ops.events.some((event) => event.flightInstanceId === flight.id && event.type === "blocked" && event.message.includes(waitReason));
  if (!duplicate) {
    appendOpsEvent(ops, flight.id, "blocked", targetMinute, "system", `${flight.flightNo} ${waitReason}，起飞顺延。`);
  }
}

function unreleasedFlightWaitReason(status: FlightInstance["status"]): string {
  if (status === "released") {
    return "等待玩家开始地面保障";
  }
  return "等待玩家手动放行";
}

function departFlight(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance) {
  const aircraft = flightAircraft(save, flight);
  if (flight.readiness && flight.estimatedDepartureMinute < flight.readiness.readyAtMinute) {
    const delta = flight.readiness.readyAtMinute - flight.estimatedDepartureMinute;
    flight.estimatedDepartureMinute = flight.readiness.readyAtMinute;
    flight.estimatedArrivalMinute += delta;
    flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
    flight.status = "delayed";
    flight.reason = `恢复准备窗口延后 ${delta} 分钟`;
    appendOpsEvent(ops, flight.id, "blocked", ops.currentMinute, "system", `${flight.flightNo} 待恢复：${flight.reason}`);
    return;
  }
  if (!aircraft || aircraft.location !== flight.origin) {
    flight.status = "needs_recovery";
    flight.reason = departureAircraftBlockReason(aircraft, flight.origin);
    appendOpsEvent(ops, flight.id, "blocked", ops.currentMinute, "system", `${flight.flightNo} 待恢复：${flight.reason}`);
    return;
  }
  if (aircraft.activeFlightInstanceId && aircraft.activeFlightInstanceId !== flight.id) {
    flight.status = "needs_recovery";
    flight.reason = `${aircraft.registration} 当前由 ${aircraft.activeFlightInstanceId} 锁定运行。`;
    appendOpsEvent(ops, flight.id, "blocked", ops.currentMinute, "system", `${flight.flightNo} 待恢复：${flight.reason}`);
    return;
  }
  const pendingNodes = activeGroundOpsNodes(flight);
  if (pendingNodes.length > 0) {
    const readyAtMinute = pendingGroundOpsReadyAtMinute(pendingNodes, flight.estimatedDepartureMinute);
    const delta = pendingGroundOpsDepartureDelay(readyAtMinute, flight.estimatedDepartureMinute);
    flight.estimatedDepartureMinute += delta;
    flight.estimatedArrivalMinute += delta;
    flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
    flight.reason = `等待运行节点完成：${pendingNodes.map(groundOpsNodeSummary).join(" / ")}`;
    addDelayEvent(flight, "player_action", delta, true, flight.reason);
    appendOpsEvent(ops, flight.id, "delayed", ops.currentMinute, "system", `${flight.flightNo} ${flight.reason}，起飞顺延。`);
    return;
  }
  flight.status = "airborne";
  if (flight.reason === READY_FOR_DEPARTURE_REASON) {
    flight.reason = undefined;
  }
  flight.actualDepartureMinute = flight.estimatedDepartureMinute;
  flight.currentAirport = undefined;
  aircraft.fuelOnBoardKg = flight.fuelPlan?.plannedTakeoffFuelKg ?? aircraftFuelOnBoardKg(aircraft);
  aircraft.status = "in_flight";
  aircraft.activeFlightInstanceId = flight.id;
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "departed", flight.actualDepartureMinute, "system", `${flight.flightNo} 已起飞。`);
}

function departureAircraftBlockReason(aircraft: Aircraft | undefined, origin: string): string {
  if (aircraft) {
    return `飞机位置不符：${aircraft.registration} 当前在 ${aircraft.location}，计划需要 ${origin}。`;
  }
  return "指派飞机不存在。";
}

function pendingGroundOpsReadyAtMinute(pendingNodes: GroundOpsNodeState[], fallbackDepartureMinute: number): number {
  const readyMinutes = pendingNodes.map((node) => node.readyAtMinute ?? node.plannedStartMinute ?? fallbackDepartureMinute);
  return Math.max(...readyMinutes);
}

function pendingGroundOpsDepartureDelay(readyAtMinute: number, estimatedDepartureMinute: number): number {
  const delayMinutes = readyAtMinute - estimatedDepartureMinute;
  return Math.max(1, delayMinutes);
}

function landFlight(save: SaveGame, ops: ActiveOpsDay, flight: FlightInstance) {
  const aircraft = flightAircraft(save, flight);
  const type = aircraftTypeForFleetAircraft(aircraft);
  if (!aircraft || !type) {
    flight.status = "failed";
    flight.reason = "落地结算时飞机数据缺失。";
    flight.costUsd = Math.max(flight.costUsd, 12_000);
    appendOpsEvent(ops, flight.id, "failed", flight.estimatedArrivalMinute, "system", `${flight.flightNo} 失败：${flight.reason}`);
    return;
  }
  if (!flight.payloadPlan || !flight.fuelPlan || !flight.weightPlan || !flight.costBreakdown || !flight.revenueBreakdown) {
    flight.status = "failed";
    flight.reason = "落地结算缺少放行快照。";
    flight.costUsd = Math.max(flight.costUsd, 12_000);
    aircraft.activeFlightInstanceId = undefined;
    appendOpsEvent(ops, flight.id, "failed", flight.estimatedArrivalMinute, "system", `${flight.flightNo} 失败：${flight.reason}`);
    return;
  }
  const landedAwayFromPlan = flight.destination !== flight.plannedDestination;
  flight.status = landingStatusForPlanArrival(landedAwayFromPlan);
  flight.completionMode = landingCompletionMode(landedAwayFromPlan);
  flight.finalAirport = landingFinalAirport(landedAwayFromPlan, flight.destination);
  flight.actualArrivalMinute = flight.estimatedArrivalMinute;
  flight.delayMinutes = flightArrivalDelayMinutes(flight);
  flight.currentAirport = flight.destination;
  aircraft.location = flight.destination;
  aircraft.status = "available";
  aircraft.fuelOnBoardKg = landingFuelOnBoardKg(flight, aircraft);
  aircraft.activeFlightInstanceId = undefined;
  aircraft.hoursSinceMaintenance += flight.plannedBlockMinutes / 60;
  aircraft.cyclesSinceCheck += 1;
  flight.passengers = flight.payloadPlan?.passengers ?? landedPassengerCount(flight);
  flight.revenueUsd = flight.revenueBreakdown?.totalRevenueUsd ?? Math.round(flight.passengers * flight.distanceKm * (flight.airportOfferSnapshot?.payloadOffer.yieldUsdPerKm ?? 0));
  flight.costUsd = flight.costBreakdown?.totalCostUsd ?? estimateCostFromFlightSnapshot(flight, type);
  flight.aircraftSnapshotAfter = snapshotAircraftState(aircraft);
  refreshDelaySummary(flight);
  syncCurrentMovementFromFlight(flight);
  appendOpsEvent(ops, flight.id, "landed", flight.actualArrivalMinute, "system", landingEventMessage(flight, landedAwayFromPlan));
}

function landingFuelOnBoardKg(flight: FlightInstance, aircraft: Aircraft): number {
  const plannedLandingFuelKg = flight.fuelPlan?.plannedLandingFuelKg ?? aircraftFuelOnBoardKg(aircraft);
  const roundedLandingFuelKg = Math.round(plannedLandingFuelKg);
  return Math.max(0, roundedLandingFuelKg);
}

function aircraftTypeForFleetAircraft(aircraft: Aircraft | undefined): AircraftType | undefined {
  if (!aircraft) {
    return undefined;
  }
  return aircraftTypeById(aircraft.typeId);
}

function landingStatusForPlanArrival(landedAwayFromPlan: boolean): FlightInstance["status"] {
  if (landedAwayFromPlan) {
    return "interrupted";
  }
  return "landed";
}

function landingCompletionMode(landedAwayFromPlan: boolean): FlightInstance["completionMode"] {
  if (landedAwayFromPlan) {
    return undefined;
  }
  return "arrived_planned_destination";
}

function landingFinalAirport(landedAwayFromPlan: boolean, destination: string): string | undefined {
  if (landedAwayFromPlan) {
    return undefined;
  }
  return destination;
}

function landingEventMessage(flight: FlightInstance, landedAwayFromPlan: boolean): string {
  if (landedAwayFromPlan) {
    return `${flight.flightNo} 已落地 ${flight.destination}，尚未抵达原计划目的地 ${flight.plannedDestination}。`;
  }
  return `${flight.flightNo} 已落地 ${flight.destination}。`;
}

function estimateCostFromFlightSnapshot(flight: FlightInstance, type: AircraftType): number {
  const originFuel = flight.airportOfferSnapshot?.fuelQuote.priceUsdPerTon ?? flight.fuelPlan?.originFuelPriceUsdPerTon ?? 0;
  const destinationFuel = flight.airportOfferSnapshot?.destinationFuelQuote.priceUsdPerTon ?? flight.fuelPlan?.destinationFuelPriceUsdPerTon ?? originFuel;
  const baseFuel = baseFuelPriceForPolicy(flight.fuelPolicy, originFuel, destinationFuel);
  const resiliencePremium = fuelPolicyResiliencePremium(flight.fuelPolicy);
  const tripFuelKg = flight.fuelPlan?.tripFuelKg ?? Math.round(type.fuelBurnKgPerKm * flight.distanceKm);
  const fuelCostUsd = (tripFuelKg / 1000) * baseFuel * resiliencePremium;
  const operationsAndHandling = 18_000 + flight.distanceKm * 9;
  const maintenanceReserve = type.fuelBurnKgPerKm * flight.distanceKm * 3.4;
  return Math.round(fuelCostUsd + operationsAndHandling + maintenanceReserve);
}

function fuelPolicyResiliencePremium(fuelPolicy: FlightInstance["fuelPolicy"]): number {
  if (fuelPolicy === "resilience") {
    return 1.08;
  }
  return 1;
}

function baseFuelPriceForPolicy(fuelPolicy: FlightInstance["fuelPolicy"], originFuel: number, destinationFuel: number): number {
  if (fuelPolicy === "home_tankering") {
    return originFuel;
  }
  if (fuelPolicy === "cheapest_enroute") {
    return Math.min(originFuel, destinationFuel);
  }
  return (originFuel + destinationFuel) / 2;
}

function rerouteFlight(save: SaveGame, flight: FlightInstance, destinationCode: string, reason: string) {
  const origin = requireAirport(flight.origin);
  const destination = requireAirport(destinationCode);
  const type = aircraftTypeById(flight.aircraftTypeId);
  const distanceKm = calculateDistanceKm(origin.code, destination.code);
  const blockMinutes = flightBlockMinutes(distanceKm, type);
  flight.destination = destination.code;
  flight.distanceKm = distanceKm;
  flight.plannedBlockMinutes = blockMinutes;
  flight.estimatedArrivalMinute = flight.estimatedDepartureMinute + blockMinutes;
  flight.delayMinutes = estimatedFlightArrivalDelayMinutes(flight);
  flight.status = statusAfterDiversion(flight.status);
  flight.reason = reason;
  syncCurrentMovementFromFlight(flight);
  void save;
}

function markFlightNeedsRelease(flight: FlightInstance) {
  if (flight.status === "airborne" || flight.status === "diverted") {
    return;
  }
  const groundOps = currentGroundOpsForFlight(flight);
  if (groundOps?.status === "in_progress") {
    flight.status = "ground_service";
    return;
  }
  if (groundOps?.status === "completed") {
    flight.status = "ready_for_departure";
    return;
  }
  flight.releaseDraft = releasableDraftAfterMarking(flight);
  flight.status = releaseWaitStatus(flight);
}

function releasableDraftAfterMarking(flight: FlightInstance): FlightInstance["releaseDraft"] {
  if (flight.releaseDraft?.status === "confirmed") {
    return undefined;
  }
  return flight.releaseDraft;
}

function releaseWaitStatus(flight: FlightInstance): FlightInstance["status"] {
  if (flight.estimatedDepartureMinute > flight.plannedDepartureMinute) {
    return "delayed";
  }
  return "awaiting_release";
}

function cascadeManualAircraftSwap(save: SaveGame, ops: ActiveOpsDay, changedFlight: FlightInstance, aircraft: Aircraft, reason: string) {
  if (!changedFlight.rotationId) {
    return;
  }
  const rotationId = changedFlight.rotationId;
  let projectedLocation = changedFlight.destination;
  let projectedReadyMinute = changedFlight.estimatedArrivalMinute + minimumTurnForFlight(changedFlight);
  const downstream = ops.flights
    .filter(
      (flight) =>
        flight.rotationId === rotationId &&
        flight.sequence > changedFlight.sequence &&
        flight.rotationBinding === "auto_bound" &&
        flight.aircraftAssignmentSource !== "manual" &&
        !isTerminalFlightStatus(flight.status) &&
        flight.status !== "airborne" &&
        flight.status !== "diverted"
    )
    .sort((a, b) => a.sequence - b.sequence);
  for (const flight of downstream) {
    if (flight.origin !== projectedLocation) {
      flight.aircraftId = undefined;
      flight.aircraftAssignmentSource = "none";
      flight.status = "needs_recovery";
      flight.reason = `前序换机后飞机位置不符：${projectedLocation} 无法衔接 ${flight.origin}`;
      appendOpsEvent(ops, flight.id, "blocked", ops.currentMinute, "system", `${flight.flightNo} 待恢复：${flight.reason}。`);
      return;
    }
    if (projectedReadyMinute > flight.estimatedDepartureMinute) {
      const delta = projectedReadyMinute - flight.estimatedDepartureMinute;
      flight.estimatedDepartureMinute = projectedReadyMinute;
      flight.estimatedArrivalMinute += delta;
      flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
      flight.status = "delayed";
      flight.reason = `同一航班串换机衔接延误 ${delta} 分钟`;
    }
    const validationBlocks = validateOperation({
      day: save.day,
      origin: requireAirport(flight.origin),
      destination: requireAirport(flight.destination),
      distanceKm: flight.distanceKm,
      assignment: {
        id: `cascade-${flight.id}`,
        targetType: "rotation",
        targetId: rotationId,
        aircraftId: aircraft.id,
        fuelPolicy: flight.fuelPolicy,
        assignmentMode: "manual",
        updatedDay: save.day
      },
      licenseRequired: undefined,
      grantedLicenseCodes: save.licenses.filter((license) => license.granted).map((license) => license.code),
      aircraft,
      events: []
    });
    if (validationBlocks.length > 0) {
      flight.aircraftId = undefined;
      flight.aircraftAssignmentSource = "none";
      flight.status = "needs_recovery";
      flight.reason = validationBlocks[0];
      appendOpsEvent(ops, flight.id, "blocked", ops.currentMinute, "system", `${flight.flightNo} 待恢复：${flight.reason}。`);
      return;
    }
    flight.aircraftId = aircraft.id;
    flight.aircraftTypeId = aircraft.typeId;
    flight.aircraftAssignmentSource = "auto";
    flight.reason = reason;
    appendOpsEvent(ops, flight.id, "redispatched", ops.currentMinute, "system", `${flight.flightNo} 跟随前序换机，改由 ${aircraft.registration} 执行。`);
    projectedLocation = flight.destination;
    projectedReadyMinute = flight.estimatedArrivalMinute + minimumTurnForFlight(flight);
  }
}

function propagateAircraftDelays(ops: ActiveOpsDay, aircraftId: string | undefined) {
  if (!aircraftId) {
    return;
  }
  const flights = ops.flights
    .filter((flight) => flight.aircraftId === aircraftId)
    .sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute || a.sequence - b.sequence);
  let readyMinute = 0;
  for (const flight of flights) {
    if (flight.status === "cancelled" || flight.status === "failed" || flight.status === "needs_recovery") {
      continue;
    }
    if (readyMinute > flight.estimatedDepartureMinute && flight.status !== "airborne" && flight.status !== "landed" && flight.status !== "diverted") {
      const delta = readyMinute - flight.estimatedDepartureMinute;
      flight.estimatedDepartureMinute = readyMinute;
      flight.estimatedArrivalMinute += delta;
      flight.delayMinutes = Math.max(0, flight.estimatedArrivalMinute - flight.plannedArrivalMinute);
      flight.status = propagatedDelayStatus(flight.status);
      flight.reason = `同机前序航班延误传播 ${delta} 分钟`;
      addDelayEvent(flight, "late_aircraft", delta, false, flight.reason, latestPriorAircraftFlight(ops, flight)?.id);
      appendOpsEvent(ops, flight.id, "delayed", ops.currentMinute, "system", `${flight.flightNo} ${flight.reason}。`);
    }
    readyMinute = Math.max(readyMinute, flight.estimatedArrivalMinute + minimumTurnForFlight(flight));
  }
}

function propagatedDelayStatus(status: FlightInstance["status"]): FlightInstance["status"] {
  if (status === "scheduled") {
    return "delayed";
  }
  return status;
}

function operationsFromFlightInstances(save: SaveGame, ops: ActiveOpsDay): DailyOperation[] {
  const groups = new Map<string, FlightInstance[]>();
  for (const flight of ops.flights) {
    const key = `${flight.sourceType}:${flight.sourceId}`;
    groups.set(key, [...(groups.get(key) ?? []), flight]);
  }
  return [...groups.entries()].map(([key, flights]) => {
    const [targetType, targetId] = key.split(":") as [DailyOperation["targetType"], string];
    const sorted = flights.sort((a, b) => a.sequence - b.sequence);
    const legs = sorted.map((flight) => legFromFlightInstance(flight));
    const kpis = calculateOperationalKpis(legs);
    const revenueUsd = sorted.reduce((sum, flight) => sum + flight.revenueUsd, 0);
    const costUsd = sorted.reduce((sum, flight) => sum + flight.costUsd, 0);
    const operationStatus = operationStatusFromLegs(legs);
    const title = sorted[0]?.sourceTitle ?? targetId;
    for (const recovery of save.recoveryFlights) {
      if (recovery.id === targetId) {
        recovery.status = recoveryStatusFromOperationStatus(operationStatus);
      }
    }
    return {
      ...operationBase(ops.day, title, targetType, targetId, sorted[0]?.aircraftId),
      status: operationStatus,
      revenueUsd,
      costUsd,
      profitUsd: revenueUsd - costUsd,
      passengers: sorted.reduce((sum, flight) => sum + flight.passengers, 0),
      reliabilityDelta: operationReliabilityDelta(kpis),
      reputationDelta: operationReputationDelta(operationStatus),
      notes: operationNotesFromLegs(legs),
      legs,
      kpis
    };
  });
}

function recoveryStatusFromOperationStatus(status: DailyOperation["status"]): RecoveryFlight["status"] {
  if (status === "operated" || status === "delayed") {
    return "completed";
  }
  return "failed";
}

function operationReliabilityDelta(kpis: OperationalKpis): number {
  if (kpis.scheduledLegs <= 0) {
    return 0;
  }
  const arrivalRateContribution = (kpis.arrivalOnTimeRate - 85) / 20;
  const cancellationPenalty = kpis.cancelledLegs * 0.5;
  const reliabilityDelta = arrivalRateContribution - cancellationPenalty;
  return Number(reliabilityDelta.toFixed(2));
}

function legFromFlightInstance(flight: FlightInstance): FlightLeg {
  const arrivalDelay = flightArrivalDelayMinutes(flight);
  const status = legStatusFromFlight(flight, arrivalDelay);
  return {
	    id: flight.id,
	    occurrenceId: flight.occurrenceId,
	    scheduledFlightId: flight.scheduledFlightId,
	    recoveryId: flight.recoveryId,
    rotationId: flight.rotationId,
    sequence: flight.sequence,
    flightNo: flight.flightNo,
    origin: flight.origin,
    destination: flight.destination,
    distanceKm: flight.distanceKm,
    plannedBlockHours: Number((flight.plannedBlockMinutes / 60).toFixed(2)),
    departureTimeLocal: dayMinuteToLabel(flight.actualDepartureMinute ?? flight.estimatedDepartureMinute),
    arrivalTimeLocal: dayMinuteToLabel(flight.actualArrivalMinute ?? flight.estimatedArrivalMinute),
    plannedDepartureMinute: flight.plannedDepartureMinute,
    plannedArrivalMinute: flight.plannedArrivalMinute,
    estimatedDepartureMinute: flight.estimatedDepartureMinute,
    estimatedArrivalMinute: flight.estimatedArrivalMinute,
    actualDepartureMinute: flight.actualDepartureMinute,
    actualArrivalMinute: flight.actualArrivalMinute,
    arrivalDelayMinutes: arrivalDelay,
    propagatedDelayMinutes: Math.max(0, flight.estimatedDepartureMinute - flight.plannedDepartureMinute),
    turnBufferMinutes: flight.plannedDepartureMinute - flight.estimatedDepartureMinute,
    status,
    reason: legReasonForFlight(flight)
  };
}

function legReasonForFlight(flight: FlightInstance): string | undefined {
  if (flight.reason) {
    return flight.reason;
  }
  if (flight.technicalStop) {
    return `技术经停 ${flight.technicalStop}`;
  }
  return undefined;
}

function operationReputationDelta(status: DailyOperation["status"]): number {
  if (status === "cancelled" || status === "failed") {
    return -1.2;
  }
  if (status === "delayed") {
    return -0.3;
  }
  return 0.2;
}

function flightArrivalDelayMinutes(flight: FlightInstance): number {
  if (flight.actualArrivalMinute !== undefined) {
    return Math.max(0, flight.actualArrivalMinute - flight.plannedArrivalMinute);
  }
  return estimatedFlightArrivalDelayMinutes(flight);
}

function estimatedFlightArrivalDelayMinutes(flight: Pick<FlightInstance, "estimatedArrivalMinute" | "plannedArrivalMinute">): number {
  const delayMinutes = flight.estimatedArrivalMinute - flight.plannedArrivalMinute;
  return Math.max(0, delayMinutes);
}

function legStatusFromFlight(flight: FlightInstance, arrivalDelay: number): FlightLeg["status"] {
  if (flight.status !== "landed") {
    return "cancelled";
  }
  if (arrivalDelay > ON_TIME_ARRIVAL_THRESHOLD_MINUTES || flight.destination !== flight.plannedDestination) {
    return "delayed";
  }
  return "operated";
}

function assignmentForFlight(flight: FlightInstance): DispatchAssignment {
  return {
    id: `active-${flight.id}`,
    targetType: flight.sourceType,
    targetId: flight.sourceId,
    aircraftId: flight.aircraftId ?? "",
    fuelPolicy: flight.fuelPolicy,
    assignmentMode: assignmentModeFromAircraftSource(flight.aircraftAssignmentSource),
    updatedDay: 0
  };
}

function assignmentModeFromAircraftSource(source: FlightInstance["aircraftAssignmentSource"]): DispatchAssignment["assignmentMode"] {
  if (source === "auto") {
    return "auto";
  }
  return "manual";
}

function minimumTurnForFlight(flight: FlightInstance): number {
  return flight.minimumTurnMinutes || aircraftTypeById(flight.aircraftTypeId)?.turnMinutes || 60;
}

function isTerminalFlightStatus(status: FlightInstance["status"]): boolean {
  return status === "landed" || status === "cancelled" || status === "failed";
}

function commitOpsAssignment(save: SaveGame, plan: DailyResourcePlan, assignment: DispatchAssignment | undefined) {
  if (!assignment) {
    return;
  }
  const index = save.assignments.findIndex((item) => item.targetType === assignment.targetType && item.targetId === assignment.targetId);
  const normalized = { ...assignment, updatedDay: save.day };
  if (index >= 0) {
    save.assignments[index] = normalized;
  } else {
    save.assignments.push(normalized);
  }
  plan.usedAircraftIds.add(assignment.aircraftId);
}

function rotationInputSelectors(input: PlayerRotationInput, schedule: ScheduleSource): ResolvedScheduleSelector[] {
  const ids = rotationSelectorIds(input);
  return ids.map((id) => resolveScheduleSelector(id, schedule));
}

function rotationSelectorIds(input: PlayerRotationInput): string[] {
  if (input.scheduledFlightIds && input.scheduledFlightIds.length > 0) {
    return input.scheduledFlightIds;
  }
  return input.occurrenceIds ?? [];
}

function findOccupiedRotationSelector(rotations: PlayerRotation[], selectorIds: string[], editingRotationId: string, schedule: ScheduleSource): string | undefined {
  for (const rotation of rotations) {
    if (rotation.id === editingRotationId) continue;
    for (const leg of rotation.legs) {
      const selectorId = rotationLegSelectorId(leg);
      if (selectorId && selectorIds.some((requestedId) => rotationSelectorsOverlap(selectorId, requestedId))) {
        return selectorId;
      }
    }
  }
  return undefined;
}

function buildPlayerRotationLegs(selectors: ResolvedScheduleSelector[], save: SaveGame, rotationId: string, schedule: ScheduleSource) {
  let readyMinute = 0;
  const firstOccurrence = selectors[0].firstOccurrence;
  const baseDay = firstOccurrence?.day ?? save.day;
  return selectors.map((selector, index) => {
    const plan = selector.flightPlan;
    if (!plan) {
      throw new Error("GM 定期航班不存在。");
    }
    const occurrence = selector.firstOccurrence;
    const previousPlan = selectors[index - 1]?.flightPlan;
    if (index > 0 && previousPlan && previousPlan.destination !== plan.origin) {
      throw new Error(`航段链不连续：${previousPlan.destination} 无法衔接 ${plan.origin}。`);
    }
    let departureDayOffset = Math.max(0, (occurrence?.day ?? baseDay) - baseDay);
    let plannedDepartureMinute = departureDayOffset * 1440 + timeToMinutes(occurrence?.departureTimeLocal ?? plan.departureTimeLocal);
    while (plannedDepartureMinute < readyMinute) {
      departureDayOffset += 1;
      plannedDepartureMinute = departureDayOffset * 1440 + timeToMinutes(occurrence?.departureTimeLocal ?? plan.departureTimeLocal);
    }
    const template: FlightLegTemplate = {
      id: `rot-leg-${selector.selectorId}`,
      sequence: index + 1,
      flightNo: plan.flightNo,
      origin: plan.origin,
      destination: plan.destination,
      departureDayOffset,
      departureTimeLocal: occurrence?.departureTimeLocal ?? plan.departureTimeLocal,
      minimumTurnMinutes: occurrence?.minimumTurnMinutes ?? plan.minimumTurnMinutes
    };
    const legPlan = buildLegPlan(template, "a320");
    readyMinute = legPlan.plannedArrivalMinute + template.minimumTurnMinutes;
    return {
      id: `${rotationId}-leg-${index + 1}`,
      sequence: index + 1,
      occurrenceId: undefined,
      scheduledFlightId: selector.scheduledFlightId,
      departureDayOffset
    };
  });
}

function assertRotationCanChange(save: SaveGame, rotationId: string | undefined) {
  if (!rotationId || !save.activeOpsDay) {
    return;
  }
  const running = save.activeOpsDay.flights.some((flight) => flight.rotationId === rotationId && !isTerminalFlightStatus(flight.status));
  if (running) {
    throw new Error("运行日开放时不能修改正在执行的航班串。");
  }
}

function normalizeRegistration(value: string): string {
  const registration = value.trim().toUpperCase();
  if (!registration) {
    throw new Error("飞机注册号不能为空。");
  }
  return registration;
}

function restoreAircraftAvailability(save: SaveGame, alerts: string[]) {
  for (const aircraft of save.fleet) {
    if (aircraft.status === "maintenance" && aircraft.availableFromDay <= save.day) {
      aircraft.status = "available";
      alerts.push(`${aircraft.registration}: 维修窗口结束，恢复可用。`);
    }
  }
}

function buildLegPlan(template: FlightLegTemplate, aircraftTypeId?: string): LegPlan {
  const origin = requireAirport(template.origin);
  const destination = requireAirport(template.destination);
  const distanceKm = calculateDistanceKm(origin.code, destination.code);
  const type = aircraftTypeById(aircraftTypeId ?? "a320");
  const blockMinutes = flightBlockMinutes(distanceKm, type);
  const plannedDepartureMinute = template.departureDayOffset * 1440 + timeToMinutes(template.departureTimeLocal);
  return {
    template,
    aircraftTypeId,
    origin,
    destination,
    distanceKm,
    blockMinutes,
    plannedDepartureMinute,
    plannedArrivalMinute: plannedDepartureMinute + blockMinutes
  };
}

function rotationOperatesToday(rotation: PlayerRotation, save: SaveGame, schedule: ScheduleSource): boolean {
  const firstOccurrence = resolveOccurrenceForLeg(rotation.legs[0], schedule, save.day);
  return Boolean(firstOccurrence && firstOccurrence.day === save.day && firstOccurrence.dateIso === save.dateIso && firstOccurrence.status !== "completed");
}

function rotationPriority(schedule: ScheduleSource, rotation: PlayerRotation): number {
  const legPriorities = rotation.legs.map((leg) => rotationLegPriority(schedule, leg));
  return Math.max(0, ...legPriorities);
}

function rotationLegPriority(schedule: ScheduleSource, leg: PlayerRotation["legs"][number]): number {
  return resolveOccurrenceForLeg(leg, schedule)?.priority ?? schedule.flightPlans.find((plan) => plan.id === leg.scheduledFlightId)?.priority ?? 0;
}

function resolveRotationLegPlans(rotation: PlayerRotation, schedule: ScheduleSource, aircraftTypeId?: string, baseDay?: number): { plans: LegPlan[]; missingPlanIds: string[] } {
  const plans: LegPlan[] = [];
  const missingPlanIds: string[] = [];
  const cycleStartDay = baseDay ?? resolveOccurrenceForLeg(rotation.legs[0], schedule)?.day;
  for (const leg of [...rotation.legs].sort((a, b) => a.sequence - b.sequence)) {
    const occurrence = resolveOccurrenceForLeg(leg, schedule, cycleStartDay);
    const flightPlan = rotationLegFlightPlan(schedule, leg, occurrence);
    if (!occurrence || !flightPlan) {
      missingPlanIds.push(rotationLegSelectorId(leg) ?? leg.id);
      continue;
    }
    const template: FlightLegTemplate = {
      id: leg.id,
      sequence: leg.sequence,
      flightNo: occurrence.flightNo,
      origin: occurrence.origin,
      destination: occurrence.destination,
      departureDayOffset: leg.departureDayOffset,
      departureTimeLocal: occurrence.departureTimeLocal,
      minimumTurnMinutes: occurrence.minimumTurnMinutes
    };
    plans.push({ ...buildLegPlan(template, aircraftTypeId), scheduledFlightId: flightPlan.id, flightPlan, occurrenceId: occurrence.id, occurrence });
  }
  return { plans, missingPlanIds };
}

function rotationLegFlightPlan(schedule: ScheduleSource, leg: PlayerRotation["legs"][number], occurrence: FlightOccurrence | undefined): FlightPlan | undefined {
  if (occurrence) {
    return schedule.flightPlans.find((plan) => plan.id === occurrence.flightPlanId);
  }
  return schedule.flightPlans.find((plan) => plan.id === leg.scheduledFlightId);
}

function resolveRotationFlightAssignments(
  save: SaveGame,
  dailyPlan: DailyResourcePlan,
  rotation: PlayerRotation,
  plans: LegPlan[],
  events: GlobalEvent[]
): Map<number, { assignment?: DispatchAssignment; assignmentSource: FlightInstance["aircraftAssignmentSource"]; reason?: string }> {
  const assignments = new Map<number, { assignment?: DispatchAssignment; assignmentSource: FlightInstance["aircraftAssignmentSource"]; reason?: string }>();
  const standing = save.assignments.find((assignment) => assignment.targetType === "rotation" && assignment.targetId === rotation.id && assignment.assignmentMode === "manual");
  let preferredAircraftId: string | undefined;
  for (const plan of plans.sort((a, b) => a.plannedDepartureMinute - b.plannedDepartureMinute || a.template.sequence - b.template.sequence)) {
    const resolved = resolveRotationPlanAssignment(save, dailyPlan, rotation, plan, events, standing, preferredAircraftId);
    assignments.set(plan.template.sequence, resolved);
    if (resolved.assignment) {
      preferredAircraftId = resolved.assignment.aircraftId;
    }
  }
  return assignments;
}

function resolveRotationPlanAssignment(
  save: SaveGame,
  dailyPlan: DailyResourcePlan,
  rotation: PlayerRotation,
  plan: LegPlan,
  events: GlobalEvent[],
  manualAssignment?: DispatchAssignment,
  preferredAircraftId?: string
): { assignment?: DispatchAssignment; assignmentSource: FlightInstance["aircraftAssignmentSource"]; reason?: string } {
  const forcedAircraftId = manualAssignment?.aircraftId;
  const candidates = rotationAircraftCandidates(save, dailyPlan, plan, forcedAircraftId);
  let lastReason = rotationCandidateEmptyReason(candidates.length, plan.origin.code);
  const sortedCandidates = candidates.sort(
    (a, b) => aircraftCandidateScore(save, dailyPlan, b, plan, preferredAircraftId) - aircraftCandidateScore(save, dailyPlan, a, plan, preferredAircraftId)
  );
  for (const aircraft of sortedCandidates) {
    const state = aircraftPlanState(dailyPlan, aircraft);
    const assignment = rotationAssignmentForCandidate(save, rotation, plan, aircraft, manualAssignment);
    const rejection = rejectRotationAircraftCandidate(save, aircraft, state, assignment, plan, events);
    if (rejection) {
      lastReason = rejection;
      continue;
    }
    state.location = plan.destination.code;
    state.readyMinute = plan.plannedArrivalMinute + plan.template.minimumTurnMinutes;
    state.hoursSinceMaintenance += plan.blockMinutes / 60;
    state.cyclesSinceCheck += 1;
    return { assignment, assignmentSource: rotationAssignmentSource(manualAssignment) };
  }
  return { assignmentSource: "none", reason: lastReason };
}

function rotationAircraftCandidates(save: SaveGame, dailyPlan: DailyResourcePlan, plan: LegPlan, forcedAircraftId: string | undefined): Aircraft[] {
  if (forcedAircraftId) {
    const forcedAircraft = save.fleet.find((aircraft) => aircraft.id === forcedAircraftId);
    if (forcedAircraft) {
      return [forcedAircraft];
    }
    return [];
  }
  return save.fleet.filter((aircraft) => aircraftPlanState(dailyPlan, aircraft).location === plan.origin.code);
}

function rotationCandidateEmptyReason(candidateCount: number, originCode: string): string {
  if (candidateCount === 0) {
    return `${originCode} 没有可用飞机；系统不自动调机。`;
  }
  return `${originCode} 始发站无可用飞机。`;
}

function rotationAssignmentForCandidate(
  save: SaveGame,
  rotation: PlayerRotation,
  plan: LegPlan,
  aircraft: Aircraft,
  manualAssignment: DispatchAssignment | undefined
): DispatchAssignment {
  if (manualAssignment) {
    return { ...manualAssignment, aircraftId: aircraft.id, updatedDay: save.day };
  }
  return {
    id: `assign-${rotation.id}-${plan.template.sequence}-${aircraft.id}`,
    targetType: "rotation",
    targetId: rotation.id,
    aircraftId: aircraft.id,
    fuelPolicy: "minimum_cost",
    assignmentMode: "auto",
    updatedDay: save.day
  };
}

function rotationAssignmentSource(manualAssignment: DispatchAssignment | undefined): FlightInstance["aircraftAssignmentSource"] {
  if (manualAssignment) {
    return "manual";
  }
  return "auto";
}

function aircraftPlanState(plan: DailyResourcePlan, aircraft: Aircraft): AircraftPlanState {
  const existing = plan.aircraftStates.get(aircraft.id);
  if (existing) {
    return existing;
  }
  const state: AircraftPlanState = {
    aircraftId: aircraft.id,
    location: aircraft.location,
    readyMinute: 0,
    hoursSinceMaintenance: aircraft.hoursSinceMaintenance,
    cyclesSinceCheck: aircraft.cyclesSinceCheck
  };
  plan.aircraftStates.set(aircraft.id, state);
  return state;
}

function aircraftCandidateScore(save: SaveGame, dailyPlan: DailyResourcePlan, aircraft: Aircraft, plan: LegPlan, preferredAircraftId?: string): number {
  const state = aircraftPlanState(dailyPlan, aircraft);
  const type = aircraftTypeById(aircraft.typeId);
  const remainingHours = remainingMaintenanceHours(type, state);
  const locationScore = rotationCandidateLocationScore(state, plan);
  const readinessScore = rotationCandidateReadinessScore(state, plan);
  const maintenanceScore = remainingHours * 3 + Math.max(0, MAX_CYCLES_SINCE_CHECK - state.cyclesSinceCheck);
  const continuityScore = rotationCandidateContinuityScore(aircraft.id, preferredAircraftId);
  void save;
  return continuityScore + locationScore + readinessScore + maintenanceScore;
}

function remainingMaintenanceHours(type: AircraftType | undefined, state: AircraftPlanState): number {
  if (!type) {
    return 0;
  }
  return type.maintenanceIntervalHours - state.hoursSinceMaintenance;
}

function rotationCandidateLocationScore(state: AircraftPlanState, plan: LegPlan): number {
  if (state.location === plan.origin.code) {
    return 5_000;
  }
  return 0;
}

function rotationCandidateReadinessScore(state: AircraftPlanState, plan: LegPlan): number {
  if (state.readyMinute > plan.plannedDepartureMinute) {
    return -10_000;
  }
  const readyAheadMinutes = Math.max(0, plan.plannedDepartureMinute - state.readyMinute);
  const earlyReadyPenalty = readyAheadMinutes / 20;
  return 1_000 - earlyReadyPenalty;
}

function rotationCandidateContinuityScore(aircraftId: string, preferredAircraftId: string | undefined): number {
  if (aircraftId === preferredAircraftId) {
    return 20_000;
  }
  return 0;
}

function rejectRotationAircraftCandidate(
  save: SaveGame,
  aircraft: Aircraft,
  state: AircraftPlanState,
  assignment: DispatchAssignment,
  plan: LegPlan,
  events: GlobalEvent[]
): string | undefined {
  const type = aircraftTypeById(aircraft.typeId);
  if (!type) {
    return "机型数据缺失。";
  }
  if (aircraft.status !== "available" || aircraft.availableFromDay > save.day) {
    return `${aircraft.registration} 当前不可用。`;
  }
  if (state.location !== plan.origin.code) {
    return `${aircraft.registration} 当前在 ${state.location}，不在 ${plan.origin.code}；系统不自动调机。`;
  }
  if (state.readyMinute > plan.plannedDepartureMinute) {
    return `${aircraft.registration} 前序任务到 ${dayMinuteToLabel(state.readyMinute)} 才可用。`;
  }
  if (state.hoursSinceMaintenance >= type.maintenanceIntervalHours || state.hoursSinceMaintenance + plan.blockMinutes / 60 > type.maintenanceIntervalHours) {
    return `${aircraft.registration} FH 已接近/超过例行检查限制。`;
  }
  if (state.cyclesSinceCheck >= MAX_CYCLES_SINCE_CHECK) {
    return `${aircraft.registration} FC 已接近/超过例行检查限制。`;
  }
  const blocks = validateOperation({
    day: save.day,
    plannedTypeId: plan.aircraftTypeId,
    origin: plan.origin,
    destination: plan.destination,
    distanceKm: plan.distanceKm,
    assignment,
    licenseRequired: undefined,
    grantedLicenseCodes: save.licenses.filter((license) => license.granted).map((license) => license.code),
    aircraft,
    events
  });
  return blocks[0];
}

function resolveDailyAssignment(save: SaveGame, plan: DailyResourcePlan, request: DispatchBindingRequest): ResolvedDispatch {
  const standing = save.assignments.find((item) => item.targetType === request.targetType && item.targetId === request.targetId);
  if (standing?.assignmentMode === "manual") {
    return { assignment: standing, conflicts: assignmentConflicts(standing, plan) };
  }
  const auto = buildAutoAssignment(toDispatchBindingSnapshot(save, plan), request);
  if (auto) {
    return {
      assignment: {
        ...auto,
        id: standing?.id ?? auto.id,
        fuelPolicy: standing?.fuelPolicy ?? auto.fuelPolicy,
        assignmentMode: "auto",
        updatedDay: save.day
      },
      conflicts: []
    };
  }
  return resolvedStandingAssignment(standing, plan);
}

function resolvedStandingAssignment(standing: DispatchAssignment | undefined, plan: DailyResourcePlan): ResolvedDispatch {
  if (standing) {
    return { assignment: standing, conflicts: assignmentConflicts(standing, plan) };
  }
  return { assignment: undefined, conflicts: [] };
}

function assignmentConflicts(assignment: DispatchAssignment, plan: DailyResourcePlan): string[] {
  const notes: string[] = [];
  if (plan.usedAircraftIds.has(assignment.aircraftId)) {
    notes.push("排班飞机与同日其他轮转冲突。");
  }
  return notes;
}

function toDispatchBindingSnapshot(save: SaveGame, plan: DailyResourcePlan): DispatchBindingSnapshot {
  return {
    day: save.day,
    assignmentCount: save.assignments.length,
    usedAircraftIds: [...plan.usedAircraftIds],
    aircraft: save.fleet.map(({ id, typeId, status, availableFromDay }) => ({ id, typeId, status, availableFromDay }))
  };
}

function operationBase(day: number, title: string, targetType: DailyOperation["targetType"], targetId: string, aircraftId?: string): DailyOperation {
  return {
    id: `op-${day}-${targetType}-${targetId}`,
    day,
    title,
    targetId,
    targetType,
    aircraftId,
    status: "operated",
    revenueUsd: 0,
    costUsd: 0,
    profitUsd: 0,
    passengers: 0,
    reliabilityDelta: 0,
    reputationDelta: 0,
    notes: [],
    legs: []
  };
}

function operationStatusFromLegs(legs: FlightLeg[]): DailyOperation["status"] {
  if (legs.length === 0) return "failed";
  const operated = legs.filter((leg) => leg.status === "operated" || leg.status === "delayed").length;
  if (operated === 0) return "cancelled";
  if (legs.some((leg) => leg.status === "cancelled")) return "failed";
  if (legs.some((leg) => leg.status === "delayed")) return "delayed";
  return "operated";
}

function operationNotesFromLegs(legs: FlightLeg[]): string[] {
  const notes: string[] = [];
  const cancelled = legs.filter((leg) => leg.status === "cancelled");
  const delayed = legs.filter((leg) => leg.status === "delayed");
  const propagated = legs.filter((leg) => (leg.propagatedDelayMinutes ?? 0) > 0);
  if (cancelled.length > 0) {
    notes.push(`${cancelled.length} 段未执行：${cancelled.map(cancelledLegNote).join(" / ")}`);
  }
  if (delayed.length > 0) {
    notes.push(`${delayed.length} 段到达超过 A+15。`);
  }
  if (propagated.length > 0) {
    notes.push(`${propagated.length} 段受到同机过站延误传播。`);
  }
  return operationNotesOrDefault(notes);
}

function cancelledLegNote(leg: FlightLeg): string {
  if (leg.reason) {
    return `${leg.flightNo}(${leg.reason})`;
  }
  return leg.flightNo;
}

function operationNotesOrDefault(notes: string[]): string[] {
  if (notes.length > 0) {
    return notes;
  }
  return ["轮转运行正常。"];
}

function landedPassengerCount(flight: FlightInstance): number {
  if (flight.sourceType === "recovery") {
    if (flight.flightNo.startsWith("POS")) {
      return 0;
    }
    const transferred = flight.passengerTransfers?.reduce((sum, transfer) => sum + transfer.passengers, 0) ?? 0;
    if (transferred > 0) {
      return transferred;
    }
  }
  return estimatedPassengerCountForFlight(flight);
}

function estimatedPassengerCountForFlight(flight: FlightInstance): number {
  return flight.payloadPlan?.passengers ?? flight.airportOfferSnapshot?.payloadOffer.passengers ?? flight.passengers ?? 0;
}

function assignmentTargetExists(save: SaveGame, assignment: Omit<DispatchAssignment, "id">, schedule?: ScheduleSource): boolean {
  if (assignment.targetType === "rotation") {
    return save.rotations.some((rotation) => rotation.id === assignment.targetId);
  }
  return save.recoveryFlights.some((recovery) => recovery.id === assignment.targetId);
}
